import React, { useState, useCallback, useRef } from 'react';
import shp from 'shpjs';
import { MapContainer, TileLayer, GeoJSON, Marker, CircleMarker, Polyline, Popup, Tooltip, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { supabase, authedFetch } from './supabase';
import Login from './Login';
import Dashboard from './Dashboard';
import ProjectDashboard from './ProjectDashboard';
import ResultsSummary from './ResultsSummary';
import { periodTotals } from './tripTotals';
import { DEFAULT_FLOW_SCENARIOS } from './flowScenarios';
import { defaultPurposeSplit, educationFraction, normaliseSplit, EDUCATION_CAR_PCT, EDUCATION_GRAVITY_DEFAULTS } from './tripPurpose';
import HelpModal from './HelpModal';
import { ToolSlot } from './DockPanel';
import { recomputeAssignedTotals, moveVertex, insertVertex, removeVertex, midpoints, findRouteIndex } from './routeEdits';
import { nearestEdgeName } from './nearestRoad';
import { exportPDF, exportExcel, exportActiveTravelPDF, exportActiveTravelJPEG } from './Export';
import SchematicEditor from './SchematicEditor';
import ProjectSetup from './ProjectSetup';
import { downloadRoutesShapefile } from './shapefileExport';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const BASE_URL = "https://services1.arcgis.com/ESMARspQHYMw9BZ9/arcgis/rest/services/Output_Areas_Dec_2011_Boundaries_EW_BGC_2022/FeatureServer/0/query";
// LSOA boundaries at the SAME generalisation level (BGC — Generalised Clipped)
// as the MSOA layer below, so shared LSOA/MSOA edges (and local-authority edges)
// line up instead of showing slivers from mismatched simplification.
const LSOA_URL = "https://services1.arcgis.com/ESMARspQHYMw9BZ9/arcgis/rest/services/LSOA_Dec_2011_Boundaries_Generalised_Clipped_BGC_EW_V3/FeatureServer/0/query";
const MSOA_URL = "https://services1.arcgis.com/ESMARspQHYMw9BZ9/arcgis/rest/services/MSOA_Dec_2011_Boundaries_Generalised_Clipped_BGC_EW_V3_2022/FeatureServer/0/query";
// Population-weighted centroid services (same as the assignment backend uses)
const LSOA_PWC_URL = "https://services1.arcgis.com/ESMARspQHYMw9BZ9/arcgis/rest/services/LSOA_Dec_2011_PWC_in_England_and_Wales_2022/FeatureServer/0/query";
const MSOA_PWC_URL = "https://services1.arcgis.com/ESMARspQHYMw9BZ9/arcgis/rest/services/MSOA_Dec_2011_PWC_in_England_and_Wales_2022/FeatureServer/0/query";
const BACKEND_URL = "https://odin-backend-production-cedf.up.railway.app";

const redPin = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
});

// Handles for manually reshaping an assigned route: solid = existing vertex
// (drag to move, right-click to delete), faint = segment midpoint (drag to add).
const vertexIcon = L.divIcon({ className: 'route-vertex', iconSize: [14, 14], iconAnchor: [7, 7], html: '<div style="width:14px;height:14px;border-radius:50%;background:#fff;border:2.5px solid #3D1A6E;box-shadow:0 1px 3px rgba(0,0,0,0.35);cursor:grab;"></div>' });
const midVertexIcon = L.divIcon({ className: 'route-midvertex', iconSize: [11, 11], iconAnchor: [5.5, 5.5], html: '<div style="width:11px;height:11px;border-radius:50%;background:#7B4DBF;opacity:0.5;border:1.5px solid #fff;cursor:copy;"></div>' });

function LoadOAs({ onLoad, locked }) {
  useMapEvents({
    moveend: (e) => {
      if (locked) return;
      const bounds = e.target.getBounds();
      const bbox = [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()].join('%2C');
      const zoom = e.target.getZoom();
      if (zoom < 12) { onLoad(null); return; }
      const url = BASE_URL + "?where=1%3D1&outFields=OA11CD&geometry=" + bbox +
        "&geometryType=esriGeometryEnvelope&inSR=4326&spatialRel=esriSpatialRelIntersects&outSR=4326&f=geojson&resultRecordCount=200";
      fetch(url).then(res => res.json()).then(data => onLoad(data)).catch(err => console.error("Error:", err));
    }
  });
  return null;
}

function PinPlacer({ pinMode, onPin }) {
  useMapEvents({ click: (e) => { if (pinMode) onPin([e.latlng.lat, e.latlng.lng]); } });
  return null;
}

// Two clicks on the map draw one gate line across a real approach road.
function GateDrawer({ active, hasDraftStart, onFirstClick, onSecondClick, onMouseMove }) {
  useMapEvents({
    click: (e) => {
      if (!active) return;
      if (!hasDraftStart) onFirstClick(e.latlng.lat, e.latlng.lng);
      else onSecondClick(e.latlng.lat, e.latlng.lng);
    },
    mousemove: (e) => { if (active && hasDraftStart) onMouseMove(e.latlng.lat, e.latlng.lng); }
  });
  return null;
}

// Renders confirmed gates (letter-labelled lines) plus a live preview of the
// gate currently being drawn.
function GatesOverlay({ gates, draftStart, draftEnd }) {
  return (
    <>
      {gates.map(g => (
        <React.Fragment key={g.id}>
          <Polyline positions={[[g.p1[1], g.p1[0]], [g.p2[1], g.p2[0]]]} pathOptions={{ color: '#7C3AED', weight: 4 }} />
          <Marker
            position={[(g.p1[1] + g.p2[1]) / 2, (g.p1[0] + g.p2[0]) / 2]}
            icon={L.divIcon({
              className: '',
              html: `<div style="font-size:12px;font-weight:800;color:#7C3AED;background:white;border:1.5px solid #7C3AED;border-radius:4px;padding:1px 5px;white-space:nowrap;transform:translate(-50%,-50%)">${g.letter}</div>`
            })}
            interactive={false}
          />
        </React.Fragment>
      ))}
      {draftStart && (
        <>
          <CircleMarker center={[draftStart.lat, draftStart.lng]} radius={6} pathOptions={{ color: '#E85D00', fillColor: '#E85D00', fillOpacity: 1 }} />
          <Polyline
            positions={[[draftStart.lat, draftStart.lng], draftEnd ? [draftEnd.lat, draftEnd.lng] : [draftStart.lat, draftStart.lng]]}
            pathOptions={{ color: '#E85D00', weight: 2, dashArray: '4 4' }}
          />
        </>
      )}
    </>
  );
}

function AccessPlacer({ accessMode, onAccess }) {
  useMapEvents({ click: (e) => { if (accessMode) onAccess(e.latlng.lat, e.latlng.lng); } });
  return null;
}



function FlyTo({ target }) {
  const map = useMap();
  React.useEffect(() => {
    if (target) map.flyTo(target.center, target.zoom, { duration: 1.2 });
  }, [target, map]);
  return null;
}

function PolygonDrawer({ drawing, onPoint, onMouseMove }) {
  useMapEvents({
    click: (e) => { if (!drawing) return; onPoint([e.latlng.lat, e.latlng.lng]); },
    mousemove: (e) => { if (!drawing) return; onMouseMove([e.latlng.lat, e.latlng.lng]); }
  });
  return null;
}

function isPointInPolygon(point, geometry) {
  const [px, py] = point;
  let inside = false;
  const rings = geometry.type === 'Polygon' ? geometry.coordinates : geometry.coordinates.flat(1);
  for (const ring of rings) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i]; const [xj, yj] = ring[j];
      const intersect = ((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
  }
  return inside;
}

// Gate points are [lng, lat] pairs to match GeoJSON route coordinates.
function gateCrossProductJs(o, a, b) {
  return (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
}

function gateSegmentsIntersectJs(p1, p2, p3, p4) {
  const d1 = gateCrossProductJs(p3, p4, p1);
  const d2 = gateCrossProductJs(p3, p4, p2);
  const d3 = gateCrossProductJs(p1, p2, p3);
  const d4 = gateCrossProductJs(p1, p2, p4);
  return ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
         ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0));
}

// Draw-time suggestion: if every currently-assigned route crossing this gate
// line does so from the same raw side, it's likely a genuine one-way arm.
// Always editable afterwards in the confirmation popup.
function suggestGateDirectionFromRoutes(routes, p1, p2) {
  const sides = new Set();
  for (const feature of routes) {
    const coords = feature.geometry?.coordinates;
    if (!coords || coords.length < 2) continue;
    for (let i = 0; i < coords.length - 1; i++) {
      const a = coords[i], b = coords[i + 1];
      if (gateSegmentsIntersectJs(a, b, p1, p2)) {
        sides.add(gateCrossProductJs(p1, p2, a) > 0 ? 1 : -1);
      }
    }
  }
  if (sides.size === 0) return 'two-way';
  if (sides.size === 1) return sides.has(1) ? 'entry' : 'exit';
  return 'two-way';
}

function percentageToColour(pct, maxPct) {
  const intensity = Math.min(pct / maxPct, 1);
  const r = Math.round(61 + (255 - 61) * (1 - intensity));
  const g = Math.round(26 + (255 - 26) * (1 - intensity));
  const b = Math.round(110 + (255 - 110) * (1 - intensity));
  return `rgb(${r},${g},${b})`;
}

function roadColour(highway) {
  const colours = {
    motorway: '#E8001C', motorway_link: '#E8001C',
    trunk: '#FF6B00', trunk_link: '#FF6B00',
    primary: '#FF9900', primary_link: '#FF9900',
    secondary: '#FFD000', secondary_link: '#FFD000',
    tertiary: '#999999', tertiary_link: '#999999',
    residential: '#CCCCCC', living_street: '#CCCCCC',
    unclassified: '#CCCCCC', service: '#DDDDDD',
    busway: '#00A896'
  };
  return colours[highway] || '#CCCCCC';
}

function roadWeight(highway) {
  const weights = {
    motorway: 5, motorway_link: 3, trunk: 4, trunk_link: 3,
    primary: 4, primary_link: 3, secondary: 3, secondary_link: 2,
    tertiary: 2, tertiary_link: 2, residential: 1.5, living_street: 1,
    unclassified: 1.5, service: 1, busway: 2
  };
  return weights[highway] || 1.5;
}

const DEFAULT_MANUAL_MODES = [
  { code: 1, name: 'Work from home', percentage: 0 },
  { code: 2, name: 'Metro/tram', percentage: 0 },
  { code: 3, name: 'Train', percentage: 0 },
  { code: 4, name: 'Bus', percentage: 0 },
  { code: 5, name: 'Taxi', percentage: 0 },
  { code: 6, name: 'Motorcycle', percentage: 0 },
  { code: 7, name: 'Car driver', percentage: 0 },
  { code: 8, name: 'Car passenger', percentage: 0 },
  { code: 9, name: 'Bicycle', percentage: 0 },
  { code: 10, name: 'On foot', percentage: 0 },
  { code: 11, name: 'Other', percentage: 0 },
];

function App() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('dashboard');
  const [projectId, setProjectId] = useState(null);
  const [projectName, setProjectName] = useState('');
  const [projectNumber, setProjectNumber] = useState('');
  const [projectClient, setProjectClient] = useState('');
  const [projectPreparedBy, setProjectPreparedBy] = useState('');
  const [projectCheckedBy, setProjectCheckedBy] = useState('');
  const [outputAreas, setOutputAreas] = useState(null);
  const [selectedOAs, setSelectedOAs] = useState([]);
  const [zoom, setZoom] = useState(7);
  const [pinMode, setPinMode] = useState(false);
  const [pinLocation, setPinLocation] = useState(null);
  const [railStations, setRailStations] = useState(null);
  const [railLoading, setRailLoading] = useState(false);
  const [railError, setRailError] = useState(null);
  const [busStops, setBusStops] = useState(null);
  const [busLoading, setBusLoading] = useState(false);
  const [busError, setBusError] = useState(null);
  const [expandedBus, setExpandedBus] = useState(null);
  const [busRoute, setBusRoute] = useState(null);       // { shape_id, points, line, dest }
  const [busRouteLoading, setBusRouteLoading] = useState(null); // shape_id being fetched
  const [busRouteEditing, setBusRouteEditing] = useState(false);
  const [multiMode, setMultiMode] = useState(false);
  const [odFlows, setOdFlows] = useState(null);
  const [odLoading, setOdLoading] = useState(false);
  const [odError, setOdError] = useState(null);
  // Radius (km) from the site within which destinations get fine LSOA detail;
  // beyond it they're coarser MSOAs. Replaces the old local-authority split.
  const [distributionRadiusKm, setDistributionRadiusKm] = useState(10);
  const [destLayer, setDestLayer] = useState(null);
  // Education-purpose schools with their gravity-distributed trip counts, for the
  // map layer. Populated when an assignment runs with an education trip split.
  const [educationSchools, setEducationSchools] = useState(null);
  // School URNs the planner has excluded as destinations (their share
  // redistributes to the remaining schools). Persisted with the project.
  const [excludedSchools, setExcludedSchools] = useState([]);
  const excludeParam = excludedSchools.length ? `&exclude=${excludedSchools.join(',')}` : '';
  // Routing centroids per destination: { [code]: { lat, lng, edited } }.
  // Fetched as population-weighted centroids; the user can drag them onto the
  // road network and the adjusted point is used for assignment routing.
  const [destCentroids, setDestCentroids] = useState({});
  const [destLoading, setDestLoading] = useState(false);
  const [devSize, setDevSize] = useState('');
  const [amArrivals, setAmArrivals] = useState('');
  const [amDepartures, setAmDepartures] = useState('');
  const [pmArrivals, setPmArrivals] = useState('');
  const [pmDepartures, setPmDepartures] = useState('');
  const [assignPeriod, setAssignPeriod] = useState('am');
  const [dockOpen, setDockOpen] = useState(true);
  // Junction turning matrices captured from the schematic editor (development
  // traffic), plus surveyed existing flows entered/pasted in Results.
  // existingFlows is in-memory only until a projects.existing_flows column is
  // added — see [[project-ui-redesign]] TODO.
  const [junctionMatrices, setJunctionMatrices] = useState(null);
  const [existingFlows, setExistingFlows] = useState({});
  // User-composable junction "flow scenarios" (Existing/Development leaves +
  // growth/sum derivations) shown as tabs in Results → Junction matrices.
  const [flowScenarios, setFlowScenarios] = useState(DEFAULT_FLOW_SCENARIOS);
  const [amPeakTime] = useState('08:00');
  const [pmPeakTime] = useState('17:00');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [modeShare, setModeShare] = useState(null);
  const [modeLoading, setModeLoading] = useState(false);
  const [modeShareMethod, setModeShareMethod] = useState('census');
  const [manualModes, setManualModes] = useState(DEFAULT_MANUAL_MODES);
  // Per-area mode-split overrides. Keyed by local-authority name (derived
  // from the census zone-name prefix), value = { car, rail, bus, cycle, walk,
  // other } percentages summing to 100. Lets the user reassign a whole area's
  // trips to sustainable modes (e.g. central London → mostly rail); only the
  // car % feeds the road assignment.
  const [modeOverrides, setModeOverrides] = useState({});
  // Zone-level overrides (keyed by destination code) take precedence over the
  // local-authority override for that one zone.
  const [zoneOverrides, setZoneOverrides] = useState({});
  const [modeOverrideEditingLad, setModeOverrideEditingLad] = useState(null);
  const [modeOverrideEditingZone, setModeOverrideEditingZone] = useState(null);
  const [originMSOA, setOriginMSOA] = useState(null);
  const [roadNetwork, setRoadNetwork] = useState(null);
  const [roadLoading, setRoadLoading] = useState(false);
  const [roadError, setRoadError] = useState(null);
  const [roadRadius] = useState('1000');
  const [assignedNetwork, setAssignedNetwork] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [siteAccessNode, setSiteAccessNode] = useState(null);
  const [accessMode, setAccessMode] = useState(false);
  const [schematicNodes, setSchematicNodes] = useState([]);
  const [schematicLinks, setSchematicLinks] = useState([]);
  const [schematicSiteBox, setSchematicSiteBox] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [flyTarget, setFlyTarget] = useState(null);
  const [showDistDetail, setShowDistDetail] = useState(false);
  const [showAggregated, setShowAggregated] = useState(false);
  const [selectedRouteDestination, setSelectedRouteDestination] = useState(null);
  // Manual route editing: which assigned route ({destination, direction}) is
  // having its path reshaped on the map.
  const [editingRoute, setEditingRoute] = useState(null);
  // Bumped on every route edit so the routes <GeoJSON> layer (which only
  // re-renders when its key changes) redraws the reshaped line.
  const [routeEditVersion, setRouteEditVersion] = useState(0);
  const [roadLabelPositions, setRoadLabelPositions] = useState({});
  const [labelMoveMode, setLabelMoveMode] = useState(false);
  const [openStep, setOpenStep] = useState(1);
  const [studyPolygon, setStudyPolygon] = useState([]);
  const [drawingPolygon, setDrawingPolygon] = useState(false);
  const [polygonPreviewPoint, setPolygonPreviewPoint] = useState(null);
  const [siteBoundary, setSiteBoundary] = useState([]);
  const [drawingSiteBoundary, setDrawingSiteBoundary] = useState(false);
  const [siteBoundaryPreviewPoint, setSiteBoundaryPreviewPoint] = useState(null);
  const [pendingJunctionNodeId, setPendingJunctionNodeId] = useState(null);
  const [pendingGates, setPendingGates] = useState([]);
  const [gateDrawStart, setGateDrawStart] = useState(null);
  const [gateDraftPoint, setGateDraftPoint] = useState(null);
  const [pendingGateConfirm, setPendingGateConfirm] = useState(null);
  const [junctionNumArms, setJunctionNumArms] = useState(3);
  const schematicJunctionMapCallback = useRef(null);
  const junctionMatrixExportRef = useRef(null);
  const mapRef = useRef(null);
  const boundaryFileRef = useRef(null);
  const labelMoveModeRef = useRef(false);
  const assignedNetworkRef = useRef(null);
  const roadLabelPositionsRef = useRef({});

  function MapRefSetter() {
    const map = useMap();
    React.useEffect(() => { mapRef.current = map; }, [map]);
    return null;
  }

  function LabelPlacer() {
    useMapEvents({
      click: (e) => {
        if (!labelMoveModeRef.current) return;
        const clickLat = e.latlng.lat;
        const clickLng = e.latlng.lng;
        const network = assignedNetworkRef.current;

        // Find nearest segment and get its trip count
        let nearestTrips = 0;
        let nearestDist = Infinity;
        if (network?.aggregated?.features) {
          network.aggregated.features.forEach(f => {
            const trips = f.properties.trips || 0;
            const coords = f.geometry.coordinates;
            for (let i = 0; i < coords.length - 1; i++) {
              const midLng = (coords[i][0] + coords[i+1][0]) / 2;
              const midLat = (coords[i][1] + coords[i+1][1]) / 2;
              const d = Math.sqrt((midLat - clickLat) ** 2 + (midLng - clickLng) ** 2);
              if (d < nearestDist) { nearestDist = d; nearestTrips = trips; }
            }
          });
        }

        const name = window.prompt('Road name:');
        if (!name || !name.trim()) return;
        const key = `${name.trim()}||${Date.now()}`;
        setRoadLabelPositions(prev => ({ ...prev, [key]: { lat: clickLat, lng: clickLng, name: name.trim(), trips: nearestTrips } }));
      },
      contextmenu: (e) => {
        if (!labelMoveModeRef.current) return;
        const clickLat = e.latlng.lat;
        const clickLng = e.latlng.lng;
        setRoadLabelPositions(prev => {
          const next = { ...prev };
          let nearestKey = null, nearestDist = Infinity;
          Object.entries(roadLabelPositionsRef.current).forEach(([key, pos]) => {
            const d = Math.sqrt((pos.lat - clickLat) ** 2 + (pos.lng - clickLng) ** 2);
            if (d < nearestDist) { nearestDist = d; nearestKey = key; }
          });
          if (nearestKey && nearestDist < 0.05) delete next[nearestKey];
          return next;
        });
      }
    });
    return null;
  }
  const [showSetup, setShowSetup] = useState(false);
  const [timePeriods, setTimePeriods] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [activeScenarioId, setActiveScenarioId] = useState(null);
  const [activeTimePeriodId, setActiveTimePeriodId] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showProjectDetailsModal, setShowProjectDetailsModal] = useState(false);
  const [exportIncludeMap, setExportIncludeMap] = useState(true);
  const [exportIncludeSchematic, setExportIncludeSchematic] = useState(true);
  const [exportIncludeMatrices, setExportIncludeMatrices] = useState(true);
  const [exportGenerating, setExportGenerating] = useState(false);
  const [mainTab, setMainTab] = useState('site');
  const [showActiveTravelModal, setShowActiveTravelModal] = useState(false);
  const [atMode, setAtMode] = useState('walking');
  const [atSelectedMinutes, setAtSelectedMinutes] = useState(new Set([15]));
  const [atCustomMinutes, setAtCustomMinutes] = useState('');
  const [atDestTypes, setAtDestTypes] = useState({
    school: true, hospital: true, gp: true, pharmacy: false,
    supermarket: false, park: true, leisure: false,
    train_station: true, bus_stop: false,
  });
  const [atLoading, setAtLoading] = useState(false);
  const [atResults, setAtResults] = useState(null);
  const [atError, setAtError] = useState(null);
  const [atShowAggregated, setAtShowAggregated] = useState(false);
  const [atLayerVisibility, setAtLayerVisibility] = useState({
    isochrone: true, destinations: true, routes: true,
  });
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  // Per-tool docking (see ToolSlot). toolDocks[key] = 'left'|'right'|'float'|'popout'
  // (missing = 'left'); toolFloats[key] = { x, y } for a floated tool.
  const [toolDocks, setToolDocks] = useState({});
  const [toolFloats, setToolFloats] = useState({});
  const [rightZoneEl, setRightZoneEl] = useState(null); // right dock zone DOM node
  const rightZoneRef = React.useCallback(node => setRightZoneEl(node), []);
  const dockOf = k => toolDocks[k] || 'left';
  // A detached tool (its own window or a floating card) must keep its body open
  // regardless of the shared `openStep` accordion, which the rail changes.
  const isDetached = k => { const d = dockOf(k); return d === 'popout' || d === 'float'; };
  const setToolDock = (k, mode) => setToolDocks(p => ({ ...p, [k]: mode }));
  const setToolFloatAt = (k, pos) => setToolFloats(p => ({ ...p, [k]: pos }));
  const [accountTab, setAccountTab] = useState('details');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [accountMsg, setAccountMsg] = useState('');
  const [accountLoading, setAccountLoading] = useState(false);
  const [layerVisibility, setLayerVisibility] = useState({
    studyArea: true,
    destinationZones: true,
    assignedZones: false,
    destCentroids: false,
    outputAreas: true,
    roadNetwork: true,
    arrivals: true,
    departures: true,
    siteAccess: true,
    railStations: true,
    busStops: true,
    educationSchools: true,
  });

  function toggleLayer(key) {
    setLayerVisibility(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function toggleStep(n) { setOpenStep(prev => prev === n ? null : n); }

  function LoadingBar({ label }) {
    return (
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: '#7B4DBF', marginBottom: 4, fontWeight: 600 }}>{label}</div>
        <div style={{ height: 4, background: '#EDE5F8', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: '40%', background: 'linear-gradient(90deg, #3D1A6E, #7B4DBF, #3D1A6E)',
            borderRadius: 2,
            animation: 'odinSlide 1.4s ease-in-out infinite',
          }} />
        </div>
        <style>{`@keyframes odinSlide { 0% { transform: translateX(-200%); } 100% { transform: translateX(350%); } }`}</style>
      </div>
    );
  }

  function stepHeader(n, label, unlocked = true, complete = false) {
    const isOpen = openStep === n;
    return (
      <div onClick={() => unlocked && toggleStep(n)}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', borderRadius: 6, marginBottom: isOpen ? 10 : 6, cursor: unlocked ? 'pointer' : 'default', background: isOpen ? '#3D1A6E' : complete ? '#F0FDF9' : unlocked ? '#F5F0FA' : '#FAFAFA', border: `1.5px solid ${isOpen ? '#3D1A6E' : complete ? '#0D9488' : unlocked ? '#EDE5F8' : '#F0EDF8'}` }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: isOpen ? 'white' : complete ? '#0D9488' : unlocked ? '#3D1A6E' : '#C4BDD4' }}>
          {complete && !isOpen ? '✅ ' : ''}{label}
        </div>
        {unlocked && <div style={{ fontSize: 10, color: isOpen ? 'rgba(255,255,255,0.7)' : '#C4BDD4' }}>{isOpen ? '▲' : '▼'}</div>}
      </div>
    );
  }

  // Shows a "finish these first" checklist when a step is blocked, so users
  // are never stuck on a locked section without knowing what's missing.
  function prereqNotice(items, title = 'Finish these first:') {
    return (
      <div style={{ background: '#FFF9E6', border: '1px solid #FFD000', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#3D1A6E', marginBottom: 8 }}>{title}</div>
        {items.map((c, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 13, lineHeight: '18px', flexShrink: 0 }}>{c.done ? '✅' : '⬜'}</span>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: c.done ? '#0D9488' : '#1C1C2E' }}>{c.label}</div>
              {!c.done && c.hint && <div style={{ fontSize: 11, color: '#6B6880', marginTop: 1 }}>{c.hint}</div>}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const pinLocationRef = useRef(null);
  const multiModeRef = useRef(false);
  const selectedOAsRef = useRef([]);

  pinLocationRef.current = pinLocation;
  multiModeRef.current = multiMode;
  selectedOAsRef.current = selectedOAs;
  labelMoveModeRef.current = labelMoveMode;
  assignedNetworkRef.current = assignedNetwork;
  roadLabelPositionsRef.current = roadLabelPositions;

  const size = parseFloat(devSize) || 0;
  const amArr = parseFloat(amArrivals) || 0;
  const amDep = parseFloat(amDepartures) || 0;
  const pmArr = parseFloat(pmArrivals) || 0;
  const pmDep = parseFloat(pmDepartures) || 0;
  const amTotal = size > 0 && (amArr + amDep) > 0 ? Math.round((amArr + amDep) * size) : null;
  const pmTotal = size > 0 && (pmArr + pmDep) > 0 ? Math.round((pmArr + pmDep) * size) : null;
  const totalPersonTrips = assignPeriod === 'both'
    ? (amTotal !== null || pmTotal !== null ? (amTotal || 0) + (pmTotal || 0) : null)
    : assignPeriod === 'pm' ? pmTotal : amTotal;

  // Purpose split ({employment,education,other} %) for a period under the active
  // scenario — honours "same trip purpose as Scenario 1" and falls back to the
  // NTS default for the period's start time. `startTimeFallback` covers legacy
  // projects with no setup periods. Used to carve Education out of assignment.
  function purposeSplitForPeriod(tp, startTimeFallback) {
    const active = scenarios.find(sc => sc.id === activeScenarioId) || scenarios[0];
    const s1 = scenarios[0];
    const src = (active && s1 && active !== s1 && active.sameTripPurpose) ? s1 : active;
    const stored = tp && src && src.tripPurpose && src.tripPurpose[tp.id];
    const band = (src && src.purposeBand) || 'hour';
    return stored || defaultPurposeSplit(tp ? tp.startTime : startTimeFallback, band);
  }

  // Destination zones that actually received an assigned car route (a subset of
  // the full distribution — most zones round to 0 cars and get none). Used by
  // the "Assigned zones" map layer + its count.
  const routedDestSet = new Set((assignedNetwork?.routes?.features || []).map(f => f.properties.destination));
  const routedZoneCount = destLayer ? destLayer.features.filter(f => routedDestSet.has(f.properties.code)).length : 0;
  const totalZoneCount = destLayer ? destLayer.features.length : 0;

  // Keep the legacy trip fields (which drive amTotal/pmTotal and therefore
  // trip assignment) in sync with the ACTIVE scenario, honouring its
  // match-with-first options. Without this, switching scenarios left
  // assignment stuck on the first scenario's trips.
  // Auto-load nearest rail stations + bus stops when the Transit tab is opened
  // with a site pin set. Depends only on the tab + pin so it fires on open /
  // pin change, not every render; each guard stops its own re-fetch once loaded.
  React.useEffect(() => {
    if (mainTab !== 'transit' || !pinLocation) return;
    let cancelled = false;
    if (!railStations && !railLoading && !railError) {
      (async () => {
        setRailLoading(true); setRailError(null);
        try {
          const res = await authedFetch(`${BACKEND_URL}/api/nearest-rail-station?lat=${pinLocation[0]}&lng=${pinLocation[1]}&n=5`);
          const data = await res.json();
          if (cancelled) return;
          if (data && data.stations && data.stations.length) setRailStations(data.stations);
          else setRailError(data && data.detail ? data.detail : 'No stations found');
        } catch (e) { if (!cancelled) setRailError('Lookup failed — check your connection.'); }
        if (!cancelled) setRailLoading(false);
      })();
    }
    if (!busStops && !busLoading && !busError) {
      (async () => {
        setBusLoading(true); setBusError(null);
        try {
          // Prefer BODS services (stops + peak frequencies); fall back to plain
          // OSM stop locations if the bus_stops table isn't populated yet.
          let res = await authedFetch(`${BACKEND_URL}/api/nearest-bus-services?lat=${pinLocation[0]}&lng=${pinLocation[1]}&radius_m=800&n=8`);
          let data = await res.json();
          if (!(data && data.stops && data.stops.length)) {
            res = await authedFetch(`${BACKEND_URL}/api/nearest-bus-stops?lat=${pinLocation[0]}&lng=${pinLocation[1]}&radius_m=800&n=8`);
            data = await res.json();
          }
          if (cancelled) return;
          if (data && data.stops && data.stops.length) setBusStops(data.stops);
          else setBusError(data && data.detail ? data.detail : 'No bus stops within 800 m');
        } catch (e) { if (!cancelled) setBusError('Lookup failed — check your connection.'); }
        if (!cancelled) setBusLoading(false);
      })();
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab, pinLocation]);

  React.useEffect(() => {
    if (!scenarios.length || !activeScenarioId) return;
    const s = scenarios.find(sc => sc.id === activeScenarioId);
    if (!s) return;
    const idx = scenarios.indexOf(s);
    const s1 = scenarios[0];
    const tp0 = timePeriods[0], tp1 = timePeriods[1];
    if (s.tripEntryMode === 'direct') {
      setDevSize('1');
      const d0 = (s.directTrips && s.directTrips[tp0 && tp0.id]) || {};
      setAmArrivals(d0.arrivals || ''); setAmDepartures(d0.departures || '');
      const d1 = (s.directTrips && s.directTrips[tp1 && tp1.id]) || {};
      setPmArrivals(d1.arrivals || ''); setPmDepartures(d1.departures || '');
    } else {
      const effDev = (idx > 0 && s.sameDevSize && s1) ? s1.devSize : s.devSize;
      setDevSize(effDev || '');
      const ratesSc = (idx > 0 && s.sameTripRates && s1) ? s1 : s;
      const r0 = (ratesSc.tripRates && ratesSc.tripRates[tp0 && tp0.id]) || {};
      setAmArrivals(r0.arrivals || ''); setAmDepartures(r0.departures || '');
      const r1 = (ratesSc.tripRates && ratesSc.tripRates[tp1 && tp1.id]) || {};
      setPmArrivals(r1.arrivals || ''); setPmDepartures(r1.departures || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScenarioId, scenarios, timePeriods]);

  // Existing-flow editors used by the Results junction matrices.
  function setExistingCell(label, from, to, value) {
    setExistingFlows(prev => {
      const j = { ...(prev[label] || {}) };
      j[from] = { ...(j[from] || {}), [to]: value };
      return { ...prev, [label]: j };
    });
  }
  function pasteExistingMatrix(label, arms, grid) {
    setExistingFlows(prev => {
      const j = {};
      arms.forEach((f, i) => { j[f] = {}; arms.forEach((t, k) => { if (i !== k) j[f][t] = grid[i][k]; }); });
      return { ...prev, [label]: j };
    });
  }

  React.useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => { setSession(session); setLoading(false); });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setSession(session));
    return () => subscription.unsubscribe();
  }, []);

  const handleLoad = useCallback((data) => { setOutputAreas(data); }, []);

  function handleNewProject(meta) {
    const name = typeof meta === 'string' ? meta : meta.name;
    setProjectName(name);
    setProjectNumber(typeof meta === 'object' ? meta.projectNumber || '' : '');
    setProjectClient(typeof meta === 'object' ? meta.client || '' : '');
    setProjectPreparedBy(typeof meta === 'object' ? meta.preparedBy || '' : '');
    setProjectCheckedBy(typeof meta === 'object' ? meta.checkedBy || '' : '');
    setModeShareMethod('census');
    setTimePeriods([]);
    setScenarios([]);
    setActiveScenarioId(null);
    setActiveTimePeriodId(null);
    setProjectId(null); setSelectedOAs([]);
    setPinLocation(null); setOdFlows(null); setDestLayer(null);
    setRailStations(null); setRailError(null); setBusStops(null); setBusError(null); setExpandedBus(null); setBusRoute(null); setBusRouteEditing(false);
    setDevSize(''); setAmArrivals(''); setAmDepartures('');
    setPmArrivals(''); setPmDepartures(''); setAssignPeriod('am');
    setModeShare(null); setOriginMSOA(null);
    setRoadNetwork(null); setAssignedNetwork(null); setEducationSchools(null);
    setSiteAccessNode(null); setAccessMode(false);
    setShowEditor(false);
    setSchematicNodes([]); setSchematicLinks([]); setSchematicSiteBox(null);
    setJunctionMatrices(null); setExistingFlows({});
    setFlowScenarios(DEFAULT_FLOW_SCENARIOS);
    setModeOverrides({}); setZoneOverrides({});
    setDestCentroids({}); setRoadLabelPositions({}); setSiteBoundary([]);
    setManualModes(DEFAULT_MANUAL_MODES);
    setSearchQuery(''); setSearchResults([]); setFlyTarget(null);
    setStudyPolygon([]); setDrawingPolygon(false); setPolygonPreviewPoint(null);
    setShowSetup(true);
    setView('map');
  }

  async function handleLoadProject(project) {
    setProjectId(project.id);
    setProjectName(project.name);
    setSelectedOAs(project.selected_oas || []);
    setPinLocation(project.pin_location || null);
    setDevSize(project.dev_size ? String(project.dev_size) : '');
    setAmArrivals(project.am_arrivals ? String(project.am_arrivals) : '');
    setAmDepartures(project.am_departures ? String(project.am_departures) : '');
    setPmArrivals(project.pm_arrivals ? String(project.pm_arrivals) : '');
    setPmDepartures(project.pm_departures ? String(project.pm_departures) : '');
    setProjectNumber(project.project_number || '');
    setProjectClient(project.client || '');
    setProjectPreparedBy(project.prepared_by || '');
    setProjectCheckedBy(project.checked_by || '');
    setOdFlows(project.od_flows || null);
    if (project.od_flows && project.od_flows.radius_km) setDistributionRadiusKm(project.od_flows.radius_km);
    setModeShare(project.mode_share || null);
    setOriginMSOA(project.origin_msoa || null);
    // Restore study area + mode-share method/manual modes so a reloaded project
    // keeps its study area (needed to load the road network) and mode share.
    setStudyPolygon(project.study_polygon || []);
    setModeShareMethod(project.mode_share_method || 'census');
    setManualModes(project.manual_modes || DEFAULT_MANUAL_MODES);
    // Restore scenarios / time periods / junction existing flows (persisted as jsonb).
    const loadedScenarios = project.scenarios || [];
    const loadedPeriods = project.time_periods || [];
    setScenarios(loadedScenarios);
    setTimePeriods(loadedPeriods);
    setActiveScenarioId(loadedScenarios.length ? loadedScenarios[0].id : null);
    setActiveTimePeriodId(loadedPeriods.length ? loadedPeriods[0].id : null);
    setExistingFlows(project.existing_flows || {});
    setFlowScenarios(project.flow_scenarios && project.flow_scenarios.length ? project.flow_scenarios : DEFAULT_FLOW_SCENARIOS);
    // Restore the computed / hand-edited artefacts so a reloaded project is
    // complete (road network, assignment, schematic, overrides, boundaries).
    setRoadNetwork(project.road_network || null);
    setAssignedNetwork(project.assigned_network || null);
    setEducationSchools(project.education_schools || null);
    setExcludedSchools(project.excluded_schools || []);
    setSiteAccessNode(project.site_access_node || null);
    setSchematicNodes(project.schematic_nodes || []);
    setSchematicLinks(project.schematic_links || []);
    setSchematicSiteBox(project.schematic_site_box || null);
    // Development junction matrices are computed in the schematic editor; persist
    // them so the Results → Junction matrices tab shows on reload without having
    // to reopen the editor. Reopening the editor recomputes the same values.
    setJunctionMatrices(project.junction_matrices || null);
    setModeOverrides(project.mode_overrides || {});
    setZoneOverrides(project.zone_overrides || {});
    setDestCentroids(project.dest_centroids || {});
    setRoadLabelPositions(project.road_label_positions || {});
    setSiteBoundary(project.site_boundary || []);
    setDestLayer(null); setAccessMode(false);
    setShowEditor(false);
    setView('overview');
    if (project.od_flows && project.od_flows.flows) {
      await fetchDestBoundaries(project.od_flows.flows);
    }
  }

  async function saveProject() {
    setSaving(true); setSaveMsg('');
    const { data: { user } } = await supabase.auth.getUser();
    const projectData = {
      userid: user.id, name: projectName,
      project_number: projectNumber || null,
      client: projectClient || null,
      prepared_by: projectPreparedBy || null,
      checked_by: projectCheckedBy || null,
      pin_location: pinLocation, selected_oas: selectedOAs,
      dev_size: devSize ? parseFloat(devSize) : null,
      od_flows: odFlows,
      scenarios: scenarios,
      time_periods: timePeriods,
      existing_flows: existingFlows,
      flow_scenarios: flowScenarios,
      study_polygon: studyPolygon,
      mode_share: modeShare,
      mode_share_method: modeShareMethod,
      manual_modes: manualModes,
      origin_msoa: originMSOA,
      road_network: roadNetwork,
      assigned_network: assignedNetwork,
      education_schools: educationSchools,
      excluded_schools: excludedSchools,
      site_access_node: siteAccessNode,
      schematic_nodes: schematicNodes,
      schematic_links: schematicLinks,
      schematic_site_box: schematicSiteBox,
      mode_overrides: modeOverrides,
      zone_overrides: zoneOverrides,
      dest_centroids: destCentroids,
      road_label_positions: roadLabelPositions,
      site_boundary: siteBoundary,
      junction_matrices: junctionMatrices
    };
    let error;
    if (projectId) {
      ({ error } = await supabase.from('projects').update(projectData).eq('id', projectId));
    } else {
      const { data, error: insertError } = await supabase.from('projects').insert(projectData).select().single();
      error = insertError;
      if (data) setProjectId(data.id);
    }
    setSaving(false);
    if (error) {
      const kb = Math.round(JSON.stringify(projectData).length / 1024);
      console.error('Save error:', error, '| payload ~' + kb + 'KB',
        '| road_network ~' + Math.round(JSON.stringify(roadNetwork || null).length / 1024) + 'KB',
        '| assigned_network ~' + Math.round(JSON.stringify(assignedNetwork || null).length / 1024) + 'KB');
      setSaveMsg('Error saving: ' + (error.message || error.details || error.hint || 'unknown') + ' (' + kb + 'KB)');
    } else {
      setSaveMsg('Project saved ✅');
    }
    setTimeout(() => setSaveMsg(''), 8000);
  }

  function handleSetupComplete({ timePeriods: tps, scenarios: scs }) {
    setTimePeriods(tps);
    setScenarios(scs);
    if (scs.length > 0) setActiveScenarioId(scs[0].id);
    if (tps.length > 0) setActiveTimePeriodId(tps[0].id);
    if (scs.length > 0) {
      const s = scs[0];
      const isDirect = s.tripEntryMode === 'direct';
      if (s.devSize && !isDirect) setDevSize(s.devSize);
      const firstTp = tps[0];
      if (firstTp) {
        if (isDirect && s.directTrips?.[firstTp.id]) {
          // Direct mode — trips are already vehicle trips, set as rates with devSize=1
          setDevSize('1');
          setAmArrivals(s.directTrips[firstTp.id].arrivals || '');
          setAmDepartures(s.directTrips[firstTp.id].departures || '');
        } else if (s.tripRates[firstTp.id]) {
          setAmArrivals(s.tripRates[firstTp.id].arrivals || '');
          setAmDepartures(s.tripRates[firstTp.id].departures || '');
        }
      }
      if (tps[1]) {
        if (isDirect && s.directTrips?.[tps[1].id]) {
          setPmArrivals(s.directTrips[tps[1].id].arrivals || '');
          setPmDepartures(s.directTrips[tps[1].id].departures || '');
        } else if (s.tripRates[tps[1].id]) {
          setPmArrivals(s.tripRates[tps[1].id].arrivals || '');
          setPmDepartures(s.tripRates[tps[1].id].departures || '');
        }
      }
    }
    setShowSetup(false);
  }

  function handleExportPDF() {
    setExportIncludeMap(!!destLayer);
    setExportIncludeSchematic(schematicNodes.length > 0);
    setExportIncludeMatrices(schematicNodes.length > 0);
    setShowExportModal(true);
  }

  async function runExportPDF() {
    setExportGenerating(true);
    const junctionMatrices = exportIncludeMatrices ? (junctionMatrixExportRef.current?.() || null) : null;
    const modeAssumptions = [
      // Local-authority-level overrides (excluding zones that carry their own
      // override, which are reported separately below to avoid double count).
      ...Object.keys(modeOverrides).map(lad => {
        const split = modeOverrides[lad];
        const ladPct = odFlows ? odFlows.flows.filter(f => ladOf(f) === lad && !zoneOverrides[f.destination]).reduce((s, f) => s + f.percentage, 0) : 0;
        const devTrips = totalPersonTrips ? Math.round(ladPct / 100 * totalPersonTrips) : null;
        return { lad, split, devTrips, carTrips: devTrips != null ? Math.round(devTrips * split.car / 100) : null };
      }),
      // Zone-level overrides.
      ...Object.keys(zoneOverrides).map(dest => {
        const split = zoneOverrides[dest];
        const flow = odFlows ? odFlows.flows.find(f => f.destination === dest) : null;
        const devTrips = (flow && totalPersonTrips) ? Math.round(flow.percentage / 100 * totalPersonTrips) : null;
        return { lad: `${flow ? (flow.name || dest) : dest} (zone)`, split, devTrips, carTrips: devTrips != null ? Math.round(devTrips * split.car / 100) : null };
      }),
    ];
    await exportPDF({
      projectName, projectNumber, projectClient, projectPreparedBy, projectCheckedBy,
      selectedOAs, pinLocation, devSize,
      amArr, amDep, pmArr, pmDep, amTotal, pmTotal,
      totalPersonTrips, assignPeriod, amPeakTime, pmPeakTime,
      odFlows, modeShare, originMSOA, assignedNetwork, roadRadius,
      educationSchools,
      includeMap: exportIncludeMap,
      destLayer,
      mapboxToken: MAPBOX_TOKEN,
      includeSchematic: exportIncludeSchematic,
      schematicNodes,
      schematicLinks,
      schematicSiteBox,
      junctionMatrices,
      modeAssumptions: modeAssumptions.length > 0 ? modeAssumptions : null,
    });
    setExportGenerating(false);
    setShowExportModal(false);
  }


  function handleExportExcel() {
    exportExcel({ projectName, selectedOAs, pinLocation, devSize, amArr, amDep, pmArr, pmDep, amTotal, pmTotal, totalPersonTrips, assignPeriod, amPeakTime, pmPeakTime, odFlows, modeShare, originMSOA, assignedNetwork, roadRadius, educationSchools });
  }

  // The PDF export options modal, as a function so it can render on any view
  // (map, results, …) — it is position:fixed, so where it's rendered doesn't
  // matter, only that it's mounted while showExportModal is true.
  function renderExportModal() {
    if (!showExportModal) return null;
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ background: 'white', borderRadius: 10, width: 400, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#3D1A6E', marginBottom: 6, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Export PDF Report</div>
          <div style={{ fontSize: 12, color: '#6B6880', marginBottom: 20 }}>Choose what to include in your report:</div>

          <div onClick={() => destLayer && setExportIncludeMap(prev => !prev)}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 8, border: `1.5px solid ${exportIncludeMap ? '#3D1A6E' : '#EDE5F8'}`, background: exportIncludeMap ? '#F5F0FA' : 'white', marginBottom: 10, cursor: destLayer ? 'pointer' : 'not-allowed', opacity: destLayer ? 1 : 0.4 }}>
            <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${exportIncludeMap ? '#3D1A6E' : '#C4BDD4'}`, background: exportIncludeMap ? '#3D1A6E' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {exportIncludeMap && <div style={{ color: 'white', fontSize: 12, lineHeight: 1 }}>✓</div>}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1C1C2E' }}>🗺️ Destination zones map</div>
              <div style={{ fontSize: 11, color: '#6B6880', marginTop: 2 }}>{destLayer ? `${destLayer.features.length} destination zones` : 'Not available — run census data first'}</div>
            </div>
          </div>

          <div onClick={() => schematicNodes.length > 0 && setExportIncludeSchematic(prev => !prev)}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 8, border: `1.5px solid ${exportIncludeSchematic ? '#3D1A6E' : '#EDE5F8'}`, background: exportIncludeSchematic ? '#F5F0FA' : 'white', marginBottom: 10, cursor: schematicNodes.length > 0 ? 'pointer' : 'not-allowed', opacity: schematicNodes.length > 0 ? 1 : 0.4 }}>
            <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${exportIncludeSchematic ? '#3D1A6E' : '#C4BDD4'}`, background: exportIncludeSchematic ? '#3D1A6E' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {exportIncludeSchematic && <div style={{ color: 'white', fontSize: 12, lineHeight: 1 }}>✓</div>}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1C1C2E' }}>📐 Schematic diagram</div>
              <div style={{ fontSize: 11, color: '#6B6880', marginTop: 2 }}>{schematicNodes.length > 0 ? `${schematicNodes.length} junctions · ${schematicLinks.length} links` : 'Not available — draw schematic first'}</div>
            </div>
          </div>

          <div onClick={() => schematicNodes.length > 0 && setExportIncludeMatrices(prev => !prev)}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 8, border: `1.5px solid ${exportIncludeMatrices ? '#3D1A6E' : '#EDE5F8'}`, background: exportIncludeMatrices ? '#F5F0FA' : 'white', marginBottom: 24, cursor: schematicNodes.length > 0 ? 'pointer' : 'not-allowed', opacity: schematicNodes.length > 0 ? 1 : 0.4 }}>
            <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${exportIncludeMatrices ? '#3D1A6E' : '#C4BDD4'}`, background: exportIncludeMatrices ? '#3D1A6E' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {exportIncludeMatrices && <div style={{ color: 'white', fontSize: 12, lineHeight: 1 }}>✓</div>}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1C1C2E' }}>🔢 Junction turning matrices</div>
              <div style={{ fontSize: 11, color: '#6B6880', marginTop: 2 }}>{schematicNodes.length > 0 ? 'Diagram + turning matrix for each junction' : 'Not available — draw schematic first'}</div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setShowExportModal(false)} disabled={exportGenerating}
              style={{ flex: 1, padding: '10px', background: 'white', color: '#6B6880', border: '1.5px solid #EDE5F8', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
              Cancel
            </button>
            <button onClick={runExportPDF} disabled={exportGenerating}
              style={{ flex: 2, padding: '10px', background: exportGenerating ? '#EDE5F8' : '#3D1A6E', color: exportGenerating ? '#7B4DBF' : 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: exportGenerating ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
              {exportGenerating ? 'Generating PDF...' : '📄 Generate PDF'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fetch + draw one bus route's polyline (by GTFS shape_id) when a service is
  // clicked. Clicking the active service again clears it.
  async function showBusRoute(sv) {
    if (!sv || !sv.shape) return;
    setBusRouteEditing(false);
    if (busRoute && busRoute.shape_id === sv.shape) { setBusRoute(null); return; }
    setBusRouteLoading(sv.shape);
    try {
      const res = await authedFetch(`${BACKEND_URL}/api/bus-shape?shape_id=${encodeURIComponent(sv.shape)}`);
      const data = await res.json();
      if (data && data.points && data.points.length) setBusRoute({ shape_id: sv.shape, points: data.points, line: sv.line, dest: sv.dest, synthetic: !!data.synthetic });
      else setBusRoute(null);
    } catch (e) { setBusRoute(null); }
    setBusRouteLoading(null);
  }

  function handlePinDrop(pos) {
    setPinLocation(pos); setPinMode(false);
    setRailStations(null); setRailError(null); setBusStops(null); setBusError(null); setExpandedBus(null); setBusRoute(null); setBusRouteEditing(false);
    setOdFlows(null); setDestLayer(null);
    setModeShare(null); setOriginMSOA(null);
    setRoadNetwork(null); setAssignedNetwork(null);
    setSiteAccessNode(null);
    if (outputAreas && outputAreas.features) {
      const point = [pos[1], pos[0]];
      for (const feature of outputAreas.features) {
        if (isPointInPolygon(point, feature.geometry)) {
          setSelectedOAs([feature.properties.OA11CD]);
          return;
        }
      }
    }
    setSelectedOAs([]);
  }

  function handleOAClick(oaCode) {
    if (!pinLocationRef.current) return;
    if (!multiModeRef.current) return;
    setSelectedOAs(prev => prev.includes(oaCode) ? prev.filter(c => c !== oaCode) : [...prev, oaCode]);
  }

  
  async function handleAccessMapClick(lat, lng) {
    if (!accessMode) return;
    try {
      const polygonParam = encodeURIComponent(JSON.stringify(studyPolygon));
      const res = await authedFetch(
        `${BACKEND_URL}/api/split-edge?pin_lat=${pinLocation[0]}&pin_lng=${pinLocation[1]}&access_lat=${lat}&access_lng=${lng}&polygon=${polygonParam}`
      );
      const data = await res.json();
      if (data.snapped) {
        setSiteAccessNode({ lat: data.access_lat, lng: data.access_lng, osmid: null });
      } else {
        setSiteAccessNode({ lat, lng, osmid: null });
      }
    } catch (err) {
      setSiteAccessNode({ lat, lng, osmid: null });
    }
    setAccessMode(false);
  }

  async function fetchODFlows() {
    if (selectedOAs.length === 0) return;
    setOdLoading(true); setOdError(null);
    setOdFlows(null); setDestLayer(null);
    setModeShare(null); setOriginMSOA(null);
    try {
      // Fetch OD flows AND mode share lookup simultaneously
      const [results, lookupData] = await Promise.all([
        Promise.all(selectedOAs.map(oa => authedFetch(`${BACKEND_URL}/api/od-flows-tiered?oa=${oa}${pinLocation ? `&site_lat=${pinLocation[0]}&site_lng=${pinLocation[1]}&radius_km=${distributionRadiusKm}` : ''}`).then(r => r.json()))),
        authedFetch(`${BACKEND_URL}/api/oa-to-msoa?oa=${selectedOAs[0]}`).then(r => r.json())
      ]);

      // Handle mode share fetch in background
      if (lookupData.msoa) {
        setOriginMSOA(lookupData);
        setModeLoading(true);
        authedFetch(`${BACKEND_URL}/api/mode-share?msoa=${lookupData.msoa}`)
          .then(r => r.json())
          .then(modeData => { setModeShare(modeData); setModeLoading(false); })
          .catch(err => { console.error("Mode share error:", err); setModeLoading(false); });
      }

      // Handle OD flows
      const combinedFlows = {};
      let totalTrips = 0;
      for (const result of results) {
        totalTrips += result.total_trips || 0;
        for (const flow of result.flows || []) {
          if (combinedFlows[flow.destination]) {
            combinedFlows[flow.destination].count += flow.count;
          } else {
            combinedFlows[flow.destination] = {
              destination: flow.destination,
              name: flow.name,
              count: flow.count,
              type: flow.type
            };
          }
        }
      }
      const flows = Object.values(combinedFlows).map(f => ({
        ...f, percentage: totalTrips > 0 ? Math.round((f.count / totalTrips) * 100 * 100) / 100 : 0
      }));
      flows.sort((a, b) => b.count - a.count);
      const flowData = { total_trips: totalTrips, flows, radius_km: distributionRadiusKm };
      setOdFlows(flowData);
      setOdLoading(false);
      await fetchDestBoundaries(flows);
    } catch (err) {
      setOdError("Failed to fetch census data");
      setOdLoading(false);
    }
  }

  async function fetchDestBoundaries(flows) {
    if (!flows || flows.length === 0) return;
    setDestLoading(true);
    const flowMap = {};
    flows.forEach(f => { flowMap[f.destination] = f; });
    const lsoaCodes = flows.filter(f => f.type === 'lsoa').map(f => `'${f.destination}'`).join(',');
    const msoaCodes = flows.filter(f => f.type === 'msoa').map(f => `'${f.destination}'`).join(',');
    const allFeatures = [];
    try {
      if (lsoaCodes) {
        const lsoaRes = await fetch(LSOA_URL + "?where=" + encodeURIComponent(`LSOA11CD IN (${lsoaCodes})`) + "&outFields=LSOA11CD&outSR=4326&f=geojson&resultRecordCount=500");
        const lsoaData = await lsoaRes.json();
        if (lsoaData.features) {
          lsoaData.features.forEach(f => {
            const flow = flowMap[f.properties.LSOA11CD];
            if (flow) allFeatures.push({ ...f, properties: { ...f.properties, code: f.properties.LSOA11CD, name: flow.name, percentage: flow.percentage, count: flow.count, type: 'lsoa' } });
          });
        }
      }
      if (msoaCodes) {
        const msoaRes = await fetch(MSOA_URL + "?where=" + encodeURIComponent(`MSOA11CD IN (${msoaCodes})`) + "&outFields=MSOA11CD&outSR=4326&f=geojson&resultRecordCount=500");
        const msoaData = await msoaRes.json();
        if (msoaData.features) {
          msoaData.features.forEach(f => {
            const flow = flowMap[f.properties.MSOA11CD];
            if (flow) allFeatures.push({ ...f, properties: { ...f.properties, code: f.properties.MSOA11CD, name: flow.name, percentage: flow.percentage, count: flow.count, type: 'msoa' } });
          });
        }
      }
      if (allFeatures.length > 0) setDestLayer({ type: 'FeatureCollection', features: allFeatures });
    } catch (err) {
      console.error("Dest boundary error:", err);
    }
    setDestLoading(false);
    fetchDestCentroids(flows);
  }

  // Fetch population-weighted centroids for the destinations so they can be
  // shown/edited on the map. Uses the same PWC ArcGIS services as the backend.
  async function fetchDestCentroids(flows) {
    if (!flows || flows.length === 0) return;
    const lsoaCodes = flows.filter(f => f.type === 'lsoa').map(f => `'${f.destination}'`).join(',');
    const msoaCodes = flows.filter(f => f.type === 'msoa').map(f => `'${f.destination}'`).join(',');
    const next = {};
    try {
      if (lsoaCodes) {
        // The LSOA PWC service exposes the code as lowercase `lsoa11cd` (unlike
        // the boundary service's uppercase LSOA11CD). Query + read it lowercase,
        // reading both cases defensively so a service change can't silently drop
        // every LSOA centroid again.
        const r = await fetch(LSOA_PWC_URL + "?where=" + encodeURIComponent(`lsoa11cd IN (${lsoaCodes})`) + "&outFields=lsoa11cd&returnGeometry=true&outSR=4326&f=json&resultRecordCount=500");
        const d = await r.json();
        (d.features || []).forEach(feat => {
          const code = feat.attributes?.lsoa11cd || feat.attributes?.LSOA11CD;
          const g = feat.geometry;
          if (code && g && g.x != null && g.y != null) next[code] = { lat: g.y, lng: g.x, edited: false };
        });
      }
      if (msoaCodes) {
        const r = await fetch(MSOA_PWC_URL + "?where=" + encodeURIComponent(`msoa11cd IN (${msoaCodes})`) + "&outFields=msoa11cd&returnGeometry=true&outSR=4326&f=json&resultRecordCount=500");
        const d = await r.json();
        (d.features || []).forEach(feat => {
          const code = feat.attributes?.msoa11cd || feat.attributes?.MSOA11CD;
          const g = feat.geometry;
          if (code && g && g.x != null && g.y != null) next[code] = { lat: g.y, lng: g.x, edited: false };
        });
      }
      if (Object.keys(next).length > 0) setDestCentroids(next);
    } catch (err) {
      console.error("Centroid fetch error:", err);
    }
  }

  async function fetchRoadNetwork() {
    if (!pinLocation || studyPolygon.length < 3) return;
    setRoadLoading(true); setRoadError(null); setRoadNetwork(null); setAssignedNetwork(null);
    setSiteAccessNode(null);
    try {
      const polygonParam = encodeURIComponent(JSON.stringify(studyPolygon));
      const res = await authedFetch(`${BACKEND_URL}/api/road-network?pin_lat=${pinLocation[0]}&pin_lng=${pinLocation[1]}&polygon=${polygonParam}`);
      const data = await res.json();
      console.log('Road network response:', data.edge_count, data.error);
      if (data.edges) setRoadNetwork(data);
      else {
        console.error('Road network missing edges:', data);
        setRoadError(data.error
          ? `Couldn't build the road network: ${data.error}`
          : 'No roads found for this study area. Try a larger or slightly different area.');
      }
    } catch (err) {
      console.error("Road network fetch error:", err);
      setRoadError("Couldn't reach the server to load the road network. Check your connection and try again.");
    }
    setRoadLoading(false);
  }

  // Local authority from a census zone name ("Westminster 018" ->
  // "Westminster", "Camden 001A" -> "Camden").
  function ladFromName(name) {
    return (name || '').trim().replace(/\s+\S+$/, '').trim() || 'Other';
  }
  function ladOf(flow) { return ladFromName(flow.name || flow.destination || ''); }

  // The effective mode-split override for a flow: a zone-level override wins,
  // else the local-authority override, else none (use the global split).
  function resolveOverride(flow) {
    return zoneOverrides[flow.destination] || modeOverrides[ladOf(flow)] || null;
  }

  // Car trips assigned to roads, both on the standard global mode share and
  // after per-area/zone overrides, so the UI can show the adjustment clearly.
  function getCarTripAdjustment() {
    const modes = modeShareMethod === 'manual' ? manualModes : modeShare?.modes;
    const globalCar = (parseFloat(modes?.find(m => m.code === 7)?.percentage) || 0) / 100;
    const periodAmArr = assignPeriod !== 'pm' ? Math.round(amArr * size) : 0;
    const periodAmDep = assignPeriod !== 'pm' ? Math.round(amDep * size) : 0;
    const periodPmArr = assignPeriod !== 'am' ? Math.round(pmArr * size) : 0;
    const periodPmDep = assignPeriod !== 'am' ? Math.round(pmDep * size) : 0;
    const persTrips = periodAmArr + periodPmArr + periodAmDep + periodPmDep;
    const flows = odFlows?.flows || [];
    const sumPct = flows.reduce((s, f) => s + (f.percentage || 0), 0) || 1;
    let effCar = 0;
    for (const f of flows) {
      const pnorm = (f.percentage || 0) / sumPct;
      const ov = resolveOverride(f);
      effCar += pnorm * (ov ? (ov.car || 0) / 100 : globalCar);
    }
    const standard = Math.round(persTrips * globalCar);
    const adjusted = Math.round(persTrips * effCar);
    return { standard, adjusted, reassigned: standard - adjusted, hasOverrides: standard !== adjusted };
  }

  // The global mode split reduced to the main modes the per-area override
  // editor exposes, as whole-number percentages summing to 100. Rail = metro/
  // tram (2) + train (3); Other absorbs the remaining census modes so the
  // split always totals 100.
  function globalMainSplit() {
    const modes = (modeShareMethod === 'manual' ? manualModes : modeShare?.modes) || [];
    const pct = (code) => { const m = modes.find(x => x.code === code); return m ? Math.round(parseFloat(m.percentage) || 0) : 0; };
    const car = pct(7), rail = pct(2) + pct(3), bus = pct(4), cycle = pct(9), walk = pct(10);
    const other = Math.max(0, 100 - (car + rail + bus + cycle + walk));
    return { car, rail, bus, cycle, walk, other };
  }

  async function assignTrips() {
    const effectiveModeShare = modeShareMethod === 'manual'
      ? { modes: manualModes.map(m => ({ ...m, percentage: parseFloat(m.percentage) || 0 })) }
      : modeShare;
    if (!pinLocation || !odFlows || !effectiveModeShare) return;
    setAssignLoading(true); setAssignedNetwork(null); setEducationSchools(null);
    const carMode = effectiveModeShare.modes.find(m => m.code === 7);
    const carPct = carMode ? carMode.percentage / 100 : 0;
    const periodAmArr = assignPeriod !== 'pm' ? Math.round(amArr * size) : 0;
    const periodAmDep = assignPeriod !== 'pm' ? Math.round(amDep * size) : 0;
    const periodPmArr = assignPeriod !== 'am' ? Math.round(pmArr * size) : 0;
    const periodPmDep = assignPeriod !== 'am' ? Math.round(pmDep * size) : 0;
    const totalArr = periodAmArr + periodPmArr;
    const totalDep = periodAmDep + periodPmDep;
    if (totalArr + totalDep === 0) { setAssignLoading(false); return; }

    // ── Trip purpose: carve Education out of the census assignment ──────────
    // Education trips route to schools (via the backend gravity model) at the
    // education car share; Employment + Other keep the census distribution and
    // the census/manual mode share, so they are simply the non-education pool.
    // Per-period education fraction (AM = period[0], PM = period[1]).
    const eduFracAm = educationFraction(purposeSplitForPeriod(timePeriods[0], amPeakTime));
    const eduFracPm = educationFraction(purposeSplitForPeriod(timePeriods[1], pmPeakTime));
    const eduArr = Math.round(periodAmArr * eduFracAm + periodPmArr * eduFracPm);
    const eduDep = Math.round(periodAmDep * eduFracAm + periodPmDep * eduFracPm);
    const arrPersonTrips = Math.max(0, totalArr - eduArr);   // non-education (census)
    const depPersonTrips = Math.max(0, totalDep - eduDep);
    const eduGrav = EDUCATION_GRAVITY_DEFAULTS;
    const eduExcludeParam = excludedSchools.length ? `&edu_exclude=${excludedSchools.join(',')}` : '';
    const eduParams = (eduArr + eduDep) > 0
      ? `&edu_arr_trips=${eduArr}&edu_dep_trips=${eduDep}&edu_car_pct=${EDUCATION_CAR_PCT / 100}`
        + `&edu_primary_share=${eduGrav.primaryShare / 100}&edu_beta_primary=${eduGrav.betaPrimary}`
        + `&edu_beta_secondary=${eduGrav.betaSecondary}&edu_radius_km=${eduGrav.radiusKm}${eduExcludeParam}`
      : '';
    const flowsStr = odFlows.flows.map(f => {
      const base = `${f.destination}:${f.count}:${odFlows.total_trips}`;
      const ov = resolveOverride(f);
      // Append the per-destination car fraction only where a zone or area
      // override exists; otherwise the backend uses the global car share.
      return ov ? `${base}:${((ov.car || 0) / 100).toFixed(4)}` : base;
    }).join(',');
    const accessParams = siteAccessNode ? `&access_lat=${siteAccessNode.lat}&access_lng=${siteAccessNode.lng}` : '';
    const polygonParam = encodeURIComponent(JSON.stringify(studyPolygon));
    // Pass the (possibly user-adjusted) centroids so routing uses them instead
    // of the backend PWC lookup; falls back to PWC for any not supplied.
    const centroidsStr = Object.entries(destCentroids).map(([code, c]) => `${code}:${c.lat.toFixed(6)}:${c.lng.toFixed(6)}`).join(',');
    const centroidsParam = centroidsStr ? `&centroids=${encodeURIComponent(centroidsStr)}` : '';
    try {
      const res = await authedFetch(
        `${BACKEND_URL}/api/assign-trips?pin_lat=${pinLocation[0]}&pin_lng=${pinLocation[1]}&polygon=${polygonParam}&arr_person_trips=${arrPersonTrips}&dep_person_trips=${depPersonTrips}&car_pct=${carPct}&flows=${encodeURIComponent(flowsStr)}${centroidsParam}${accessParams}&am_peak=${amPeakTime}&pm_peak=${pmPeakTime}&assign_period=${assignPeriod}${eduParams}&project_id=${projectId || ''}`
      );
      const data = await res.json();
      if (data.edges) { setAssignedNetwork(data); setShowAggregated(false); }

      // Populate the education-schools map layer (named schools + their share of
      // education person trips) alongside the assignment. Cheap (no HERE calls).
      if ((eduArr + eduDep) > 0) {
        try {
          const eduRes = await authedFetch(
            `${BACKEND_URL}/api/education-flows?site_lat=${pinLocation[0]}&site_lng=${pinLocation[1]}`
            + `&edu_trips=${eduArr + eduDep}&primary_share=${eduGrav.primaryShare / 100}`
            + `&beta_primary=${eduGrav.betaPrimary}&beta_secondary=${eduGrav.betaSecondary}`
            + `&radius_km=${eduGrav.radiusKm}&max_schools=100${excludeParam}`
          );
          const eduData = await eduRes.json();
          if (eduData.schools) setEducationSchools(eduData);
        } catch (e) { console.error('Education flows error:', e); }
      } else {
        setEducationSchools(null);
      }
    } catch (err) {
      console.error("Assignment error:", err);
    }
    setAssignLoading(false);
  }

  // Re-run the education gravity for the map layer + schools list with a given
  // exclude set, so excluding/including a school updates the display immediately.
  // The actual assignment picks up the exclusions on its next run.
  async function refreshEducationSchools(exclArr) {
    if (!pinLocation || !educationSchools) return;
    const g = EDUCATION_GRAVITY_DEFAULTS;
    const trips = educationSchools.edu_trips || 0;
    const excl = exclArr.length ? `&exclude=${exclArr.join(',')}` : '';
    try {
      const res = await authedFetch(
        `${BACKEND_URL}/api/education-flows?site_lat=${pinLocation[0]}&site_lng=${pinLocation[1]}`
        + `&edu_trips=${trips}&primary_share=${g.primaryShare / 100}&beta_primary=${g.betaPrimary}`
        + `&beta_secondary=${g.betaSecondary}&radius_km=${g.radiusKm}&max_schools=100${excl}`
      );
      const data = await res.json();
      if (data.schools) setEducationSchools(data);
    } catch (e) { console.error('Education refresh error:', e); }
  }

  // Exclude a school as a destination (its share redistributes) or re-include it.
  function toggleSchoolExclusion(urn) {
    setExcludedSchools(prev => {
      const next = prev.includes(urn) ? prev.filter(u => u !== urn) : [...prev, urn];
      refreshEducationSchools(next);
      return next;
    });
  }

  // Manual route editing: replace one assigned route's geometry and/or trip count,
  // flag it as edited, and recompute the headline totals from all routes. `patch`
  // may carry { coords } (new [[lng,lat],…]) and/or { trips }.
  function updateRouteFeature(destination, direction, patch) {
    setRouteEditVersion(v => v + 1);
    setAssignedNetwork(prev => {
      const feats = prev?.routes?.features;
      if (!feats) return prev;
      const idx = findRouteIndex(feats, destination, direction);
      if (idx < 0) return prev;
      const f = feats[idx];
      // Snapshot the original geometry + trips the first time a route is touched,
      // so "Restore to default" can bring it back.
      const original = f.properties._original || { coordinates: f.geometry.coordinates, trips: f.properties.trips };
      const nextFeat = {
        ...f,
        properties: {
          ...f.properties,
          ...(patch.trips != null ? { trips: Math.max(0, Math.round(patch.trips)) } : {}),
          ...(patch.note != null ? { note: patch.note } : {}),
          _original: original,
          _edited: true,
        },
        geometry: patch.coords
          ? { ...f.geometry, coordinates: patch.coords }
          : f.geometry,
      };
      const nextFeatures = feats.map((x, i) => (i === idx ? nextFeat : x));
      return {
        ...prev,
        routes: { ...prev.routes, features: nextFeatures },
        ...recomputeAssignedTotals(nextFeatures),
      };
    });
  }

  // Revert one route to its originally-assigned path + trip count, clearing the
  // edited flag, note and snapshot.
  function restoreRouteFeature(destination, direction) {
    setRouteEditVersion(v => v + 1);
    setAssignedNetwork(prev => {
      const feats = prev?.routes?.features;
      if (!feats) return prev;
      const idx = findRouteIndex(feats, destination, direction);
      if (idx < 0) return prev;
      const f = feats[idx];
      const orig = f.properties._original;
      if (!orig) return prev; // nothing to restore
      const { _original, _edited, note, ...keepProps } = f.properties;
      const nextFeat = {
        ...f,
        properties: { ...keepProps, trips: orig.trips },
        geometry: { ...f.geometry, coordinates: orig.coordinates },
      };
      const nextFeatures = feats.map((x, i) => (i === idx ? nextFeat : x));
      return {
        ...prev,
        routes: { ...prev.routes, features: nextFeatures },
        ...recomputeAssignedTotals(nextFeatures),
      };
    });
  }

  function resetGateDrawingState() {
    setPendingJunctionNodeId(null);
    setPendingGates([]);
    setGateDrawStart(null);
    setGateDraftPoint(null);
    setPendingGateConfirm(null);
    schematicJunctionMapCallback.current = null;
  }

  function handleGateSecondClick(lat, lng) {
    if (!gateDrawStart) return;
    const p1 = [gateDrawStart.lng, gateDrawStart.lat];
    const p2 = [lng, lat];
    const routes = assignedNetwork?.routes?.features || [];
    const suggestedDirection = suggestGateDirectionFromRoutes(routes, p1, p2);
    const usedLetters = new Set(pendingGates.map(g => g.letter));
    let suggestedLetter = 'A';
    for (let i = 0; i < 26; i++) {
      const letter = String.fromCharCode(65 + i);
      if (!usedLetters.has(letter)) { suggestedLetter = letter; break; }
    }
    // Auto-detect the road name from the nearest OSM road edge already loaded in
    // the network (same source as the rest of the app) — editable in the popup.
    const midLng = (p1[0] + p2[0]) / 2, midLat = (p1[1] + p2[1]) / 2;
    const roadName = nearestEdgeName(midLng, midLat, assignedNetwork?.edges || roadNetwork?.edges);
    setPendingGateConfirm({ p1, p2, letter: suggestedLetter, direction: suggestedDirection, roadName });
    setGateDrawStart(null);
    setGateDraftPoint(null);
  }

  function confirmPendingGate() {
    if (!pendingGateConfirm) return;
    setPendingGates(prev => [...prev, {
      id: `gate-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      letter: pendingGateConfirm.letter || 'A',
      p1: pendingGateConfirm.p1,
      p2: pendingGateConfirm.p2,
      direction: pendingGateConfirm.direction,
      roadName: (pendingGateConfirm.roadName || '').trim() || null,
    }]);
    setPendingGateConfirm(null);
  }

  // Confirm the pending gate with Enter (Escape = redraw) so the planner isn't
  // forced to click OK for every gate.
  React.useEffect(() => {
    if (!pendingGateConfirm) return;
    const onKey = e => {
      if (e.key === 'Enter') { e.preventDefault(); confirmPendingGate(); }
      else if (e.key === 'Escape') { e.preventDefault(); redrawPendingGate(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingGateConfirm]);

  function redrawPendingGate() {
    setPendingGateConfirm(null);
  }

  function finishGateDrawing() {
    if (schematicJunctionMapCallback.current && pendingGates.length > 0) {
      schematicJunctionMapCallback.current({ gates: pendingGates });
    }
    resetGateDrawingState();
    setShowEditor(true);
  }

  function cancelGateDrawing() {
    resetGateDrawingState();
    setShowEditor(true);
  }

  const maxPercentage = odFlows ? Math.max(...odFlows.flows.map(f => f.percentage)) : 1;

  function styleDestZone(feature) {
    const pct = feature.properties.percentage || 0;
    // Highlight the zone(s) whose mode split is being edited, so the user can
    // see on the map which area/zone the override applies to.
    const isHl = (modeOverrideEditingZone && feature.properties.code === modeOverrideEditingZone)
      || (modeOverrideEditingLad && ladFromName(feature.properties.name) === modeOverrideEditingLad);
    if (isHl) return { fillColor: '#0D9488', fillOpacity: 0.55, color: '#0D9488', weight: 3 };
    return { fillColor: percentageToColour(pct, maxPercentage), fillOpacity: 0.7, color: '#3D1A6E', weight: feature.properties.type === 'lsoa' ? 1 : 1.5 };
  }

  function onEachDestZone(feature, layer) {
    const { percentage: pct, count, code, name, type } = feature.properties;
    const devTrips = totalPersonTrips ? Math.round(pct / 100 * totalPersonTrips) : null;
    layer.bindTooltip(
      `<div style="font-family:Arial;font-size:12px;padding:4px 8px;">
        <strong style="color:#3D1A6E">${name}</strong><br/>
        <span style="color:#6B6880;font-size:11px">${code} · ${type.toUpperCase()}</span><br/>
        ${pct}% of trips (${count} census)<br/>
        ${devTrips !== null ? `<span style="color:#0D9488">${devTrips} dev trips</span>` : ''}
      </div>`, { sticky: true }
    );
  }

  function styleRoad(feature) {
    return { color: roadColour(feature.properties.highway), weight: roadWeight(feature.properties.highway), opacity: 0.8 };
  }

  function onEachRoad(feature, layer) {
    const name = feature.properties.name || 'Unnamed road';
    const hw = feature.properties.highway || '';
    const speed = feature.properties.maxspeed || 'unknown';
    const length = feature.properties.length ? `${Math.round(feature.properties.length)}m` : '';
    layer.bindTooltip(`<div style="font-family:Arial;font-size:12px;padding:4px 8px;"><strong style="color:#3D1A6E">${name}</strong><br/>${hw} · ${speed} · ${length}</div>`, { sticky: true });
  }

  function styleRoute(feature) {
    const trips = feature.properties.trips || 0;
    const direction = feature.properties.direction || 'departures';
    const maxTrips = assignedNetwork?.routes?.features
      ? Math.max(...assignedNetwork.routes.features.map(f => f.properties.trips || 0), 1) : 1;
    const intensity = Math.min(trips / maxTrips, 1);
    const weight = 2 + intensity * 6;
    return direction === 'arrivals'
      ? { color: '#6B21A8', weight, opacity: 0.85 }
      : { color: '#0D9488', weight, opacity: 0.85 };
  }

  function styleAggregatedEdge(feature) {
    const trips = feature.properties.trips || 0;
    const maxTrips = assignedNetwork?.aggregated?.features
      ? Math.max(...assignedNetwork.aggregated.features.map(f => f.properties.trips || 0), 1) : 1;
    if (trips === 0) return { color: '#E0DCF0', weight: 1, opacity: 0.5 };
    const intensity = Math.min(trips / maxTrips, 1);
    const r = Math.round(61 + (232 - 61) * intensity);
    const g = Math.round(26 * (1 - intensity));
    const b = Math.round(110 * (1 - intensity));
    return { color: `rgb(${r},${g},${b})`, weight: 2 + intensity * 7, opacity: 0.95 };
  }

  React.useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.eachLayer(l => { if (l._odinLabel) map.removeLayer(l); });
    if (!showAggregated || !assignedNetwork?.aggregated?.features || !layerVisibility.roadLabels) return;
    if (Object.keys(roadLabelPositions).length === 0) return;

    // Build trip totals per road name for the label value
    const roadTrips = {};
    assignedNetwork.aggregated.features.forEach(f => {
      const name = f.properties.name;
      const trips = f.properties.trips || 0;
      if (!name || name === 'Unnamed road') return;
      if (!roadTrips[name] || trips > roadTrips[name]) roadTrips[name] = trips;
    });

    // Only render labels that have been manually placed
    Object.entries(roadLabelPositions).forEach(([key, pos]) => {
      // Support both old format (name only) and new format (with name+trips embedded)
      const name = pos.name || key.split('||')[0];
      const trips = pos.trips || roadTrips[name];
      if (!trips || !name) return;
      const label = L.tooltip({
        permanent: true, direction: 'center', className: 'odin-road-label',
      })
        .setContent(`<div style="font-family:Arial;font-size:10px;font-weight:700;color:#1C1C2E;white-space:nowrap;pointer-events:none;">${name}${trips ? ` <span style="color:#3D1A6E;">· ${trips}</span>` : ''}</div>`)
        .setLatLng([pos.lat, pos.lng]);
      label._odinLabel = true;
      label.addTo(map);
    });
  }, [showAggregated, assignedNetwork, layerVisibility.roadLabels, roadLabelPositions]);

  function onEachAggregatedEdge(feature, layer) {
    // Labels handled by useEffect above
  }

  

  function onEachRoute(feature, layer) {
    const trips = feature.properties.trips || 0;
    const dest = feature.properties.destination || '';
    const flow = odFlows?.flows?.find(f => f.destination === dest);
    const name = flow?.name || dest;
    const type = flow?.type?.toUpperCase() || '';
    const pct = flow?.percentage || 0;
    layer.bindTooltip(
      `<div style="font-family:Arial;font-size:12px;padding:4px 8px;">
        <strong style="color:#3D1A6E">${name}</strong><br/>
        <span style="color:#6B6880;font-size:11px">${dest} · ${type}</span><br/>
        <span style="color:${feature.properties.direction === 'arrivals' ? '#6B21A8' : '#0D9488'};font-weight:700">${trips} ${feature.properties.direction || 'vehicle'} trips</span>
        <span style="color:#6B6880"> · ${pct}% of census trips</span>
      </div>`, { sticky: true }
    );
  }

  const onEachOA = useCallback((feature, layer) => {
    layer.on({
      click: () => handleOAClick(feature.properties.OA11CD),
      mouseover: (e) => { if (!selectedOAsRef.current.includes(feature.properties.OA11CD)) e.target.setStyle({ fillOpacity: 0.35, weight: 2 }); },
      mouseout: (e) => { if (!selectedOAsRef.current.includes(feature.properties.OA11CD)) e.target.setStyle({ fillOpacity: 0.1, weight: 1 }); }
    });
  }, []);

  function styleOA(feature) {
    const isSelected = selectedOAs.includes(feature.properties.OA11CD);
    return { fillColor: isSelected ? '#3D1A6E' : '#7B4DBF', fillOpacity: isSelected ? 0.55 : 0.1, color: '#3D1A6E', weight: isSelected ? 3 : 1 };
  }

  function clearAll() {
    setSelectedOAs([]); setPinLocation(null); setPinMode(false);
    setMultiMode(false); setOdFlows(null); setOdError(null);
    setDestLayer(null); setDevSize('');
    setAmArrivals(''); setAmDepartures('');
    setPmArrivals(''); setPmDepartures(''); setAssignPeriod('am');
    setModeShare(null); setOriginMSOA(null);
    setRoadNetwork(null); setAssignedNetwork(null); setEducationSchools(null);
    setSiteAccessNode(null); setAccessMode(false);
    setShowEditor(false);
    setSchematicNodes([]); setSchematicLinks([]); setSchematicSiteBox(null);
    setStudyPolygon([]); setDrawingPolygon(false); setPolygonPreviewPoint(null);
  }

  async function searchPlace(query) {
    if (!query || query.length < 3) { setSearchResults([]); return; }
    setSearchLoading(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&countrycodes=gb`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await res.json();
      setSearchResults(data);
    } catch (err) {
      console.error('Search error:', err);
    }
    setSearchLoading(false);
  }

  function handleSearchSelect(result) {
    const lat = parseFloat(result.lat);
    const lon = parseFloat(result.lon);
    const bbox = result.boundingbox;
    let zoom = 15;
    if (bbox) {
      const latSpan = Math.abs(parseFloat(bbox[1]) - parseFloat(bbox[0]));
      if (latSpan > 1) zoom = 9;
      else if (latSpan > 0.1) zoom = 12;
      else if (latSpan > 0.01) zoom = 14;
      else zoom = 16;
    }
    setFlyTarget({ center: [lat, lon], zoom });
    setSearchQuery(result.display_name.split(',')[0]);
    setSearchResults([]);
  }

  function getVehicleTripPreview() {
    const effectiveModes = modeShareMethod === 'manual' ? manualModes : modeShare?.modes;
    const carPct = (effectiveModes?.find(m => m.code === 7)?.percentage || 0) / 100;
    const periodAmArr = assignPeriod !== 'pm' ? Math.round(amArr * size) : 0;
    const periodAmDep = assignPeriod !== 'pm' ? Math.round(amDep * size) : 0;
    const periodPmArr = assignPeriod !== 'am' ? Math.round(pmArr * size) : 0;
    const periodPmDep = assignPeriod !== 'am' ? Math.round(pmDep * size) : 0;
    const arrV = Math.round((periodAmArr + periodPmArr) * carPct);
    const depV = Math.round((periodAmDep + periodPmDep) * carPct);
    return { arrV, depV };
  }

  async function handleChangeEmail() {
    if (!newEmail) return;
    setAccountLoading(true); setAccountMsg('');
    const { error } = await supabase.auth.updateUser({ email: newEmail });
    setAccountLoading(false);
    setAccountMsg(error ? `Error: ${error.message}` : '✅ Confirmation sent to your new email address. Click the link to confirm the change.');
    if (!error) setNewEmail('');
  }

  async function handleChangePassword() {
    if (!newPassword || newPassword !== confirmPassword) {
      setAccountMsg('Passwords do not match.'); return;
    }
    if (newPassword.length < 8) {
      setAccountMsg('Password must be at least 8 characters.'); return;
    }
    setAccountLoading(true); setAccountMsg('');
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setAccountLoading(false);
    setAccountMsg(error ? `Error: ${error.message}` : '✅ Password updated successfully.');
    if (!error) { setNewPassword(''); setConfirmPassword(''); }
  }

  async function handleDeleteAccount() {
    if (deleteConfirmText !== 'DELETE') return;
    setAccountLoading(true); setAccountMsg('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from('projects').delete().eq('userid', user.id);
      const { error } = await supabase.functions.invoke('delete-user');
      if (error) {
        setAccountMsg('Error deleting account. Please contact support at admin@odinflow.co.uk');
        setAccountLoading(false); return;
      }
      await supabase.auth.signOut();
    } catch (e) {
      setAccountMsg('Error deleting account. Please contact support at admin@odinflow.co.uk');
      setAccountLoading(false);
    }
  }

  async function handleBoundaryFileLoad(e) {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    try {
      let coords = null;

      if (ext === 'geojson' || ext === 'json') {
        const text = await file.text();
        const geojson = JSON.parse(text);
        const feature = geojson.type === 'FeatureCollection' ? geojson.features[0] : geojson;
        const geometry = feature.geometry || feature;
        if (geometry.type === 'Polygon') {
          coords = geometry.coordinates[0].map(([lng, lat]) => [lat, lng]);
        } else if (geometry.type === 'MultiPolygon') {
          coords = geometry.coordinates[0][0].map(([lng, lat]) => [lat, lng]);
        }
      } else if (ext === 'kml') {
        const text = await file.text();
        const parser = new DOMParser();
        const kmlDoc = parser.parseFromString(text, 'text/xml');
        const coordsEl = kmlDoc.querySelector('coordinates');
        if (coordsEl) {
          const raw = coordsEl.textContent.trim().split(/\s+/);
          coords = raw.map(c => {
            const parts = c.split(',');
            return [parseFloat(parts[1]), parseFloat(parts[0])];
          }).filter(c => !isNaN(c[0]) && !isNaN(c[1]));
        }
      } else if (ext === 'zip') {
        // Shapefile as zip
        const buffer = await file.arrayBuffer();
        const geojson = await shp(buffer);
        const feature = geojson.type === 'FeatureCollection' ? geojson.features[0] : geojson;
        const geometry = feature.geometry || feature;
        if (geometry.type === 'Polygon') {
          coords = geometry.coordinates[0].map(([lng, lat]) => [lat, lng]);
        } else if (geometry.type === 'MultiPolygon') {
          coords = geometry.coordinates[0][0].map(([lng, lat]) => [lat, lng]);
        }
      }

      if (coords && coords.length >= 3) {
        setStudyPolygon(coords);
        setDrawingPolygon(false);
        setRoadNetwork(null);
        setAssignedNetwork(null);
        // Fly to the boundary
        const lats = coords.map(c => c[0]);
        const lngs = coords.map(c => c[1]);
        const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
        const centerLng = (Math.min(...lngs) + Math.max(...lngs)) / 2;
        setFlyTarget({ center: [centerLat, centerLng], zoom: 14 });
      } else {
        alert('Could not read polygon from file. Please check the file format.');
      }
    } catch (err) {
      console.error('Boundary file error:', err);
      alert('Error reading file: ' + err.message);
    }
    // Reset input so same file can be re-loaded
    e.target.value = '';
  }

  async function runActiveTravelAnalysis() {
    const origin = siteAccessNode || (pinLocation ? { lat: pinLocation[0], lng: pinLocation[1] } : null);
    if (!origin) return;
    setAtLoading(true); setAtError(null); setAtResults(null);
    const minutesArray = Array.from(atSelectedMinutes).sort((a, b) => a - b);
    const maxMinutes = atCustomMinutes ? parseInt(atCustomMinutes) : Math.max(...minutesArray);
    const selectedTypes = Object.entries(atDestTypes).filter(([,v]) => v).map(([k]) => k).join(',');
    const radius_m = Math.round(maxMinutes * (atMode === 'cycling' ? 250 : 80));

    try {
      const modes = atMode === 'both' ? ['walking', 'cycling'] : [atMode];
      const isochroneResults = {};
      const destinationResults = {};
      const routeResults = {};

      for (const mode of modes) {
        // Fetch isochrone for each selected time threshold
        const allMinutes = atCustomMinutes ? [parseInt(atCustomMinutes)] : minutesArray;
        isochroneResults[mode] = {};
        for (const mins of allMinutes) {
          const isoRes = await authedFetch(
            `${BACKEND_URL}/api/isochrone?lat=${origin.lat}&lng=${origin.lng}&mode=${mode}&minutes=${mins}`
          );
          isochroneResults[mode][mins] = await isoRes.json();
        }
        // Use largest isochrone for destination filtering
        const largestIso = isochroneResults[mode][maxMinutes] || Object.values(isochroneResults[mode])[Object.values(isochroneResults[mode]).length - 1];

        // Fetch destinations using wider radius then filter by largest isochrone
        const destRes = await authedFetch(
          `${BACKEND_URL}/api/nearby-destinations?lat=${origin.lat}&lng=${origin.lng}&radius_m=${radius_m}&types=${selectedTypes}`
        );
        const destData = await destRes.json();
        const allDests = destData.destinations || [];

        // Filter destinations to those inside the largest isochrone polygon
        if (largestIso?.geometry && allDests.length > 0) {
          const filterRes = await authedFetch(`${BACKEND_URL}/api/filter-by-isochrone`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              isochrone: largestIso,
              destinations: allDests
            })
          });
          const filterData = await filterRes.json();
          destinationResults[mode] = filterData.destinations || allDests;
        } else {
          destinationResults[mode] = allDests;
        }

        // Build destinations string for routing
        if (destinationResults[mode].length > 0) {
          const destsStr = destinationResults[mode]
            .map(d => `${d.name},${d.type},${d.lat},${d.lng}`)
            .join('|');
          const routeRes = await authedFetch(
            `${BACKEND_URL}/api/active-travel-routes?origin_lat=${origin.lat}&origin_lng=${origin.lng}&destinations=${encodeURIComponent(destsStr)}&mode=${mode}&departure_time=08:00`
          );
          routeResults[mode] = await routeRes.json();
        } else {
          routeResults[mode] = { routes: { type: 'FeatureCollection', features: [] }, aggregated: { type: 'FeatureCollection', features: [] } };
        }
      }

      setAtResults({ isochroneResults, destinationResults, routeResults, minutes: maxMinutes, minutesArray: atCustomMinutes ? [parseInt(atCustomMinutes)] : minutesArray, mode: atMode, siteBoundary: siteBoundary.length >= 3 ? siteBoundary : null, studyPolygon: studyPolygon.length >= 3 ? studyPolygon : null, pinLocation });
    } catch (err) {
      setAtError('Analysis failed. Please try again.');
      console.error('Active travel error:', err);
    }
    setAtLoading(false);
  }

  if (loading) return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F6F1E9' }}>
      <div style={{ fontSize: 14, color: '#7B4DBF', fontWeight: 600 }}>Loading ODIN...</div>
    </div>
  );

  if (!session) return <Login />;
  if (view === 'dashboard') return <Dashboard onNewProject={handleNewProject} onLoadProject={handleLoadProject} />;

  if (showSetup) return (
    <ProjectSetup
      initialTimePeriods={timePeriods}
      initialScenarios={scenarios}
      onComplete={handleSetupComplete}
      onSkip={() => setShowSetup(false)}
      isEditing={timePeriods.length > 0}
    />
  );

  if (view === 'overview') return (
    <ProjectDashboard
      name={projectName}
      projectNumber={projectNumber}
      client={projectClient}
      preparedBy={projectPreparedBy}
      checkedBy={projectCheckedBy}
      scenarios={scenarios}
      timePeriods={timePeriods}
      devSize={devSize}
      pinLocation={pinLocation}
      selectedOAs={selectedOAs}
      modeShare={modeShare}
      odFlows={odFlows}
      onOpenMap={() => setView('map')}
      onOpenResults={() => setView('results')}
      onBackToProjects={() => setView('dashboard')}
      onEditDetails={() => { setView('map'); setShowProjectDetailsModal(true); }}
    />
  );

  if (view === 'results') return (
    <>
      <ResultsSummary
        projectName={projectName}
        scenarios={scenarios}
        timePeriods={timePeriods}
        devSize={devSize}
        modeShare={modeShare}
        manualModes={manualModes}
        modeShareMethod={modeShareMethod}
        odFlows={odFlows}
        educationSchools={educationSchools}
        assignedNetwork={assignedNetwork}
        junctionMatrices={junctionMatrices}
        assignPeriod={assignPeriod}
        existingFlows={existingFlows}
        onSetExisting={setExistingCell}
        onPasteExisting={pasteExistingMatrix}
        flowScenarios={flowScenarios}
        onChangeFlowScenarios={setFlowScenarios}
        onExportPDF={handleExportPDF}
        onExportExcel={handleExportExcel}
        exportGenerating={exportGenerating}
        onBackToOverview={() => setView('overview')}
        onOpenMap={() => setView('map')}
      />
      {renderExportModal()}
    </>
  );

  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column' }}>

      {/* ── Toolbar ── */}
      <div style={{ height: 52, minHeight: 52, background: 'white', borderBottom: '1.5px solid #EDE5F8', borderLeft: '4px solid #3D1A6E', display: 'flex', alignItems: 'center', padding: '0 12px', zIndex: 2000, boxShadow: '0 2px 8px rgba(61,26,110,0.08)', gap: 8 }}>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <svg width="18" height="22" viewBox="0 0 197 249" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M158.29,177.25c4.37,3.62,3.99,11.25,1.09,14.9-3.48,4.37-9.56,4.58-13.73,1.1l-48.6-40.52-.24,71.04c-.02,5.58-6.19,8.2-10.14,7.99-5.27-.27-9.86-3.75-9.89-9.84l-.29-69.51-47.82,41.21c-4.27,3.68-9.47,3.15-13.56-.41-2.97-2.58-4.95-10.22-.59-13.94l56.23-47.93L15.15,85.99c-4.5-3.67-3.42-10.67-.58-13.96,3.79-4.38,9.86-4.61,14.39-.94l47.62,38.59.09-91.63c0-4.41,1.79-8.05,5.23-9.8,2.94-1.5,7.95-1.51,10.98.9l29.47,23.46c5.92,4.72,10.1,11.62,4.75,18.78-3.06,4.09-9.99,3.9-14.06.58l-16.07-13.13v70.41s46.81-40.03,46.81-40.03l-12.93-12.02,31.27-6.11,22.14-4.2-17.67,53.75-8.72-17.32-55.58,47.57,56,46.38Z" fill="#3D1A6E"/>
          </svg>
          <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '0.12em', color: '#3D1A6E' }}>ODINFLOW</span>
        </div>

        <div style={{ width: 1, height: 24, background: '#EDE5F8', flexShrink: 0 }} />

        {/* Project name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, maxWidth: 160 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#1C1C2E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{projectName}</div>
          <button onClick={() => setShowProjectDetailsModal(true)} title="Edit project details"
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#C4BDD4', padding: '2px', lineHeight: 1, flexShrink: 0 }}>✏️</button>
        </div>

        <div style={{ width: 1, height: 24, background: '#EDE5F8', flexShrink: 0 }} />

        {/* Back to project dashboard (modes now live in the icon rail) */}
        <button onClick={() => setView('overview')} title="Project overview"
          style={{ padding: '6px 12px', background: '#F5F0FA', color: '#3D1A6E', border: '1.5px solid #EDE5F8', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0 }}>
          ← Overview
        </button>

        <div style={{ width: 1, height: 24, background: '#EDE5F8', flexShrink: 0 }} />



        {/* Census / network / assignment tools */}
        {['census', 'network', 'assignment'].includes(mainTab) && (
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            {scenarios.length > 1 && (
              <button onClick={() => setShowCompareModal(true)} style={{ padding: '6px 10px', background: '#F5F0FA', color: '#7B4DBF', border: '1.5px solid #EDE5F8', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>⚖️ Compare</button>
            )}
            <button onClick={() => setShowEditor(true)} style={{ padding: '6px 10px', background: '#F5F0FA', color: '#3D1A6E', border: '1.5px solid #EDE5F8', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>✏️ Schematic</button>
            {/* PDF/Excel export moved to the Results page */}
          </div>
        )}

        {/* Active travel tools */}
        {mainTab === 'activetravel' && atResults && (
          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
            <button onClick={() => exportActiveTravelJPEG({ atResults, mapboxToken: MAPBOX_TOKEN })}
              style={{ padding: '6px 10px', background: 'white', color: '#0D9488', border: '1.5px solid #0D9488', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>🖼️ JPEG</button>
            <button onClick={() => exportActiveTravelPDF({ projectName, projectNumber, projectClient, projectPreparedBy, projectCheckedBy, atResults, mapboxToken: MAPBOX_TOKEN })}
              style={{ padding: '6px 10px', background: '#0D9488', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>📄 PDF</button>
          </div>
        )}

        <div style={{ flex: 1 }} />

        {/* Search */}
        <div style={{ position: 'relative', width: 220, flexShrink: 0 }}>
          <input type="text" value={searchQuery} onChange={e => { setSearchQuery(e.target.value); searchPlace(e.target.value); }} placeholder="🔍 Search place..."
            style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1.5px solid #EDE5F8', fontSize: 11, fontFamily: 'inherit', color: '#1C1C2E', outline: 'none', boxSizing: 'border-box', background: '#F9F7FD' }} />
          {searchLoading && <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 10, color: '#7B4DBF' }}>...</div>}
          {searchResults.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', borderRadius: 8, boxShadow: '0 4px 16px rgba(61,26,110,0.15)', border: '1.5px solid #EDE5F8', marginTop: 4, overflow: 'hidden', zIndex: 3000 }}>
              {searchResults.map((result, i) => (
                <div key={i} onClick={() => handleSearchSelect(result)}
                  style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: i < searchResults.length - 1 ? '1px solid #F5F0FA' : 'none', fontSize: 12, color: '#1C1C2E' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F5F0FA'}
                  onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                  <div style={{ fontWeight: 600, color: '#3D1A6E' }}>{result.display_name.split(',')[0]}</div>
                  <div style={{ fontSize: 11, color: '#6B6880', marginTop: 1 }}>{result.display_name.split(',').slice(1, 3).join(',').trim()}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ width: 1, height: 24, background: '#EDE5F8', flexShrink: 0 }} />

        {saveMsg && <div style={{ fontSize: 11, color: '#0D9488', fontWeight: 600, whiteSpace: 'nowrap' }}>{saveMsg}</div>}
        <button onClick={saveProject} disabled={saving} style={{ padding: '6px 10px', background: saving ? '#F5F0FA' : '#0D9488', color: saving ? '#7B4DBF' : 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>{saving ? '...' : '💾'}</button>
        <button onClick={() => setShowSetup(true)} style={{ padding: '6px 10px', background: '#F5F0FA', color: '#3D1A6E', border: '1.5px solid #EDE5F8', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>⚙️</button>
        <button onClick={() => setView('dashboard')} style={{ padding: '6px 10px', background: 'white', color: '#3D1A6E', border: '1.5px solid #EDE5F8', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>← Projects</button>
        <button onClick={() => setShowHelp(true)} title="Help & methodology" style={{ width: 30, height: 30, borderRadius: '50%', background: '#F5F0FA', border: '1.5px solid #EDE5F8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0, fontWeight: 800, color: '#3D1A6E' }}>?</button>
        <button onClick={() => { setShowAccountModal(true); setAccountTab('details'); setAccountMsg(''); }} title="Account" style={{ width: 30, height: 30, borderRadius: '50%', background: '#F5F0FA', border: '1.5px solid #EDE5F8', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>👤</button>
      </div>

      {/* ── Setup summary bar ── */}
      {(timePeriods.length > 0 || scenarios.length > 0 || devSize) && (() => {
        // Calculate summary figures from active scenario if available
        const activeSc = scenarios.find(sc => sc.id === activeScenarioId) || scenarios[0];
        const scDevSize = activeSc ? parseFloat(activeSc.devSize) || parseFloat(devSize) || 0 : parseFloat(devSize) || 0;
        const summaryPeriods = timePeriods.length > 0 ? timePeriods : [
          { id: 'am', name: 'AM Peak', startTime: amPeakTime },
          { id: 'pm', name: 'PM Peak', startTime: pmPeakTime },
        ];
        return (
          <div style={{ background: '#F5F0FA', borderBottom: '1px solid #EDE5F8', padding: '5px 16px', display: 'flex', alignItems: 'center', gap: 12, fontSize: 11, flexWrap: 'wrap' }}>

            {/* Scenario selector dropdown */}
            {scenarios.length > 0 && (
              <div style={{ position: 'relative' }}>
                <select
                  value={activeScenarioId || ''}
                  onChange={e => setActiveScenarioId(e.target.value)}
                  style={{ padding: '3px 24px 3px 8px', borderRadius: 4, border: '1.5px solid #3D1A6E', background: '#3D1A6E', color: 'white', fontWeight: 700, fontSize: 10, fontFamily: 'inherit', cursor: 'pointer', appearance: 'none', outline: 'none' }}>
                  {scenarios.map(sc => (
                    <option key={sc.id} value={sc.id}>{sc.name}</option>
                  ))}
                </select>
                <div style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'white', fontSize: 8 }}>▼</div>
              </div>
            )}

            {/* Divider */}
            {scenarios.length > 0 && <div style={{ width: 1, height: 16, background: '#C4BDD4' }} />}

            {/* Time periods */}
            {summaryPeriods.length > 0 && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span style={{ color: '#6B6880' }}>Periods:</span>
                {summaryPeriods.map(tp => (
                  <span key={tp.id} style={{ color: '#3D1A6E', fontWeight: 700 }}>
                    {tp.name} <span style={{ fontWeight: 400, color: '#C4BDD4' }}>({tp.startTime})</span>
                  </span>
                ))}
              </div>
            )}

            {/* Dev size */}
            {scDevSize > 0 && (
              <div style={{ color: '#6B6880' }}>
                Dev size: <strong style={{ color: '#3D1A6E' }}>{scDevSize} units</strong>
              </div>
            )}

            {/* Trip totals per period from active scenario */}
            {activeSc && timePeriods.map(tp => {
              const t = periodTotals(activeSc, tp, scenarios, devSize);
              if (!t.total) return null;
              const sp = normaliseSplit(purposeSplitForPeriod(tp));
              const emp = Math.round(t.total * sp.employment / 100);
              const edu = Math.round(t.total * sp.education / 100);
              const oth = t.total - emp - edu;
              return (
                <div key={tp.id} style={{ color: '#6B6880' }}>
                  {tp.name}: <strong style={{ color: '#3D1A6E' }}>{t.total} trips</strong>
                  <span style={{ color: '#C4BDD4' }}> ({t.arr} arr + {t.dep} dep)</span>
                  <span style={{ color: '#C4BDD4' }}> · </span>
                  <span title="Employment / Education / Other (NTS purpose split)">
                    <span style={{ color: '#3D1A6E', fontWeight: 700 }}>{emp}</span>
                    <span style={{ color: '#C4BDD4' }}> emp · </span>
                    <span style={{ color: '#0D9488', fontWeight: 700 }}>{edu}</span>
                    <span style={{ color: '#C4BDD4' }}> edu · </span>
                    <span style={{ color: '#7B4DBF', fontWeight: 700 }}>{oth}</span>
                    <span style={{ color: '#C4BDD4' }}> other</span>
                  </span>
                </div>
              );
            })}

            {/* Fallback to main state if no setup scenarios */}
            {scenarios.length === 0 && amTotal !== null && (
              <div style={{ color: '#6B6880' }}>AM: <strong style={{ color: '#3D1A6E' }}>{amTotal} trips</strong> <span style={{ color: '#C4BDD4' }}>({Math.round(amArr * size)} arr + {Math.round(amDep * size)} dep)</span></div>
            )}
            {scenarios.length === 0 && pmTotal !== null && (
              <div style={{ color: '#6B6880' }}>PM: <strong style={{ color: '#3D1A6E' }}>{pmTotal} trips</strong> <span style={{ color: '#C4BDD4' }}>({Math.round(pmArr * size)} arr + {Math.round(pmDep * size)} dep)</span></div>
            )}
          </div>
        );
      })()}

      {/* ── Main area ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── Mode rail ── */}
        <div style={{ width: 64, minWidth: 64, background: '#3D1A6E', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 10, gap: 6, zIndex: 150 }}>
          {[
            { key: 'site', icon: '📍', label: 'Site', enabled: true },
            { key: 'distribution', icon: '🗺️', label: 'Distrib', enabled: true, step: 3 },
            { key: 'network', icon: '🛣️', label: 'Network', enabled: true, step: 4 },
            { key: 'assignment', icon: '🚦', label: 'Assign', enabled: true, step: 5 },
            { key: 'activetravel', icon: '🚶', label: 'Active', enabled: !!pinLocation },
            { key: 'transit', icon: '🚆', label: 'Transit', enabled: !!pinLocation },
          ].map(m => (
            <button key={m.key} onClick={() => { if (m.enabled) { setMainTab(m.key); if (m.step) setOpenStep(m.step); } }} disabled={!m.enabled}
              title={m.enabled ? m.label : 'Set a site pin first'}
              style={{ width: 52, padding: '8px 0', borderRadius: 9, border: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: m.enabled ? 'pointer' : 'not-allowed', background: mainTab === m.key ? 'rgba(255,255,255,0.16)' : 'transparent', color: mainTab === m.key ? 'white' : m.enabled ? '#C9B8E8' : '#6B5A8F', fontFamily: 'inherit', opacity: m.enabled ? 1 : 0.55 }}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>{m.icon}</span>
              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.02em' }}>{m.label}</span>
            </button>
          ))}
        </div>

        {/* ── Left dock zone — holds the active tool when it's docked left, or a
            prompt when the active tool is detached (see ToolSlot) ── */}
        {(() => { const ad = dockOf(mainTab); const showLeft = ad === 'left' || ad === 'float' || ad === 'popout'; const W = mainTab === 'activetravel' ? 300 : 280; return (
        <div style={{ width: showLeft ? W : 0, minWidth: showLeft ? W : 0, height: '100%', background: 'white', borderRight: showLeft ? '1.5px solid #EDE5F8' : 'none', overflowY: 'auto', display: 'flex', flexDirection: 'column', zIndex: 100, order: 0 }}>
          <div style={{ padding: showLeft ? '12px 14px' : 0, flex: 1 }}>
            {(ad === 'float' || ad === 'popout') && (
              <div style={{ background: '#F5F0FA', border: '1px solid #EDE5F8', borderRadius: 8, padding: '10px 12px', fontSize: 11, color: '#6B6880' }}>
                This tool is {ad === 'popout' ? 'in a separate window' : 'floating'}.{' '}
                <button onClick={() => setToolDock(mainTab, 'left')} style={{ background: 'none', border: 'none', color: '#3D1A6E', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', fontFamily: 'inherit', fontSize: 11 }}>Dock it here</button>
              </div>
            )}

          {/* ── ACTIVE TRAVEL TAB ── */}
          <ToolSlot toolKey="activetravel" label="Active Travel" active={mainTab === 'activetravel'} dock={dockOf('activetravel')} onDock={setToolDock} floatPos={toolFloats.activetravel} setFloatPos={setToolFloatAt} rightEl={rightZoneEl} idx={0} render={() => <>
            {/* Origin info */}
            <div style={{ background: '#F0FDF9', border: '1px solid #0D9488', borderRadius: 6, padding: '8px 10px', marginBottom: 14, fontSize: 11, color: '#0D9488' }}>
              {siteAccessNode
                ? `✅ Origin: Site access point (${siteAccessNode.lat.toFixed(4)}, ${siteAccessNode.lng.toFixed(4)})`
                : `📍 Origin: Site pin — set a site access point for more accuracy`}
            </div>

            {/* Mode selector */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 6 }}>Travel Mode</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {[{ key: 'walking', label: '🚶 Walk' }, { key: 'cycling', label: '🚴 Cycle' }, { key: 'both', label: '🚶🚴 Both' }].map(m => (
                  <button key={m.key} onClick={() => setAtMode(m.key)}
                    style={{ flex: 1, padding: '7px 4px', background: atMode === m.key ? '#0D9488' : 'white', color: atMode === m.key ? 'white' : '#0D9488', border: `1.5px solid ${atMode === m.key ? '#0D9488' : '#EDE5F8'}`, borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Time threshold */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 6 }}>Time Thresholds <span style={{ fontWeight: 400, color: '#C4BDD4' }}>(select multiple)</span></div>
              <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                {[5, 10, 15, 20].map(m => {
                  const isOn = atSelectedMinutes.has(m);
                  return (
                    <button key={m} onClick={() => {
                      setAtCustomMinutes('');
                      setAtSelectedMinutes(prev => {
                        const next = new Set(prev);
                        if (next.has(m)) { if (next.size > 1) next.delete(m); }
                        else next.add(m);
                        return next;
                      });
                    }}
                      style={{ flex: 1, padding: '7px 4px', background: isOn && !atCustomMinutes ? '#0D9488' : 'white', color: isOn && !atCustomMinutes ? 'white' : '#0D9488', border: `1.5px solid ${isOn && !atCustomMinutes ? '#0D9488' : '#EDE5F8'}`, borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                      {m}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="number" min="1" max="60" value={atCustomMinutes}
                  onChange={e => setAtCustomMinutes(e.target.value)}
                  placeholder="Custom..."
                  style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: `1.5px solid ${atCustomMinutes ? '#0D9488' : '#EDE5F8'}`, fontSize: 12, fontFamily: 'inherit', outline: 'none' }} />
                <span style={{ fontSize: 11, color: '#6B6880' }}>minutes</span>
              </div>
              {atSelectedMinutes.size > 1 && !atCustomMinutes && (
                <div style={{ fontSize: 10, color: '#0D9488', marginTop: 4, background: '#F0FDF9', padding: '4px 8px', borderRadius: 4 }}>
                  {Array.from(atSelectedMinutes).sort((a,b) => a-b).join(', ')} min — overlaid largest to smallest
                </div>
              )}
            </div>

            {/* Destination types */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 6 }}>Destination Types</div>
              {[
                { key: 'school', label: '🏫 Schools' },
                { key: 'hospital', label: '🏥 Hospitals' },
                { key: 'gp', label: '⚕️ GP / Health centres' },
                { key: 'pharmacy', label: '💊 Pharmacies' },
                { key: 'supermarket', label: '🛒 Supermarkets' },
                { key: 'park', label: '🌳 Parks' },
                { key: 'leisure', label: '🏋️ Leisure centres' },
                { key: 'train_station', label: '🚉 Train stations' },
                { key: 'bus_stop', label: '🚌 Bus stations' },
              ].map(({ key, label }) => (
                <div key={key} onClick={() => setAtDestTypes(prev => ({ ...prev, [key]: !prev[key] }))}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderRadius: 6, cursor: 'pointer', marginBottom: 2, background: atDestTypes[key] ? '#F0FDF9' : 'transparent' }}
                  onMouseEnter={e => e.currentTarget.style.background = atDestTypes[key] ? '#F0FDF9' : '#F9F7FD'}
                  onMouseLeave={e => e.currentTarget.style.background = atDestTypes[key] ? '#F0FDF9' : 'transparent'}>
                  <div style={{ width: 16, height: 16, borderRadius: 3, border: `2px solid ${atDestTypes[key] ? '#0D9488' : '#C4BDD4'}`, background: atDestTypes[key] ? '#0D9488' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {atDestTypes[key] && <div style={{ color: 'white', fontSize: 10, lineHeight: 1, fontWeight: 700 }}>✓</div>}
                  </div>
                  <span style={{ fontSize: 12, color: '#1C1C2E' }}>{label}</span>
                </div>
              ))}
            </div>

            {/* Layer toggles */}
            {atResults && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 6 }}>Layers</div>
                {[
                  { key: 'isochrone', label: 'Isochrone' },
                  { key: 'destinations', label: 'Destinations' },
                  { key: 'routes', label: 'Routes' },
                ].map(({ key, label }) => (
                  <div key={key} onClick={() => setAtLayerVisibility(prev => ({ ...prev, [key]: !prev[key] }))}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 8px', borderRadius: 6, cursor: 'pointer', marginBottom: 2 }}>
                    <span style={{ fontSize: 12, color: '#1C1C2E' }}>{label}</span>
                    <div style={{ width: 32, height: 18, borderRadius: 9, background: atLayerVisibility[key] ? '#0D9488' : '#C4BDD4', position: 'relative', transition: 'background 0.2s' }}>
                      <div style={{ position: 'absolute', top: 2, left: atLayerVisibility[key] ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                    </div>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button onClick={() => setAtShowAggregated(false)}
                    style={{ flex: 1, padding: '5px', background: !atShowAggregated ? '#0D9488' : 'white', color: !atShowAggregated ? 'white' : '#0D9488', border: '1.5px solid #0D9488', borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Individual
                  </button>
                  <button onClick={() => setAtShowAggregated(true)}
                    style={{ flex: 1, padding: '5px', background: atShowAggregated ? '#0D9488' : 'white', color: atShowAggregated ? 'white' : '#0D9488', border: '1.5px solid #0D9488', borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Aggregated
                  </button>
                </div>
              </div>
            )}

            {atError && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '8px 10px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{atError}</div>}

            <button onClick={runActiveTravelAnalysis} disabled={atLoading}
              style={{ width: '100%', padding: '11px', background: atLoading ? '#EDE5F8' : '#0D9488', color: atLoading ? '#7B4DBF' : 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: atLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', marginBottom: 12 }}>
              {atLoading ? 'Running analysis...' : '▶ Run Analysis'}
            </button>

            {/* Results summary */}
            {atResults && !atLoading && (() => {
              const allDests = Object.values(atResults.destinationResults).flat();
              const uniqueDests = [...new Map(allDests.map(d => [`${d.lat},${d.lng}`, d])).values()];
              const byType = {};
              uniqueDests.forEach(d => { byType[d.type] = (byType[d.type] || 0) + 1; });
              return (
                <div style={{ background: '#F0FDF9', border: '1px solid #0D9488', borderRadius: 8, padding: '12px 14px', marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#0D9488', marginBottom: 8 }}>
                    ✅ {uniqueDests.length} destinations within {(atResults.minutesArray || [atResults.minutes]).join('/')} min {atResults.mode === 'both' ? 'walk/cycle' : atResults.mode}
                  </div>
                  {Object.entries(byType).map(([type, count]) => {
                    const labels = { school: '🏫 Schools', hospital: '🏥 Hospitals', gp: '⚕️ GP / Health', pharmacy: '💊 Pharmacies', supermarket: '🛒 Supermarkets', park: '🌳 Parks', leisure: '🏋️ Leisure', train_station: '🚉 Stations', bus_stop: '🚌 Bus stops' };
                    return (
                      <div key={type} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6B6880', marginBottom: 3 }}>
                        <span>{labels[type] || type}</span>
                        <span style={{ fontWeight: 700, color: '#0D9488' }}>{count}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Results table */}
            {atResults && !atLoading && (() => {
              const modes = atResults.mode === 'both' ? ['walking', 'cycling'] : [atResults.mode];
              const allDests = atResults.destinationResults[modes[0]] || [];
              if (allDests.length === 0) return null;
              return (
                <div style={{ marginTop: 4 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 8 }}>Destination Details</div>
                  {allDests.map((dest, i) => {
                    const walkRoute = atResults.routeResults['walking']?.routes?.features?.find(f =>
                      Math.abs(f.properties.dest_lat - dest.lat) < 0.0005 &&
                      Math.abs(f.properties.dest_lng - dest.lng) < 0.0005
                    );
                    const cycleRoute = atResults.routeResults['cycling']?.routes?.features?.find(f =>
                      Math.abs(f.properties.dest_lat - dest.lat) < 0.0005 &&
                      Math.abs(f.properties.dest_lng - dest.lng) < 0.0005
                    );
                    const typeLabels = { school: '🏫', hospital: '🏥', gp: '⚕️', pharmacy: '💊', supermarket: '🛒', park: '🌳', leisure: '🏋️', train_station: '🚉', bus_stop: '🚌' };
                    return (
                      <div key={i} style={{ padding: '8px 10px', background: '#F9F7FD', borderRadius: 6, marginBottom: 6, borderLeft: '3px solid #0D9488' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1C1C2E', marginBottom: 4 }}>
                          {typeLabels[dest.type] || '📍'} {dest.name}
                        </div>
                        <div style={{ fontSize: 10, color: '#6B6880', marginBottom: 4 }}>{dest.description}</div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {walkRoute && <div style={{ fontSize: 10, color: '#3D1A6E', background: '#F5F0FA', padding: '2px 6px', borderRadius: 4 }}>🚶 {walkRoute.properties.duration_min} min · {Math.round(walkRoute.properties.distance_m)}m</div>}
                          {cycleRoute && <div style={{ fontSize: 10, color: '#0D9488', background: '#F0FDF9', padding: '2px 6px', borderRadius: 4 }}>🚴 {cycleRoute.properties.duration_min} min · {Math.round(cycleRoute.properties.distance_m)}m</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </>} />

          {/* ── SITE TAB ── */}
          <ToolSlot toolKey="site" label="Site" active={mainTab === 'site'} dock={dockOf('site')} onDock={setToolDock} floatPos={toolFloats.site} setFloatPos={setToolFloatAt} rightEl={rightZoneEl} idx={1} render={() => <>

            {/* 1. Site Pin */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 8 }}>1. Site Pin</div>
              {!pinLocation ? (
                <button onClick={() => setPinMode(!pinMode)}
                  style={{ width: '100%', padding: '9px 12px', background: pinMode ? '#3D1A6E' : 'white', color: pinMode ? 'white' : '#3D1A6E', border: '1.5px solid #3D1A6E', borderRadius: 6, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit', marginBottom: 6 }}>
                  {pinMode ? '✕ Cancel — click map' : '📍 Drop Site Pin'}
                </button>
              ) : (
                <div style={{ background: '#F0FDF9', border: '1px solid #0D9488', borderRadius: 6, padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#0D9488' }}>✅ Pin placed</div>
                    <div style={{ fontSize: 10, color: '#6B6880', marginTop: 2 }}>{pinLocation[0].toFixed(5)}, {pinLocation[1].toFixed(5)}</div>
                    {selectedOAs.length > 0 && <div style={{ fontSize: 10, color: '#3D1A6E', fontWeight: 600, marginTop: 2 }}>OA: {selectedOAs[0]}</div>}
                  </div>
                  <button onClick={() => { setPinLocation(null); setSelectedOAs([]); setOdFlows(null); setDestLayer(null); setModeShare(null); setOriginMSOA(null); setRoadNetwork(null); setAssignedNetwork(null); setSiteAccessNode(null); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#C4BDD4', fontFamily: 'inherit', fontWeight: 600 }}>Remove</button>
                </div>
              )}
              {pinMode && <div style={{ fontSize: 11, color: '#3D1A6E', background: '#F5F0FA', padding: '6px 10px', borderRadius: 6, marginTop: 6 }}>Click anywhere on the map to place your site pin</div>}
              {pinLocation && (
                <button onClick={() => setMultiMode(!multiMode)}
                  style={{ width: '100%', padding: '7px 12px', background: multiMode ? '#5B2D9A' : 'white', color: multiMode ? 'white' : '#5B2D9A', border: '1.5px solid #5B2D9A', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginTop: 6 }}>
                  {multiMode ? '✅ Multi-area on — click OAs' : '+ Select multiple OAs'}
                </button>
              )}
            </div>

            <div style={{ borderTop: '1px solid #EDE5F8', marginBottom: 14 }} />

            {/* 2. Site Boundary */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 8 }}>2. Site Boundary <span style={{ fontWeight: 400, color: '#C4BDD4' }}>(optional)</span></div>
              {siteBoundary.length === 0 && !drawingSiteBoundary && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => { setDrawingSiteBoundary(true); setSiteBoundary([]); }}
                    style={{ flex: 1, padding: '8px', background: 'white', color: '#E8001C', border: '1.5px solid #E8001C', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    ✏️ Draw
                  </button>
                  <button onClick={() => { boundaryFileRef.current?.click(); }}
                    style={{ flex: 1, padding: '8px', background: 'white', color: '#E8001C', border: '1.5px solid #E8001C', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                    📂 Load
                  </button>
                </div>
              )}
              {drawingSiteBoundary && (
                <div>
                  <div style={{ fontSize: 11, color: '#E8001C', background: '#FFF5F5', border: '1px solid #FCA5A5', padding: '8px 10px', borderRadius: 6, marginBottom: 6 }}>
                    Click map to place boundary points · {siteBoundary.length} points placed
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {siteBoundary.length >= 3 && (
                      <button onClick={() => setDrawingSiteBoundary(false)}
                        style={{ flex: 1, padding: '8px', background: '#E8001C', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                        ✓ Close Shape
                      </button>
                    )}
                    <button onClick={() => { setDrawingSiteBoundary(false); setSiteBoundary([]); }}
                      style={{ flex: 1, padding: '8px', background: 'white', color: '#C4BDD4', border: '1.5px solid #E5E7EB', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {siteBoundary.length >= 3 && !drawingSiteBoundary && (
                <div style={{ background: '#FFF5F5', border: '1px solid #FCA5A5', borderRadius: 6, padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#E8001C' }}>✅ Site boundary set · {siteBoundary.length} pts</div>
                  <button onClick={() => { setDrawingSiteBoundary(true); setSiteBoundary([]); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#C4BDD4', fontFamily: 'inherit', fontWeight: 600 }}>Redraw</button>
                </div>
              )}
              <input ref={boundaryFileRef} type="file" accept=".geojson,.json,.kml,.zip" onChange={handleBoundaryFileLoad} style={{ display: 'none' }} />
            </div>

            <div style={{ borderTop: '1px solid #EDE5F8', marginBottom: 14 }} />

            {/* 3. Study Area */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 8 }}>3. Study Area</div>
              {!drawingPolygon && studyPolygon.length === 0 && (
                <button onClick={() => { setDrawingPolygon(true); setStudyPolygon([]); setRoadNetwork(null); setAssignedNetwork(null); }}
                  style={{ width: '100%', padding: '9px 12px', background: 'white', color: '#3D1A6E', border: '1.5px solid #3D1A6E', borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', fontFamily: 'inherit' }}>
                  ✏️ Draw Study Area
                </button>
              )}
              {drawingPolygon && (
                <div>
                  <div style={{ fontSize: 11, color: '#3D1A6E', background: '#F5F0FA', padding: '8px 10px', borderRadius: 6, marginBottom: 6 }}>
                    Click map to place points · {studyPolygon.length} points · Click first point to close
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {studyPolygon.length >= 3 && (
                      <button onClick={() => setDrawingPolygon(false)}
                        style={{ flex: 1, padding: '8px', background: '#3D1A6E', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                        ✓ Close Shape
                      </button>
                    )}
                    <button onClick={() => { setDrawingPolygon(false); setStudyPolygon([]); }}
                      style={{ flex: 1, padding: '8px', background: 'white', color: '#C4BDD4', border: '1.5px solid #E5E7EB', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {!drawingPolygon && studyPolygon.length >= 3 && (
                <div style={{ background: '#F0FDF9', border: '1px solid #0D9488', borderRadius: 6, padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#0D9488' }}>✅ Study area drawn · {studyPolygon.length} pts</div>
                  <button onClick={() => { setDrawingPolygon(true); setStudyPolygon([]); setRoadNetwork(null); setAssignedNetwork(null); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#C4BDD4', fontFamily: 'inherit', fontWeight: 600 }}>Redraw</button>
                </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid #EDE5F8', marginBottom: 14 }} />

            {/* 4. Site Access Point */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 8 }}>4. Site Access Point</div>
              {!roadNetwork && (
                <div style={{ fontSize: 11, color: '#C4BDD4', background: '#F9F7FD', padding: '8px 10px', borderRadius: 6 }}>Load road network in Network tab first</div>
              )}
              {roadNetwork && !siteAccessNode && (
                <button onClick={() => setAccessMode(!accessMode)}
                  style={{ width: '100%', padding: '9px 12px', background: accessMode ? '#0D9488' : 'white', color: accessMode ? 'white' : '#0D9488', border: '1.5px solid #0D9488', borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', cursor: 'pointer', fontFamily: 'inherit' }}>
                  {accessMode ? '✕ Cancel — click near road' : '🚗 Set Access Point'}
                </button>
              )}
              {accessMode && <div style={{ fontSize: 11, color: '#0D9488', background: '#F0FDF9', padding: '8px 10px', borderRadius: 6, marginTop: 6 }}>Click on or near a road to set the site access point</div>}
              {siteAccessNode && (
                <div style={{ background: '#F0FDF9', border: '1px solid #0D9488', borderRadius: 6, padding: '8px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#0D9488' }}>✅ Access point set</div>
                    <div style={{ fontSize: 10, color: '#6B6880', marginTop: 2 }}>{siteAccessNode.lat.toFixed(5)}, {siteAccessNode.lng.toFixed(5)}</div>
                  </div>
                  <button onClick={() => setSiteAccessNode(null)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#C4BDD4', fontFamily: 'inherit', fontWeight: 600 }}>Remove</button>
                </div>
              )}
            </div>

            <div style={{ borderTop: '1px solid #EDE5F8', marginBottom: 14 }} />

            {/* Clear all */}
            {(pinLocation || studyPolygon.length > 0 || siteBoundary.length > 0) && (
              <button onClick={() => { clearAll(); setSiteBoundary([]); setDrawingSiteBoundary(false); }}
                style={{ width: '100%', padding: '8px', background: 'white', color: '#C4BDD4', border: '1.5px solid #E5E7EB', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                Clear all
              </button>
            )}

            {/* Scenario/period banner */}
            {scenarios.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                {scenarios.length > 1 && (
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 4 }}>Active Scenario</div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {scenarios.map(sc => (
                        <button key={sc.id} onClick={() => setActiveScenarioId(sc.id)}
                          style={{ flex: 1, padding: '5px 6px', background: activeScenarioId === sc.id ? '#3D1A6E' : 'white', color: activeScenarioId === sc.id ? 'white' : '#3D1A6E', border: `1.5px solid ${activeScenarioId === sc.id ? '#3D1A6E' : '#EDE5F8'}`, borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {sc.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {timePeriods.length > 1 && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 4 }}>Active Period</div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {timePeriods.map(tp => (
                        <button key={tp.id} onClick={() => setActiveTimePeriodId(tp.id)}
                          style={{ flex: 1, padding: '5px 6px', background: activeTimePeriodId === tp.id ? '#7B4DBF' : 'white', color: activeTimePeriodId === tp.id ? 'white' : '#7B4DBF', border: `1.5px solid ${activeTimePeriodId === tp.id ? '#7B4DBF' : '#EDE5F8'}`, borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {tp.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Site Selection — now in Site tab */}
            {false && stepHeader(1, 'Site Selection', true, !!pinLocation)}
            {false && openStep === 1 && <>
              {zoom < 12 && <div style={{ fontSize: 11, color: '#6B6880', marginBottom: 10 }}>Zoom in to see Output Areas</div>}
              {pinMode && <div style={{ fontSize: 11, color: '#3D1A6E', fontWeight: 600, marginBottom: 10, background: '#F5F0FA', padding: '8px 10px', borderRadius: 6 }}>Click anywhere on the map to place your site pin</div>}
              {zoom >= 12 && !pinMode && !pinLocation && <div style={{ fontSize: 11, color: '#6B6880', marginBottom: 10 }}>Drop a pin on your development site</div>}
              {selectedOAs.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: '#6B6880', marginBottom: 6 }}>Selected Output {selectedOAs.length === 1 ? 'Area' : 'Areas'}:</div>
                  {selectedOAs.map(oa => (
                    <div key={oa} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 8px', background: '#F5F0FA', borderRadius: 5, marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: '#3D1A6E' }}>{oa}</span>
                      <button onClick={() => setSelectedOAs(prev => prev.filter(c => c !== oa))} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#C4BDD4', lineHeight: 1, padding: '0 2px' }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={() => setPinMode(!pinMode)} style={{ width: '100%', padding: '9px 12px', background: pinMode ? '#3D1A6E' : 'white', color: pinMode ? 'white' : '#3D1A6E', border: '1.5px solid #3D1A6E', borderRadius: 6, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 }}>
                {pinMode ? '✕ Cancel' : '📍 Drop site pin'}
              </button>
              {pinLocation && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color: '#0D9488' }}>✅ Pin placed</div>
                  <button onClick={() => { setPinLocation(null); setSelectedOAs([]); setOdFlows(null); setDestLayer(null); setModeShare(null); setOriginMSOA(null); setRoadNetwork(null); setAssignedNetwork(null); setSiteAccessNode(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#C4BDD4', fontFamily: 'inherit', textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.06em' }}>Remove</button>
                </div>
              )}
              {pinLocation && (
                <>
                  <button onClick={() => setMultiMode(!multiMode)} style={{ width: '100%', padding: '8px 12px', background: multiMode ? '#5B2D9A' : 'white', color: multiMode ? 'white' : '#5B2D9A', border: '1.5px solid #5B2D9A', borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 }}>
                    {multiMode ? '✅ Multi-area on' : 'Select multiple OAs'}
                  </button>
                  {multiMode && <div style={{ fontSize: 11, color: '#6B6880', marginBottom: 8 }}>Click any Output Area to add or remove it</div>}
                </>
              )}
            </>}

                        </>} />

          {/* ── PUBLIC TRANSPORT TAB ── */}
          <ToolSlot toolKey="transit" label="Transit" active={mainTab === 'transit'} dock={dockOf('transit')} onDock={setToolDock} floatPos={toolFloats.transit} setFloatPos={setToolFloatAt} rightEl={rightZoneEl} idx={2} render={() => <>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#3D1A6E', marginBottom: 4 }}>🚆 Public transport</div>
            <div style={{ fontSize: 11, color: '#6B6880', marginBottom: 14, lineHeight: 1.5 }}>Rail and bus access from the development site.</div>

            {!pinLocation ? (
              <div style={{ background: '#FFF9E6', border: '1px solid #FFD000', borderRadius: 6, padding: '10px 12px', fontSize: 12, color: '#6B6880' }}>
                ⚠️ Place a site pin first using the <strong>Site</strong> tab.
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 8 }}>Nearest rail stations</div>

                {railLoading && <div style={{ fontSize: 12, color: '#0D9488', padding: '8px 0' }}>Searching for nearby stations…</div>}
                {railError && (
                  <div style={{ fontSize: 11.5, color: '#993C1D', background: '#FAECE7', borderRadius: 6, padding: '8px 10px' }}>{railError}</div>
                )}

                {railStations && (
                  <div style={{ border: '1px solid #EDE5F8', borderRadius: 8, overflow: 'hidden' }}>
                    {railStations.map((s, i) => (
                      <div key={s.atco} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, padding: '9px 11px', background: i === 0 ? '#F0FDF9' : 'white', borderTop: i ? '1px solid #EDE5F8' : 'none' }}>
                        <span style={{ fontSize: 12.5, fontWeight: i === 0 ? 800 : 600, color: i === 0 ? '#0D9488' : '#1C1C2E' }}>{i === 0 ? '📍 ' : ''}{s.name}</span>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#6B6880', whiteSpace: 'nowrap' }}>{s.distance_km} km</span>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', margin: '16px 0 8px' }}>Nearest bus stops</div>

                {busRoute && (
                  <div style={{ background: '#E6EEFB', border: '1px solid #2563EB', borderRadius: 6, padding: '7px 10px', marginBottom: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#1E3A8A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(busRoute.synthetic && !busRoute.edited) ? '┅' : '▬'} {busRoute.line} → {busRoute.dest}{busRoute.edited ? ' (edited)' : busRoute.synthetic ? ' (approx.)' : ''}</span>
                      <button onClick={() => { setBusRoute(null); setBusRouteEditing(false); }} style={{ background: 'none', border: 'none', color: '#2563EB', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>Clear</button>
                    </div>
                    <button onClick={() => setBusRouteEditing(v => !v)}
                      style={{ marginTop: 6, width: '100%', padding: '6px 10px', background: busRouteEditing ? '#2563EB' : 'white', color: busRouteEditing ? 'white' : '#2563EB', border: '1px solid #2563EB', borderRadius: 5, fontSize: 10.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                      {busRouteEditing ? '✓ Done editing' : '✏️ Edit line — drag onto roads'}
                    </button>
                    {busRouteEditing && <div style={{ fontSize: 9.5, color: '#6B6880', marginTop: 5, lineHeight: 1.4 }}>Drag a white handle to move it · drag a purple midpoint to add a point · right-click a handle to delete.</div>}
                  </div>
                )}

                {busLoading && <div style={{ fontSize: 12, color: '#0D9488', padding: '8px 0' }}>Searching for nearby bus stops…</div>}
                {busError && (
                  <div style={{ fontSize: 11.5, color: '#993C1D', background: '#FAECE7', borderRadius: 6, padding: '8px 10px' }}>{busError}</div>
                )}

                {busStops && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {busStops.map((s, i) => {
                      const id = s.stop_id || s.atco;
                      const open = expandedBus === id;
                      const hasSvc = s.services && s.services.length > 0;
                      const shown = hasSvc ? (open ? s.services : s.services.slice(0, 4)) : [];
                      return (
                      <div key={id} style={{ border: `1px solid ${open ? '#0D9488' : '#EDE5F8'}`, borderRadius: 8, overflow: 'hidden' }}>
                        <div onClick={() => hasSvc && setExpandedBus(open ? null : id)}
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, padding: '8px 11px', background: i === 0 ? '#F0FDF9' : 'white', cursor: hasSvc ? 'pointer' : 'default' }}>
                          <span style={{ fontSize: 12.5, fontWeight: i === 0 ? 800 : 600, color: i === 0 ? '#0D9488' : '#1C1C2E' }}>{hasSvc && <span style={{ color: '#C4BDD4', fontSize: 10 }}>{open ? '▾ ' : '▸ '}</span>}{i === 0 ? '🚌 ' : ''}{s.name}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#6B6880', whiteSpace: 'nowrap' }}>{s.distance_km} km</span>
                        </div>
                        {typeof s.am_total === 'number' && (
                          <div style={{ fontSize: 10.5, color: '#6B6880', padding: '0 11px 6px' }}>
                            <strong style={{ color: '#0D9488' }}>{s.am_total}</strong> AM · <strong style={{ color: '#0D9488' }}>{s.pm_total}</strong> PM buses/hr
                            {hasSvc && <span style={{ color: '#C4BDD4' }}> · {s.services.length} route{s.services.length === 1 ? '' : 's'}</span>}
                          </div>
                        )}
                        {hasSvc && (
                          <div style={{ borderTop: '1px solid #F5F0FA' }}>
                            {shown.map((sv, j) => {
                              const active = busRoute && busRoute.shape_id === sv.shape;
                              const loadingThis = busRouteLoading === sv.shape;
                              return (
                              <div key={j} onClick={() => showBusRoute(sv)} title={sv.shape ? 'Show this route on the map' : 'No route line available for this service'}
                                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6, padding: '4px 11px', fontSize: 10.5, color: '#1C1C2E', borderTop: j ? '1px solid #FAF7FC' : 'none', cursor: sv.shape ? 'pointer' : 'default', background: active ? '#E6EEFB' : 'transparent' }}>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {sv.shape && <span style={{ color: active ? '#2563EB' : '#C4BDD4', fontSize: 9 }}>{loadingThis ? '⏳ ' : active ? '▬ ' : '➟ '}</span>}
                                  <strong style={{ color: '#3D1A6E' }}>{sv.line}</strong> → {sv.dest}
                                </span>
                                <span style={{ color: '#6B6880', whiteSpace: 'nowrap' }} title="AM/PM buses per hour">{sv.am}/{sv.pm}</span>
                              </div>
                              );
                            })}
                            {s.services.length > 4 && (
                              <div onClick={() => setExpandedBus(open ? null : id)} style={{ fontSize: 9.5, fontWeight: 700, color: '#0D9488', padding: '5px 11px 7px', cursor: 'pointer' }}>
                                {open ? '▴ Show fewer' : `▾ Show all ${s.services.length} services`}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}

                <button disabled={railLoading || busLoading} onClick={() => { setRailStations(null); setRailError(null); setBusStops(null); setBusError(null); setExpandedBus(null); setBusRoute(null); setBusRouteEditing(false); }}
                  style={{ width: '100%', marginTop: 12, padding: '8px 12px', background: 'white', color: '#3D1A6E', border: '1.5px solid #EDE5F8', borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', cursor: (railLoading || busLoading) ? 'wait' : 'pointer', fontFamily: 'inherit' }}>
                  ↻ Refresh
                </button>

                <div style={{ fontSize: 10, color: '#C4BDD4', marginTop: 10, lineHeight: 1.5 }}>
                  Straight-line distance · rail from NaPTAN · bus services + frequencies from BODS (GTFS, typical weekday AM/PM peak hour). Values shown as AM/PM buses per hour.
                </div>
              </div>
            )}
          </>} />

          {/* ── CENSUS & ASSIGNMENT TAB ── */}
          <ToolSlot toolKey="distribution" label="Distribution" active={mainTab === 'distribution'} dock={dockOf('distribution')} onDock={setToolDock} floatPos={toolFloats.distribution} setFloatPos={setToolFloatAt} rightEl={rightZoneEl} idx={3} render={() => <>

            {/* Scenario/period banner */}
            {scenarios.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                {scenarios.length > 1 && (
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 4 }}>Active Scenario</div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {scenarios.map(sc => (
                        <button key={sc.id} onClick={() => setActiveScenarioId(sc.id)}
                          style={{ flex: 1, padding: '5px 6px', background: activeScenarioId === sc.id ? '#3D1A6E' : 'white', color: activeScenarioId === sc.id ? 'white' : '#3D1A6E', border: `1.5px solid ${activeScenarioId === sc.id ? '#3D1A6E' : '#EDE5F8'}`, borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                          {sc.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {!pinLocation && (
              <div style={{ background: '#FFF9E6', border: '1px solid #FFD000', borderRadius: 6, padding: '10px 12px', marginBottom: 12, fontSize: 12, color: '#6B6880' }}>
                ⚠️ Place a site pin first using the <strong>Site</strong> tab.
              </div>
            )}

            {/* Distribution & Mode Share — combined */}
            {stepHeader(3, 'Distribution & Mode Share', selectedOAs.length > 0, !!odFlows && !!(modeShare || modeShareMethod === 'manual'))}
            {(openStep === 3 || isDetached('distribution')) && !(selectedOAs.length > 0) && prereqNotice([
              { done: selectedOAs.length > 0, label: 'Output areas selected', hint: 'Site tab → draw a study area to select the census output areas' },
            ], 'Select your study area first:')}
            {(openStep === 3 || isDetached('distribution')) && selectedOAs.length > 0 && <>
              {/* Local-detail radius: LSOA detail within it, MSOA beyond */}
              <div style={{ background: '#F9F7FD', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#6B6880' }}>Local detail radius</label>
                  <input type="number" min="1" step="1" value={distributionRadiusKm}
                    onChange={e => setDistributionRadiusKm(Math.max(1, parseFloat(e.target.value) || 1))}
                    style={{ width: 64, padding: '5px 8px', borderRadius: 6, border: '1.5px solid #EDE5F8', fontSize: 12, fontFamily: 'inherit', textAlign: 'center', color: '#1C1C2E' }} />
                  <span style={{ fontSize: 12, color: '#6B6880' }}>km from site</span>
                </div>
                <div style={{ fontSize: 10.5, color: '#8B7FA8', marginTop: 5, lineHeight: 1.4 }}>
                  Destinations within this radius use fine LSOA detail; beyond it, coarser MSOAs. Change it and re-fetch to re-tier by distance.{!pinLocation && ' (Place a site pin to use distance tiering.)'}
                </div>
              </div>
              <button onClick={fetchODFlows} disabled={odLoading} style={{ width: '100%', padding: '11px 12px', background: odLoading ? '#EDE5F8' : '#3D1A6E', color: odLoading ? '#7B4DBF' : 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: odLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', marginBottom: 8 }}>
                {odLoading ? 'Fetching census data...' : '📊 Get Census Data'}
              </button>
              {odLoading && <LoadingBar label="Fetching trip distribution..." />}
              {destLoading && <LoadingBar label="Loading destination zones..." />}
              {odError && <div style={{ fontSize: 12, color: '#991B1B', marginTop: 8, background: '#FEE2E2', padding: '8px 10px', borderRadius: 6 }}>{odError}</div>}

              {/* Mode share method toggle */}
              {odFlows && (
                <div style={{ marginTop: 4, marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 6 }}>Mode Share Method</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[{ key: 'census', label: '📊 Census' }, { key: 'manual', label: '✏️ Manual' }].map(m => (
                      <button key={m.key} onClick={() => setModeShareMethod(m.key)}
                        style={{ flex: 1, padding: '7px 4px', background: modeShareMethod === m.key ? '#3D1A6E' : 'white', color: modeShareMethod === m.key ? 'white' : '#3D1A6E', border: `1.5px solid ${modeShareMethod === m.key ? '#3D1A6E' : '#EDE5F8'}`, borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Census mode share */}
              {odFlows && modeShareMethod === 'census' && (
                <>
                  {modeLoading && <LoadingBar label="Fetching mode share..." />}
                  {modeShare && (
                <>
                  <div style={{ borderTop: '1px solid #EDE5F8', margin: '10px 0 10px' }} />
                  {originMSOA && (
                    <div style={{ fontSize: 11, color: '#6B6880', marginBottom: 8, background: '#F5F0FA', padding: '8px 10px', borderRadius: 6 }}>
                      <div>OA: <strong style={{ color: '#3D1A6E' }}>{originMSOA.oa}</strong></div>
                      <div style={{ marginTop: 3 }}>MSOA: <strong style={{ color: '#3D1A6E' }}>{originMSOA.msoa}</strong> ({originMSOA.msoa_name})</div>
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: '#3D1A6E', fontWeight: 600, marginBottom: 10 }}>{modeShare.total} census commuters — mode split:</div>
                  {modeShare.modes.map(mode => {
                    const isCarDriver = mode.code === 7;
                    return (
                      <div key={mode.code} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, background: isCarDriver ? '#F5F0FA' : 'transparent', borderRadius: isCarDriver ? 6 : 0, padding: isCarDriver ? '6px 8px' : '0' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                            <span style={{ fontSize: 11, color: isCarDriver ? '#3D1A6E' : '#6B6880', fontWeight: isCarDriver ? 700 : 400 }}>{isCarDriver ? '🚗 ' : ''}{mode.name}</span>
                            <span style={{ fontSize: 11, color: '#6B6880', fontWeight: 600 }}>{mode.percentage}%</span>
                          </div>
                          <div style={{ height: 4, background: '#EDE5F8', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{ height: '100%', borderRadius: 2, background: isCarDriver ? '#3D1A6E' : '#C4BDD4', width: `${Math.min(mode.percentage, 100)}%` }} />
                          </div>
                        </div>
                        <div style={{ fontSize: 10, color: '#C4BDD4', width: 36, textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontWeight: 600 }}>{mode.count}</div>
                          <div>census</div>
                        </div>
                      </div>
                    );
                  })}
                  {totalPersonTrips && (() => {
                    const { arrV, depV } = getVehicleTripPreview();
                    const adj = getCarTripAdjustment();
                    const stdTotal = arrV + depV;
                    const ratio = stdTotal > 0 ? adj.adjusted / stdTotal : 1;
                    const arrAdj = Math.round(arrV * ratio), depAdj = Math.round(depV * ratio);
                    return (
                      <div style={{ background: '#3D1A6E', borderRadius: 6, padding: '10px 12px', marginTop: 10, marginBottom: 4 }}>
                        <div style={{ fontSize: 11, color: '#C4BDD4', marginBottom: 4 }}>Car trips assigned to roads ({assignPeriod === 'both' ? 'AM + PM' : assignPeriod.toUpperCase()} peak):</div>
                        {adj.hasOverrides ? (
                          <>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                              <span style={{ fontSize: 14, color: '#C4BDD4', textDecoration: 'line-through' }}>{adj.standard}</span>
                              <span style={{ fontSize: 12, color: '#C4BDD4' }}>→</span>
                              <span style={{ fontSize: 22, fontWeight: 700, color: 'white' }}>{adj.adjusted}</span>
                            </div>
                            <div style={{ fontSize: 11, color: '#6FE9CE', marginTop: 4, fontWeight: 600 }}>{adj.reassigned} trips reassigned to other modes</div>
                            <div style={{ fontSize: 11, color: '#C4BDD4', marginTop: 2 }}>{arrAdj} arrivals · {depAdj} departures</div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: 22, fontWeight: 700, color: 'white' }}>{stdTotal}</div>
                            <div style={{ fontSize: 11, color: '#C4BDD4', marginTop: 4 }}>{arrV} arrivals · {depV} departures</div>
                          </>
                        )}
                      </div>
                    );
                  })()}
                  </>
                )}
                </>
              )}

              {/* Manual mode share */}
              {odFlows && modeShareMethod === 'manual' && (() => {
                const total = manualModes.reduce((sum, m) => sum + (parseFloat(m.percentage) || 0), 0);
                const totalRounded = Math.round(total * 10) / 10;
                const isValid = Math.abs(total - 100) < 0.1;
                
                return (
                  <div>
                    <div style={{ fontSize: 11, color: '#6B6880', marginBottom: 10, background: '#F5F0FA', padding: '8px 10px', borderRadius: 6 }}>
                      Enter mode share percentages. Must sum to 100%.
                    </div>
                    {manualModes.map((mode, i) => {
                      const isCarDriver = mode.code === 7;
                      return (
                        <div key={mode.code} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, background: isCarDriver ? '#F5F0FA' : 'transparent', borderRadius: isCarDriver ? 6 : 0, padding: isCarDriver ? '6px 8px' : '0 0 0 0' }}>
                          <div style={{ flex: 1, fontSize: 11, color: isCarDriver ? '#3D1A6E' : '#6B6880', fontWeight: isCarDriver ? 700 : 400 }}>
                            {isCarDriver ? '🚗 ' : ''}{mode.name}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <input
                              type="number" min="0" max="100" step="0.1"
                              value={mode.percentage}
                              onChange={e => setManualModes(prev => prev.map((m, j) => j === i ? { ...m, percentage: e.target.value } : m))}
                              style={{ width: 56, padding: '4px 6px', borderRadius: 5, border: `1.5px solid ${isCarDriver ? '#3D1A6E' : '#EDE5F8'}`, fontSize: 12, fontFamily: 'inherit', color: '#1C1C2E', outline: 'none', textAlign: 'right' }}
                            />
                            <span style={{ fontSize: 11, color: '#6B6880' }}>%</span>
                          </div>
                        </div>
                      );
                    })}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, padding: '8px 10px', borderRadius: 6, background: isValid ? '#F0FDF9' : Math.abs(total) < 0.1 ? '#F5F0FA' : '#FEE2E2', border: `1px solid ${isValid ? '#0D9488' : Math.abs(total) < 0.1 ? '#EDE5F8' : '#FCA5A5'}` }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: isValid ? '#0D9488' : '#991B1B' }}>
                        {isValid ? '✅ Valid' : `Total: ${totalRounded}% — must equal 100%`}
                      </span>
                      <span style={{ fontSize: 11, color: '#6B6880', fontWeight: 700 }}>{totalRounded}%</span>
                    </div>
                    {isValid && totalPersonTrips && (() => {
                      const { arrV, depV } = getVehicleTripPreview();
                      const adj = getCarTripAdjustment();
                      const stdTotal = arrV + depV;
                      const ratio = stdTotal > 0 ? adj.adjusted / stdTotal : 1;
                      const arrAdj = Math.round(arrV * ratio), depAdj = Math.round(depV * ratio);
                      return (
                        <div style={{ background: '#3D1A6E', borderRadius: 6, padding: '10px 12px', marginTop: 10, marginBottom: 4 }}>
                          <div style={{ fontSize: 11, color: '#C4BDD4', marginBottom: 4 }}>Car trips assigned to roads ({assignPeriod === 'both' ? 'AM + PM' : assignPeriod.toUpperCase()} peak):</div>
                          {adj.hasOverrides ? (
                            <>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                                <span style={{ fontSize: 14, color: '#C4BDD4', textDecoration: 'line-through' }}>{adj.standard}</span>
                                <span style={{ fontSize: 12, color: '#C4BDD4' }}>→</span>
                                <span style={{ fontSize: 22, fontWeight: 700, color: 'white' }}>{adj.adjusted}</span>
                              </div>
                              <div style={{ fontSize: 11, color: '#6FE9CE', marginTop: 4, fontWeight: 600 }}>{adj.reassigned} trips reassigned to other modes</div>
                              <div style={{ fontSize: 11, color: '#C4BDD4', marginTop: 2 }}>{arrAdj} arrivals · {depAdj} departures</div>
                            </>
                          ) : (
                            <>
                              <div style={{ fontSize: 22, fontWeight: 700, color: 'white' }}>{stdTotal}</div>
                              <div style={{ fontSize: 11, color: '#C4BDD4', marginTop: 4 }}>{arrV} arrivals · {depV} departures</div>
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}

              {/* Trip purpose & education schools */}
              {odFlows && (() => {
                const activeSc = scenarios.find(sc => sc.id === activeScenarioId) || scenarios[0];
                const phaseColour = { primary: '#0D9488', secondary: '#7B4DBF', all_through: '#C2740E' };
                const phaseLabel = { primary: 'Primary', secondary: 'Secondary', all_through: 'All-through' };
                const schools = (educationSchools?.schools || []).filter(s => (s.trips || 0) > 0);
                return (
                  <>
                    <div style={{ borderTop: '1px solid #EDE5F8', margin: '14px 0 10px' }} />
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#3D1A6E', marginBottom: 8 }}>Trip Purpose</div>
                    {activeSc && timePeriods.map(tp => {
                      const t = periodTotals(activeSc, tp, scenarios, devSize);
                      if (!t.total) return null;
                      const sp = normaliseSplit(purposeSplitForPeriod(tp));
                      const emp = Math.round(t.total * sp.employment / 100);
                      const edu = Math.round(t.total * sp.education / 100);
                      const oth = t.total - emp - edu;
                      return (
                        <div key={tp.id} style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 10, color: '#6B6880', marginBottom: 3 }}>{tp.name}</div>
                          <div style={{ display: 'flex', height: 12, borderRadius: 3, overflow: 'hidden', marginBottom: 3 }}>
                            {emp > 0 && <div title={`Employment ${emp}`} style={{ width: `${emp / t.total * 100}%`, background: '#3D1A6E' }} />}
                            {edu > 0 && <div title={`Education ${edu}`} style={{ width: `${edu / t.total * 100}%`, background: '#0D9488' }} />}
                            {oth > 0 && <div title={`Other ${oth}`} style={{ width: `${oth / t.total * 100}%`, background: '#7B4DBF' }} />}
                          </div>
                          <div style={{ fontSize: 10, color: '#6B6880' }}>
                            <span style={{ color: '#3D1A6E', fontWeight: 700 }}>{emp}</span> emp ·{' '}
                            <span style={{ color: '#0D9488', fontWeight: 700 }}>{edu}</span> edu ·{' '}
                            <span style={{ color: '#7B4DBF', fontWeight: 700 }}>{oth}</span> other
                          </div>
                        </div>
                      );
                    })}

                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#3D1A6E', margin: '12px 0 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Education schools</span>
                      {excludedSchools.length > 0 && (
                        <button onClick={() => { setExcludedSchools([]); refreshEducationSchools([]); }}
                          style={{ background: 'none', border: 'none', color: '#7B4DBF', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}>
                          Reset ({excludedSchools.length} excluded)
                        </button>
                      )}
                    </div>
                    {schools.length === 0 ? (
                      <div style={{ fontSize: 11, color: '#6B6880', lineHeight: 1.5 }}>Run trip assignment to distribute education trips to schools; the schools appear here, editable.</div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', gap: 12, fontSize: 9.5, color: '#6B6880', marginBottom: 6 }}>
                          {['primary', 'secondary', 'all_through'].map(p => (
                            <span key={p} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: phaseColour[p] }} />{phaseLabel[p]}
                            </span>
                          ))}
                        </div>
                        {schools.slice(0, 20).map(s => (
                          <div key={s.urn} style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4, fontSize: 11 }}>
                            <span style={{ width: 9, height: 9, borderRadius: '50%', background: phaseColour[s.phase] || '#0D9488', flexShrink: 0 }} />
                            <span style={{ flex: 1, color: '#1C1C2E', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`${s.name} · ${phaseLabel[s.phase] || s.phase} · ${s.distance_km} km`}>{s.name}</span>
                            <span style={{ fontWeight: 800, color: phaseColour[s.phase] || '#0D9488', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{s.trips}</span>
                            <button onClick={() => toggleSchoolExclusion(s.urn)} title="Exclude this school (redistributes its trips)"
                              style={{ background: 'none', border: '1px solid #EDE5F8', borderRadius: 4, color: '#C2410C', fontSize: 11, lineHeight: 1, cursor: 'pointer', fontFamily: 'inherit', padding: '2px 5px', flexShrink: 0 }}>✕</button>
                          </div>
                        ))}
                        <div style={{ fontSize: 10, color: '#6B6880', marginTop: 6, lineHeight: 1.4 }}>Excluding a school redistributes its trips to the rest. Re-run assignment to update the routes.</div>
                      </>
                    )}
                  </>
                );
              })()}

              {/* Distribution detail */}
              {odFlows && <>
                <div style={{ borderTop: '1px solid #EDE5F8', margin: '14px 0 10px' }} />
                <div onClick={() => setShowDistDetail(prev => !prev)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#3D1A6E' }}>Distribution Detail</div>
                  <div style={{ fontSize: 11, color: '#7B4DBF', fontWeight: 600 }}>{showDistDetail ? '▲ Hide' : '▼ Show'}</div>
                </div>
                {!showDistDetail && <div style={{ fontSize: 11, color: '#6B6880', marginBottom: 8 }}>{odFlows.flows.length} destination zones — click to expand</div>}
                {showDistDetail && <>
                  <div style={{ fontSize: 12, color: '#3D1A6E', fontWeight: 600, marginBottom: 8 }}>{odFlows.total_trips} census commuters from selected {selectedOAs.length === 1 ? 'OA' : 'OAs'}</div>
                  {totalPersonTrips && <div style={{ fontSize: 11, color: '#6B6880', marginBottom: 10, background: '#F5F0FA', padding: '8px 10px', borderRadius: 6 }}>Distributing <strong style={{ color: '#3D1A6E' }}>{totalPersonTrips} development trips</strong> using census proportions</div>}
                  {destLayer && <div style={{ fontSize: 11, color: '#0D9488', marginBottom: 6, background: '#F0FDF9', padding: '8px 10px', borderRadius: 6 }}>✅ LSOA zones within {originMSOA?.lsoa_name?.split(' ').slice(0, -1).join(' ') || 'local area'} · MSOA zones beyond</div>}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                    <div style={{ fontSize: 10, color: '#C4BDD4' }}>Fewer trips</div>
                    <div style={{ flex: 1, height: 8, borderRadius: 4, background: 'linear-gradient(to right, #EDE5F8, #3D1A6E)' }} />
                    <div style={{ fontSize: 10, color: '#C4BDD4' }}>More trips</div>
                  </div>
                  <div style={{ fontSize: 10, color: '#6B6880', marginBottom: 8 }}>Grouped by local authority — use <strong style={{ color: '#0D9488' }}>Adjust modes</strong> to reassign an area's trips (e.g. central London → rail). Only car trips are assigned to roads.</div>
                  {Object.keys(destCentroids).length > 0 && <div style={{ fontSize: 10, color: '#6B6880', marginBottom: 8, background: '#F0FDF9', padding: '6px 8px', borderRadius: 5 }}>📍 Turn on <strong style={{ color: '#0D9488' }}>Destination centroids</strong> in the map layers panel to drag a routing point onto the road network (population-weighted centroids can sit off it).</div>}
                  {(() => {
                    const groups = {};
                    odFlows.flows.forEach((flow, i) => { const lad = ladOf(flow); (groups[lad] = groups[lad] || []).push({ flow, i }); });
                    const ladNames = Object.keys(groups).sort((a, b) =>
                      groups[b].reduce((s, x) => s + x.flow.percentage, 0) - groups[a].reduce((s, x) => s + x.flow.percentage, 0));
                    const modeRows = [['car', '🚗 Car'], ['rail', '🚆 Rail'], ['bus', '🚌 Bus'], ['cycle', '🚲 Cycle'], ['walk', '🚶 Walk'], ['other', 'Other']];
                    // Shared 6-mode split editor for both LA-level and zone-level overrides.
                    const renderEditor = ({ split, title, onField, onReset, onRemove, onDone }) => {
                      const total = modeRows.reduce((s, [k]) => s + (parseFloat(split[k]) || 0), 0);
                      return (
                        <div style={{ marginTop: 8, background: '#F9F7FD', borderRadius: 6, padding: '8px 10px' }}>
                          <div style={{ fontSize: 10, color: '#6B6880', marginBottom: 6 }}>{title} — must total 100%. Car % feeds the road assignment; the rest is recorded for the report.</div>
                          {modeRows.map(([key, label]) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                              <span style={{ flex: 1, fontSize: 11, color: key === 'car' ? '#3D1A6E' : '#6B6880', fontWeight: key === 'car' ? 700 : 400 }}>{label}</span>
                              <input type="number" value={split[key]} min={0} max={100}
                                onChange={e => onField(key, e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                                style={{ width: 56, padding: '3px 6px', borderRadius: 5, border: '1.5px solid #EDE5F8', fontSize: 11, fontFamily: 'inherit', textAlign: 'right', boxSizing: 'border-box' }} />
                              <span style={{ fontSize: 10, color: '#C4BDD4', width: 8 }}>%</span>
                            </div>
                          ))}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, flexWrap: 'wrap', gap: 6 }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: Math.abs(total - 100) < 0.1 ? '#0D9488' : '#E8001C' }}>Total: {Math.round(total * 10) / 10}%{Math.abs(total - 100) < 0.1 ? '' : ' ⚠️ must be 100'}</span>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={onReset} style={{ padding: '3px 8px', background: 'white', color: '#7B4DBF', border: '1.5px solid #EDE5F8', borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Reset</button>
                              <button onClick={onRemove} style={{ padding: '3px 8px', background: 'white', color: '#E8001C', border: '1.5px solid #FCA5A5', borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Remove</button>
                              <button onClick={onDone} style={{ padding: '3px 8px', background: '#3D1A6E', color: 'white', border: 'none', borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Done</button>
                            </div>
                          </div>
                        </div>
                      );
                    };
                    return ladNames.map(lad => {
                      const items = groups[lad];
                      const groupPct = Math.round(items.reduce((s, x) => s + x.flow.percentage, 0) * 10) / 10;
                      const groupDev = totalPersonTrips ? Math.round(groupPct / 100 * totalPersonTrips) : null;
                      const ov = modeOverrides[lad];
                      const editing = modeOverrideEditingLad === lad;
                      return (
                        <div key={lad} style={{ marginBottom: 10, border: `1px solid ${ov ? '#0D9488' : '#EDE5F8'}`, borderRadius: 8, padding: '8px 10px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: '#3D1A6E', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lad}</div>
                              <div style={{ fontSize: 10, color: '#6B6880' }}>{items.length} zone{items.length === 1 ? '' : 's'} · {groupPct}%{groupDev !== null ? ` · ${groupDev} dev trips` : ''}</div>
                            </div>
                            <button onClick={() => { if (!ov) setModeOverrides(prev => ({ ...prev, [lad]: globalMainSplit() })); setModeOverrideEditingZone(null); setModeOverrideEditingLad(editing ? null : lad); }}
                              style={{ flexShrink: 0, padding: '4px 8px', background: ov ? '#0D9488' : 'white', color: ov ? 'white' : '#0D9488', border: '1.5px solid #0D9488', borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                              {ov ? `🚗 ${ov.car}% car` : '✏️ Adjust modes'}
                            </button>
                          </div>

                          {editing && ov && renderEditor({
                            split: ov,
                            title: `Whole-area mode split for ${lad}`,
                            onField: (k, v) => setModeOverrides(prev => ({ ...prev, [lad]: { ...prev[lad], [k]: v } })),
                            onReset: () => setModeOverrides(prev => ({ ...prev, [lad]: globalMainSplit() })),
                            onRemove: () => { setModeOverrides(prev => { const n = { ...prev }; delete n[lad]; return n; }); setModeOverrideEditingLad(null); },
                            onDone: () => setModeOverrideEditingLad(null),
                          })}

                          <div style={{ marginTop: 8 }}>
                            {items.map(({ flow, i }) => {
                              const devTrips = totalPersonTrips ? Math.round(flow.percentage / 100 * totalPersonTrips) : null;
                              const zoneOv = zoneOverrides[flow.destination];
                              const effOv = resolveOverride(flow);
                              const zEditing = modeOverrideEditingZone === flow.destination;
                              return (
                                <div key={flow.destination} style={{ marginBottom: 8 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <div style={{ fontSize: 10, color: '#C4BDD4', width: 16, textAlign: 'right', flexShrink: 0 }}>{i + 1}</div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2, alignItems: 'center' }}>
                                        <div style={{ minWidth: 0 }}>
                                          <span style={{ fontSize: 11, color: '#3D1A6E', fontWeight: 600 }}>{flow.name || flow.destination}</span>
                                          <span style={{ fontSize: 9, color: flow.type === 'lsoa' ? '#0D9488' : '#7B4DBF', marginLeft: 4, fontWeight: 700, textTransform: 'uppercase' }}>{flow.type}</span>
                                          {zoneOv && <span style={{ fontSize: 9, color: '#0D9488', marginLeft: 4, fontWeight: 700 }}>· 🚗 {zoneOv.car}%</span>}
                                        </div>
                                        <span style={{ fontSize: 11, color: '#6B6880', fontWeight: 600 }}>{flow.percentage}%</span>
                                      </div>
                                      <div style={{ height: 4, background: '#EDE5F8', borderRadius: 2, overflow: 'hidden' }}>
                                        <div style={{ height: '100%', borderRadius: 2, background: flow.type === 'lsoa' ? '#0D9488' : '#3D1A6E', width: `${Math.min(flow.percentage, 100)}%` }} />
                                      </div>
                                      {devTrips !== null && <div style={{ fontSize: 10, color: '#0D9488', marginTop: 2, fontWeight: 600 }}>{devTrips} dev · {flow.count} census{effOv ? ` · ${Math.round(devTrips * (effOv.car || 0) / 100)} car` : ''}</div>}
                                    </div>
                                    <button onClick={() => { if (!zoneOv) setZoneOverrides(prev => ({ ...prev, [flow.destination]: effOv ? { ...effOv } : globalMainSplit() })); setModeOverrideEditingLad(null); setModeOverrideEditingZone(zEditing ? null : flow.destination); }}
                                      style={{ flexShrink: 0, padding: '3px 6px', background: zoneOv ? '#0D9488' : 'white', color: zoneOv ? 'white' : '#7B4DBF', border: `1px solid ${zoneOv ? '#0D9488' : '#EDE5F8'}`, borderRadius: 5, fontSize: 9, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                      {zEditing ? 'Close' : 'modes'}
                                    </button>
                                  </div>
                                  {zEditing && zoneOv && renderEditor({
                                    split: zoneOv,
                                    title: `Mode split for ${flow.name || flow.destination}`,
                                    onField: (k, v) => setZoneOverrides(prev => ({ ...prev, [flow.destination]: { ...prev[flow.destination], [k]: v } })),
                                    onReset: () => setZoneOverrides(prev => ({ ...prev, [flow.destination]: globalMainSplit() })),
                                    onRemove: () => { setZoneOverrides(prev => { const n = { ...prev }; delete n[flow.destination]; return n; }); setModeOverrideEditingZone(null); },
                                    onDone: () => setModeOverrideEditingZone(null),
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </>}
              </>}
            </>}

            </>} />

          {/* ── NETWORK TAB: Site access + Road Network ── */}
          <ToolSlot toolKey="network" label="Network" active={mainTab === 'network'} dock={dockOf('network')} onDock={setToolDock} floatPos={toolFloats.network} setFloatPos={setToolFloatAt} rightEl={rightZoneEl} idx={4} render={() => <>

            {/* Road Network */}
            {stepHeader(4, 'Road Network', !!pinLocation, !!roadNetwork)}
            {(openStep === 4 || isDetached('network')) && !pinLocation && prereqNotice([
              { done: !!pinLocation, label: 'Site pinned', hint: 'Site tab → place the site pin' },
              { done: studyPolygon.length >= 3, label: 'Study area drawn', hint: 'Site tab → draw the study area to select output areas' },
            ], 'Set up the site first:')}
            {(openStep === 4 || isDetached('network')) && pinLocation && <>
              {studyPolygon.length < 3 && (
                <div style={{ fontSize: 11, color: '#6B6880', background: '#FFF9E6', border: '1px solid #FFD000', padding: '8px 10px', borderRadius: 6, marginBottom: 10 }}>
                  ⚠️ Draw a study area in the <strong>Site</strong> tab first
                </div>
              )}
              <button onClick={fetchRoadNetwork} disabled={roadLoading || studyPolygon.length < 3} style={{ width: '100%', padding: '11px 12px', background: (roadLoading || studyPolygon.length < 3) ? '#EDE5F8' : '#3D1A6E', color: (roadLoading || studyPolygon.length < 3) ? '#7B4DBF' : 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: (roadLoading || studyPolygon.length < 3) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', marginBottom: 8 }}>
                {roadLoading ? 'Loading road network...' : studyPolygon.length < 3 ? 'Draw study area first' : '🛣️ Load Road Network'}
            </button>
            {roadLoading && <LoadingBar label="Building road network from OpenStreetMap..." />}
            {roadError && <div style={{ fontSize: 11, color: '#991B1B', background: '#FEE2E2', border: '1px solid #FCA5A5', padding: '8px 10px', borderRadius: 6, marginBottom: 8 }}>⚠️ {roadError}</div>}
            {roadNetwork && (
                <>
                  <div style={{ fontSize: 12, color: '#0D9488', background: '#F0FDF9', padding: '8px 10px', borderRadius: 6, marginBottom: 8 }}>
                    ✅ {roadNetwork.edge_count} road links · {roadNetwork.node_count} junctions loaded
                  </div>
                  <button onClick={() => setAccessMode(!accessMode)} style={{ width: '100%', padding: '9px 12px', background: accessMode ? '#0D9488' : 'white', color: accessMode ? 'white' : '#0D9488', border: '1.5px solid #0D9488', borderRadius: 6, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 }}>
                    {accessMode ? '✕ Cancel' : '🚗 Set Site Access Point'}
                  </button>
                  {accessMode && <div style={{ fontSize: 11, color: '#0D9488', fontWeight: 600, marginBottom: 8, background: '#F0FDF9', padding: '8px 10px', borderRadius: 6 }}>Click anywhere on or near a road to set the site access point</div>}
                  {siteAccessNode && layerVisibility.siteAccess && (
                    <div style={{ fontSize: 11, color: '#0D9488', marginBottom: 8, background: '#F0FDF9', padding: '8px 10px', borderRadius: 6 }}>
                      ✅ Site access set at {siteAccessNode.lat.toFixed(5)}, {siteAccessNode.lng.toFixed(5)}
                      <button onClick={() => setSiteAccessNode(null)} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: '#C4BDD4', fontFamily: 'inherit', textTransform: 'uppercase', fontWeight: 600 }}>Remove</button>
                    </div>
                  )}
                  <div style={{ marginTop: 4, marginBottom: 4 }}>
                    {[
                      { type: 'trunk', label: 'Trunk road', colour: '#FF6B00' },
                      { type: 'primary', label: 'Primary road', colour: '#FF9900' },
                      { type: 'secondary', label: 'Secondary road', colour: '#FFD000' },
                      { type: 'tertiary', label: 'Tertiary road', colour: '#999999' },
                      { type: 'residential', label: 'Residential', colour: '#CCCCCC' },
                    ].map(r => (
                      <div key={r.type} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <div style={{ width: 24, height: 3, background: r.colour, borderRadius: 2 }} />
                        <div style={{ fontSize: 11, color: '#6B6880' }}>{r.label}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>}

          </>} />

          {/* ── ASSIGNMENT TAB: Trip Assignment ── */}
          <ToolSlot toolKey="assignment" label="Assignment" active={mainTab === 'assignment'} dock={dockOf('assignment')} onDock={setToolDock} floatPos={toolFloats.assignment} setFloatPos={setToolFloatAt} rightEl={rightZoneEl} idx={5} render={() => <>

            {/* Trip Assignment */}
            {stepHeader(5, 'Trip Assignment', !!(roadNetwork && (modeShare || modeShareMethod === 'manual') && odFlows && (amTotal !== null || pmTotal !== null)), !!assignedNetwork)}
            {/* Prerequisite checklist — shown until everything needed for assignment is ready */}
            {(openStep === 5 || isDetached('assignment')) && !(roadNetwork && (modeShare || modeShareMethod === 'manual') && odFlows && (amTotal !== null || pmTotal !== null)) && prereqNotice([
              { done: amTotal !== null || pmTotal !== null, label: 'Trip generation entered', hint: 'Set trip rates or direct trips in project setup (arrivals + departures for at least one period)' },
              { done: !!odFlows, label: 'Trip distribution generated', hint: 'Distribution tab → select output areas → generate OD flows' },
              { done: !!(modeShare || modeShareMethod === 'manual'), label: 'Mode share set', hint: 'Distribution tab → mode share' },
              { done: !!roadNetwork, label: 'Road network loaded', hint: 'Network tab → draw study area → Load road network' },
            ], 'Finish these before assigning trips:')}
            {(openStep === 5 || isDetached('assignment')) && roadNetwork && (modeShare || modeShareMethod === 'manual') && odFlows && (amTotal !== null || pmTotal !== null) && <>
              <div style={{ marginBottom: 10 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 6 }}>Peak period to assign</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[
                    { key: 'am', label: 'AM', available: amTotal !== null },
                    { key: 'pm', label: 'PM', available: pmTotal !== null },
                    { key: 'both', label: 'Both', available: amTotal !== null && pmTotal !== null },
                  ].map(p => (
                    <button key={p.key} onClick={() => p.available && setAssignPeriod(p.key)}
                      style={{ flex: 1, padding: '7px 4px', background: assignPeriod === p.key ? '#3D1A6E' : p.available ? 'white' : '#F5F0FA', color: assignPeriod === p.key ? 'white' : p.available ? '#3D1A6E' : '#C4BDD4', border: `1.5px solid ${assignPeriod === p.key ? '#3D1A6E' : p.available ? '#3D1A6E' : '#EDE5F8'}`, borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', cursor: p.available ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
                      {p.label}
                    </button>
                  ))}
                </div>
                {totalPersonTrips !== null && modeShare && (() => {
                  const { arrV, depV } = getVehicleTripPreview();
                  const adj = getCarTripAdjustment();
                  const stdTotal = arrV + depV;
                  const ratio = stdTotal > 0 ? adj.adjusted / stdTotal : 1;
                  const arrAdj = Math.round(arrV * ratio), depAdj = Math.round(depV * ratio);
                  return (
                    <div style={{ fontSize: 11, color: '#6B6880', marginTop: 6, background: '#F5F0FA', padding: '8px 10px', borderRadius: 6 }}>
                      <div style={{ fontWeight: 700, color: '#3D1A6E', marginBottom: 4 }}>
                        {assignPeriod.toUpperCase()} peak — {adj.adjusted} car trips{adj.hasOverrides ? ` (${adj.standard} before mode overrides)` : ''}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#6B21A8', flexShrink: 0 }} />
                        <span>{arrAdj} arrivals (inbound)</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#0D9488', flexShrink: 0 }} />
                        <span>{depAdj} departures (outbound)</span>
                      </div>
                      {adj.hasOverrides && <div style={{ fontSize: 10, color: '#0D9488', marginTop: 4, fontWeight: 600 }}>{adj.reassigned} trips reassigned to other modes</div>}
                    </div>
                  );
                })()}
              </div>
              {!siteAccessNode && <div style={{ fontSize: 11, color: '#6B6880', marginBottom: 8, background: '#FFF9E6', padding: '8px 10px', borderRadius: 6, border: '1px solid #FFD000' }}>⚠️ No site access point set — trips will route from pin location.</div>}
              <button onClick={assignTrips} disabled={assignLoading || !totalPersonTrips} style={{ width: '100%', padding: '11px 12px', background: (assignLoading || !totalPersonTrips) ? '#EDE5F8' : '#3D1A6E', color: (assignLoading || !totalPersonTrips) ? '#7B4DBF' : 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: (assignLoading || !totalPersonTrips) ? 'not-allowed' : 'pointer', fontFamily: 'inherit', marginBottom: 8 }}>
                {assignLoading ? 'Assigning trips...' : '🚦 Assign Trips to Network'}
            </button>
            {assignLoading && <LoadingBar label="Routing trips via HERE — this may take a minute..." />}
            {assignedNetwork && (
                <>
                  <div style={{ fontSize: 12, color: '#0D9488', background: '#F0FDF9', padding: '8px 10px', borderRadius: 6, marginBottom: 8 }}>
                    ✅ {assignedNetwork.total_assigned} vehicle trips assigned across {assignedNetwork.route_count || assignedNetwork.edge_count} routes
                    {assignedNetwork.arr_assigned !== undefined && (
                      <div style={{ fontSize: 11, color: '#6B6880', marginTop: 3 }}>
                        {assignedNetwork.arr_assigned} arrivals · {assignedNetwork.dep_assigned} departures
                      </div>
                    )}
                  </div>
                  {/* By-purpose split of the assigned vehicle trips */}
                  {(() => {
                    const feats = assignedNetwork?.routes?.features || [];
                    const isSchool = d => typeof d === 'string' && d.startsWith('SCH');
                    const sVeh = feats.filter(f => isSchool(f.properties.destination)).reduce((a, f) => a + (f.properties.trips || 0), 0);
                    const cVeh = feats.filter(f => !isSchool(f.properties.destination)).reduce((a, f) => a + (f.properties.trips || 0), 0);
                    const tot = sVeh + cVeh;
                    if (tot === 0) return null;
                    const nSchools = educationSchools?.schools?.filter(s => (s.trips || 0) > 0).length || 0;
                    return (
                      <div style={{ background: '#F9F7FD', border: '1px solid #EDE5F8', borderRadius: 6, padding: '8px 10px', marginBottom: 8 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 6 }}>Assigned by purpose</div>
                        <div style={{ display: 'flex', height: 14, borderRadius: 4, overflow: 'hidden', marginBottom: 6 }}>
                          {cVeh > 0 && <div title={`Employment + Other ${cVeh}`} style={{ width: `${(cVeh / tot) * 100}%`, background: '#3D1A6E' }} />}
                          {sVeh > 0 && <div title={`Education ${sVeh}`} style={{ width: `${(sVeh / tot) * 100}%`, background: '#0D9488' }} />}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11 }}>
                          <span><span style={{ color: '#3D1A6E', fontWeight: 800 }}>{cVeh}</span> <span style={{ color: '#6B6880' }}>emp+other → zones</span></span>
                          <span><span style={{ color: '#0D9488', fontWeight: 800 }}>{sVeh}</span> <span style={{ color: '#6B6880' }}>education → {nSchools} schools</span></span>
                        </div>
                      </div>
                    );
                  })()}
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    <button onClick={() => setShowAggregated(false)}
                      style={{ flex: 1, padding: '6px', background: !showAggregated ? '#3D1A6E' : 'white', color: !showAggregated ? 'white' : '#3D1A6E', border: '1.5px solid #3D1A6E', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Individual Routes
                    </button>
                    <button onClick={() => setShowAggregated(true)}
                      style={{ flex: 1, padding: '6px', background: showAggregated ? '#3D1A6E' : 'white', color: showAggregated ? 'white' : '#3D1A6E', border: '1.5px solid #3D1A6E', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Link Totals
                    </button>
                  </div>
                  {!showAggregated && (() => {
                    const routeFeatures = assignedNetwork?.routes?.features || [];
                    // Build unique destinations sorted by trips descending
                    const destMap = {};
                    routeFeatures.forEach(f => {
                      const dest = f.properties.destination || '';
                      if (!destMap[dest]) {
                        const flow = odFlows?.flows?.find(fl => fl.destination === dest);
                        destMap[dest] = { dest, name: flow?.name || dest, trips: 0, arrivals: 0, departures: 0, hasArr: false, hasDep: false, edited: false, arrFeat: null, depFeat: null };
                      }
                      destMap[dest].trips += (f.properties.trips || 0);
                      if (f.properties.direction === 'arrivals') { destMap[dest].arrivals += (f.properties.trips || 0); destMap[dest].hasArr = true; destMap[dest].arrFeat = f; }
                      else { destMap[dest].departures += (f.properties.trips || 0); destMap[dest].hasDep = true; destMap[dest].depFeat = f; }
                      if (f.properties._edited) destMap[dest].edited = true;
                    });
                    const dests = Object.values(destMap).sort((a, b) => b.trips - a.trips);
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                          <div style={{ width: 24, height: 3, background: '#6B21A8', borderRadius: 2 }} />
                          <div style={{ fontSize: 11, color: '#6B6880' }}>Arrivals</div>
                          <div style={{ width: 24, height: 3, background: '#0D9488', borderRadius: 2, marginLeft: 8 }} />
                          <div style={{ fontSize: 11, color: '#6B6880' }}>Departures</div>
                        </div>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#C4BDD4', marginTop: 4, marginBottom: 2 }}>Filter by destination</div>
                        {selectedRouteDestination && (
                          <button onClick={() => { setSelectedRouteDestination(null); setEditingRoute(null); }}
                            style={{ width: '100%', padding: '5px 8px', background: '#EDE5F8', color: '#3D1A6E', border: '1.5px solid #3D1A6E', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                            ✕ Show all routes
                          </button>
                        )}
                        <div style={{ fontSize: 10, color: '#6B6880', lineHeight: 1.4, marginBottom: 2 }}>Select a destination to edit its route path or trip count.</div>
                        <div style={{ maxHeight: 220, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {dests.map(({ dest, name, arrivals, departures, hasArr, hasDep, edited, arrFeat, depFeat }) => {
                            const isSelected = selectedRouteDestination === dest;
                            return (
                              <div key={dest}
                                style={{ padding: '6px 8px', borderRadius: 6, background: isSelected ? '#3D1A6E' : '#F9F7FD', border: `1.5px solid ${isSelected ? '#3D1A6E' : '#EDE5F8'}`, cursor: 'pointer', transition: 'all 0.15s' }}>
                                <div onClick={() => { setSelectedRouteDestination(isSelected ? null : dest); setEditingRoute(null); }}
                                  onMouseEnter={e => { if (!isSelected) e.currentTarget.parentElement.style.background = '#EDE5F8'; }}
                                  onMouseLeave={e => { if (!isSelected) e.currentTarget.parentElement.style.background = '#F9F7FD'; }}>
                                  <div style={{ fontSize: 11, fontWeight: 700, color: isSelected ? 'white' : '#1C1C2E', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {name}{edited && <span style={{ fontSize: 8, fontWeight: 800, letterSpacing: '0.04em', color: isSelected ? '#3D1A6E' : 'white', background: isSelected ? '#CCFBF1' : '#0D9488', borderRadius: 4, padding: '1px 5px', marginLeft: 6, verticalAlign: 'middle' }}>EDITED</span>}
                                  </div>
                                  <div style={{ display: 'flex', gap: 6 }}>
                                    {arrivals > 0 && <span style={{ fontSize: 10, color: isSelected ? '#EDE5F8' : '#6B21A8', fontWeight: 600 }}>▲ {arrivals} arr</span>}
                                    {departures > 0 && <span style={{ fontSize: 10, color: isSelected ? '#CCFBF1' : '#0D9488', fontWeight: 600 }}>▼ {departures} dep</span>}
                                  </div>
                                </div>
                                {isSelected && (
                                  <div onClick={e => e.stopPropagation()} style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    {[{ dir: 'arrivals', label: 'Arrivals', val: arrivals, show: hasArr, color: '#6B21A8', feat: arrFeat },
                                      { dir: 'departures', label: 'Departures', val: departures, show: hasDep, color: '#0D9488', feat: depFeat }]
                                      .filter(r => r.show).map(r => {
                                        const isEditing = editingRoute && editingRoute.destination === dest && editingRoute.direction === r.dir;
                                        const rEdited = r.feat && r.feat.properties._edited;
                                        return (
                                          <div key={r.dir} style={{ display: 'flex', flexDirection: 'column', gap: 6, background: 'white', borderRadius: 6, padding: '6px 8px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                              <span style={{ fontSize: 10, fontWeight: 700, color: r.color, width: 60 }}>{r.label}</span>
                                              <input type="number" min="0" value={r.val}
                                                onChange={e => updateRouteFeature(dest, r.dir, { trips: parseInt(e.target.value, 10) || 0 })}
                                                style={{ width: 58, border: '1.5px solid #EDE5F8', borderRadius: 5, padding: '4px 6px', fontSize: 11, fontFamily: 'inherit', textAlign: 'center', color: '#1C1C2E' }} />
                                              <span style={{ fontSize: 9, color: '#C4BDD4' }}>trips</span>
                                              <button onClick={() => setEditingRoute(isEditing ? null : { destination: dest, direction: r.dir })}
                                                style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: isEditing ? 'white' : '#3D1A6E', background: isEditing ? '#0D9488' : '#EDE5F8', border: 'none', borderRadius: 5, padding: '5px 9px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                                                {isEditing ? '✓ Done' : '✎ Edit path'}
                                              </button>
                                            </div>
                                            {rEdited && (
                                              <>
                                                <textarea value={r.feat.properties.note || ''} placeholder="Justification for this edit (e.g. rerouted via proposed 2045 link)…"
                                                  onChange={e => updateRouteFeature(dest, r.dir, { note: e.target.value })}
                                                  rows={2} style={{ width: '100%', boxSizing: 'border-box', border: '1.5px solid #EDE5F8', borderRadius: 5, padding: '5px 7px', fontSize: 10.5, fontFamily: 'inherit', color: '#1C1C2E', resize: 'vertical' }} />
                                                <button onClick={() => { restoreRouteFeature(dest, r.dir); if (isEditing) setEditingRoute(null); }}
                                                  style={{ alignSelf: 'flex-start', fontSize: 10, fontWeight: 700, color: '#993C1D', background: '#FAECE7', border: 'none', borderRadius: 5, padding: '5px 9px', cursor: 'pointer', fontFamily: 'inherit' }}>
                                                  ↺ Restore to default
                                                </button>
                                              </>
                                            )}
                                          </div>
                                        );
                                      })}
                                    {editingRoute && editingRoute.destination === dest && (
                                      <div style={{ fontSize: 10, color: isSelected ? '#C4BDD4' : '#6B6880', lineHeight: 1.4 }}>Drag a white handle to move a point · drag a purple midpoint to add one · right-click a handle to delete.</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                  
                {showAggregated && <>
                    {(assignedNetwork?.routes?.features || []).some(f => f.properties._edited) && (
                      <div style={{ fontSize: 10, color: '#993C1D', background: '#FAECE7', borderRadius: 6, padding: '7px 9px', marginBottom: 8, lineHeight: 1.4 }}>
                        ⚠️ Link totals reflect the original routing and don't yet include your manual route edits. Use the route list for the up-to-date per-route figures.
                      </div>
                    )}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                      <div style={{ fontSize: 10, color: '#C4BDD4' }}>Fewer trips</div>
                      <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'linear-gradient(to right, #EDE5F8, #3D1A6E)' }} />
                      <div style={{ fontSize: 10, color: '#C4BDD4' }}>More trips</div>
                    </div>
                    <div style={{ borderTop: '1px solid #EDE5F8', paddingTop: 10 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 6 }}>Road Labels</div>
                      {!labelMoveMode ? (
                        <button onClick={() => { setLabelMoveMode(true); setLayerVisibility(prev => ({ ...prev, roadLabels: true })); }}
                          style={{ width: '100%', padding: '8px 10px', background: 'white', color: '#3D1A6E', border: '1.5px solid #3D1A6E', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                          🏷️ {Object.keys(roadLabelPositions).length > 0 ? `Edit labels (${Object.keys(roadLabelPositions).length} placed)` : 'Add road labels'}
                        </button>
                      ) : (
                        <div>
                          <div style={{ fontSize: 11, color: '#3D1A6E', background: '#F5F0FA', padding: '8px 10px', borderRadius: 6, marginBottom: 6 }}>
                            Click map to place a label · Right-click a label to remove it
                          </div>
                          <button onClick={() => setLabelMoveMode(false)}
                            style={{ width: '100%', padding: '8px 10px', background: '#3D1A6E', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                            ✅ Done placing labels
                          </button>
                        </div>
                      )}
                      {Object.keys(roadLabelPositions).length > 0 && !labelMoveMode && (
                        <button onClick={() => setRoadLabelPositions({})}
                          style={{ width: '100%', padding: '6px 10px', background: 'white', color: '#C4BDD4', border: '1.5px solid #E5E7EB', borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginTop: 6 }}>
                          Clear all labels
                        </button>
                      )}
                    </div>
                  </>}
                </>
              )}
            </>}

            {/* Route export */}
            {assignedNetwork && (
              <>
                <div style={{ borderTop: '1px solid #EDE5F8', margin: '10px 0 8px' }} />
                <button onClick={() => {
                  const res = downloadRoutesShapefile(assignedNetwork, `${projectName || 'project'}_assigned_routes`);
                  if (!res.ok) alert('No assigned routes to export yet — run trip assignment first.');
                }} style={{ width: '100%', padding: '9px 12px', background: 'white', color: '#0D9488', border: '1.5px solid #0D9488', borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' }}>⬇ Routes → Shapefile</button>
              </>
            )}

            {/* Clear all */}
            {(selectedOAs.length > 0 || pinLocation) && (
              <>
                <div style={{ borderTop: '1px solid #EDE5F8', margin: '10px 0 8px' }} />
                <button onClick={clearAll} style={{ width: '100%', padding: '9px 12px', background: 'white', color: '#C4BDD4', border: '1.5px solid #E5E7EB', borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit' }}>Clear all</button>
              </>
            )}

          </>} />

          </div>
        </div>
        ); })()}

        {/* ── Right dock zone — receives a tool docked to the right of the map
            (flex order:3 places it after the map regardless of DOM order) ── */}
        {(() => { const showRight = dockOf(mainTab) === 'right'; const W = mainTab === 'activetravel' ? 300 : 280; return (
          <div ref={rightZoneRef} style={{ width: showRight ? W : 0, minWidth: showRight ? W : 0, height: '100%', background: 'white', borderLeft: showRight ? '1.5px solid #EDE5F8' : 'none', overflowY: 'auto', padding: showRight ? '12px 14px' : 0, order: 3, zIndex: 100 }} />
        ); })()}

        {/* ── Map + results dock column ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {/* ── Layer Toggle Panel ── */}
          <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, background: 'white', borderRadius: 8, boxShadow: '0 2px 12px rgba(61,26,110,0.15)', border: '1.5px solid #EDE5F8', minWidth: 180, overflow: 'hidden' }}>
            <div style={{ background: '#3D1A6E', padding: '7px 12px', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'white' }}>Layers</div>
            {[
              { key: 'studyArea', label: '📐 Study area', available: studyPolygon.length >= 3 },
              { key: 'destinationZones', label: '🗺️ Destination zones', available: !!destLayer },
              { key: 'assignedZones', label: `✅ Assigned zones${routedZoneCount ? ` (${routedZoneCount} of ${totalZoneCount})` : ''}`, available: !!destLayer && routedZoneCount > 0 },
              { key: 'destCentroids', label: '📍 Destination centroids', available: !!destLayer && Object.keys(destCentroids).length > 0 },
              { key: 'outputAreas', label: '⬡ Output areas', available: !!outputAreas && !destLayer },
              { key: 'roadNetwork', label: '🛣️ Road network', available: !!roadNetwork || !!assignedNetwork },
              { key: 'arrivals', label: '🟣 Arrivals routes', available: !!assignedNetwork && !showAggregated },
              { key: 'departures', label: '🟢 Departures routes', available: !!assignedNetwork && !showAggregated },
              { key: 'educationSchools', label: `🏫 Education schools${educationSchools?.schools?.length ? ` (${educationSchools.schools.length})` : ''}`, available: !!educationSchools?.schools?.length },
              { key: 'siteAccess', label: '📍 Site access', available: !!siteAccessNode },
              { key: 'railStations', label: '🚉 Rail stations', available: !!railStations },
              { key: 'busStops', label: '🚌 Bus stops', available: !!busStops },
              { key: 'roadLabels', label: '🏷️ Road labels', available: !!assignedNetwork && showAggregated },
            ].map(({ key, label, available }) => (
              <div key={key} onClick={() => available && toggleLayer(key)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 12px', borderBottom: '1px solid #F5F0FA', cursor: available ? 'pointer' : 'default', opacity: available ? 1 : 0.35, background: 'white' }}
                onMouseEnter={e => { if (available) e.currentTarget.style.background = '#F9F7FD'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'white'; }}>
                <span style={{ fontSize: 11, color: '#1C1C2E', fontWeight: 500 }}>{label}</span>
                <div style={{ width: 32, height: 18, borderRadius: 9, background: layerVisibility[key] && available ? '#3D1A6E' : '#C4BDD4', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
                  <div style={{ position: 'absolute', top: 2, left: layerVisibility[key] && available ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                </div>
              </div>
            ))}
          </div>

          

          {pendingJunctionNodeId && (
            <div style={{ position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, background: '#1E1A2E', border: '1px solid #3D1A6E', borderRadius: 10, padding: '12px 20px', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, minWidth: 340 }}>
              {pendingGateConfirm ? (
                <>
                  <div style={{ fontSize: 13 }}>🚧 Confirm this gate</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <label style={{ fontSize: 11, color: '#C4BDD4' }}>Arm</label>
                    <input value={pendingGateConfirm.letter}
                      onChange={e => setPendingGateConfirm(prev => ({ ...prev, letter: e.target.value.toUpperCase().slice(0, 3) }))}
                      style={{ width: 48, padding: '4px 6px', borderRadius: 4, border: '1px solid #3D1A6E', textAlign: 'center', fontWeight: 700, fontFamily: 'inherit' }} />
                    <label style={{ fontSize: 11, color: '#C4BDD4', marginLeft: 8 }}>Direction</label>
                    <select value={pendingGateConfirm.direction}
                      onChange={e => setPendingGateConfirm(prev => ({ ...prev, direction: e.target.value }))}
                      style={{ padding: '4px 6px', borderRadius: 4, border: '1px solid #3D1A6E', fontFamily: 'inherit' }}>
                      <option value="two-way">Two-way</option>
                      <option value="entry">Entry only</option>
                      <option value="exit">Exit only</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <label style={{ fontSize: 11, color: '#C4BDD4' }}>Road</label>
                    <input value={pendingGateConfirm.roadName || ''} placeholder="Nearest road name (or type)"
                      onChange={e => setPendingGateConfirm(prev => ({ ...prev, roadName: e.target.value }))}
                      style={{ width: 220, padding: '4px 6px', borderRadius: 4, border: '1px solid #3D1A6E', fontFamily: 'inherit' }} />
                  </div>
                  <div style={{ fontSize: 10, color: '#8B7FA8' }}>Press Enter to confirm · Esc to redraw</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    <button onClick={confirmPendingGate} style={{ background: '#3D1A6E', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', cursor: 'pointer', fontSize: 12 }}>✓ OK</button>
                    <button onClick={redrawPendingGate} style={{ background: 'transparent', color: '#C4BDD4', border: '1px solid #3D1A6E', borderRadius: 6, padding: '6px 16px', cursor: 'pointer', fontSize: 12 }}>Redraw</button>
                  </div>
                </>
              ) : gateDrawStart ? (
                <div style={{ fontSize: 13 }}>🎯 Click again on the far side of the road to complete the gate</div>
              ) : (
                <>
                  <div style={{ fontSize: 13 }}>🚧 Draw gate {pendingGates.length + 1} of {junctionNumArms} — click one side of the road, then the other</div>
                  <div style={{ fontSize: 12, color: '#C4BDD4' }}>{pendingGates.length} gate{pendingGates.length === 1 ? '' : 's'} placed so far</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                    {pendingGates.length > 0 && (
                      <button onClick={finishGateDrawing} style={{ background: '#0D9488', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 16px', cursor: 'pointer', fontSize: 12 }}>✓ Finish ({pendingGates.length} gate{pendingGates.length === 1 ? '' : 's'})</button>
                    )}
                    <button onClick={cancelGateDrawing} style={{ background: 'transparent', color: '#C4BDD4', border: '1px solid #3D1A6E', borderRadius: 6, padding: '6px 16px', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
                  </div>
                </>
              )}
            </div>
          )}

          <MapContainer center={[51.5, -1.5]} zoom={7} style={{ width: '100%', height: '100%', cursor: pinMode || accessMode ? 'crosshair' : labelMoveMode ? 'crosshair' : 'grab' }}
            whenCreated={(map) => {
              map.on('zoomend', () => setZoom(map.getZoom()));
            }}>
            <TileLayer url={"https://api.mapbox.com/styles/v1/mapbox/light-v11/tiles/{z}/{x}/{y}?access_token=" + MAPBOX_TOKEN} attribution="Mapbox OpenStreetMap" tileSize={512} zoomOffset={-1} />
            <MapRefSetter />
            <LabelPlacer />
            <LoadOAs onLoad={handleLoad} locked={!!pinLocation} />
            <GateDrawer
              active={!!pendingJunctionNodeId && !pendingGateConfirm}
              hasDraftStart={!!gateDrawStart}
              onFirstClick={(lat, lng) => setGateDrawStart({ lat, lng })}
              onSecondClick={handleGateSecondClick}
              onMouseMove={(lat, lng) => setGateDraftPoint({ lat, lng })}
            />
            {pendingJunctionNodeId && (
              <GatesOverlay gates={pendingGates} draftStart={gateDrawStart} draftEnd={gateDraftPoint} />
            )}
            <PinPlacer pinMode={pinMode} onPin={handlePinDrop} />
            <AccessPlacer accessMode={accessMode} onAccess={handleAccessMapClick} />
            
            <FlyTo target={flyTarget} />
            <PolygonDrawer
              drawing={drawingPolygon}
              onPoint={(pt) => {
                if (studyPolygon.length >= 3) {
                  const first = studyPolygon[0];
                  const dist = Math.sqrt((pt[0]-first[0])**2 + (pt[1]-first[1])**2);
                  if (dist < 0.001) { setDrawingPolygon(false); return; }
                }
                setStudyPolygon(prev => [...prev, pt]);
              }}
              onMouseMove={(pt) => setPolygonPreviewPoint(pt)}
            />
            <PolygonDrawer
              drawing={drawingSiteBoundary}
              onPoint={(pt) => {
                if (siteBoundary.length >= 3) {
                  const first = siteBoundary[0];
                  const dist = Math.sqrt((pt[0]-first[0])**2 + (pt[1]-first[1])**2);
                  if (dist < 0.001) { setDrawingSiteBoundary(false); return; }
                }
                setSiteBoundary(prev => [...prev, pt]);
              }}
              onMouseMove={(pt) => setSiteBoundaryPreviewPoint(pt)}
            />
            {/* Site boundary — red */}
            {siteBoundary.length >= 3 && !drawingSiteBoundary && (
              <GeoJSON key={'siteboundary-' + siteBoundary.length} data={{
                type: 'Feature',
                geometry: { type: 'Polygon', coordinates: [[...siteBoundary.map(p => [p[1], p[0]]), [siteBoundary[0][1], siteBoundary[0][0]]]] }
              }} style={{ fillColor: '#E8001C', fillOpacity: 0.08, color: '#E8001C', weight: 2.5 }} />
            )}
            {drawingSiteBoundary && siteBoundary.length > 0 && (
              <GeoJSON key={'drawing-site-' + siteBoundary.length + JSON.stringify(siteBoundaryPreviewPoint)} data={{
                type: 'Feature',
                geometry: { type: 'LineString', coordinates: [...siteBoundary.map(p => [p[1], p[0]]), ...(siteBoundaryPreviewPoint ? [[siteBoundaryPreviewPoint[1], siteBoundaryPreviewPoint[0]]] : [])] }
              }} style={{ color: '#E8001C', weight: 2.5, dashArray: '6,4', fillOpacity: 0 }} />
            )}

            {studyPolygon.length >= 3 && !drawingPolygon && layerVisibility.studyArea && (
              <GeoJSON key={'polygon-' + studyPolygon.length} data={{
                type: 'Feature',
                geometry: { type: 'Polygon', coordinates: [[...studyPolygon.map(p => [p[1], p[0]]), [studyPolygon[0][1], studyPolygon[0][0]]]] }
              }} style={{ fillColor: '#3D1A6E', fillOpacity: 0.08, color: '#3D1A6E', weight: 2, dashArray: '6,4' }} />
            )}
            {drawingPolygon && studyPolygon.length > 0 && (
              <GeoJSON key={'drawing-' + studyPolygon.length + JSON.stringify(polygonPreviewPoint)} data={{
                type: 'Feature',
                geometry: { type: 'LineString', coordinates: [...studyPolygon.map(p => [p[1], p[0]]), ...(polygonPreviewPoint ? [[polygonPreviewPoint[1], polygonPreviewPoint[0]]] : [])] }
              }} style={{ color: '#3D1A6E', weight: 2, dashArray: '6,4', fillOpacity: 0 }} />
            )}
            {destLayer && destLayer.features && layerVisibility.destinationZones && (
              <GeoJSON key={'dest-' + destLayer.features.length + '-' + (modeOverrideEditingLad || '') + '-' + (modeOverrideEditingZone || '')} data={destLayer} style={styleDestZone} onEachFeature={onEachDestZone} />
            )}
            {/* Assigned zones — only the destination zones that received a routed car trip */}
            {destLayer && destLayer.features && layerVisibility.assignedZones && routedDestSet.size > 0 && (
              <GeoJSON
                key={'assignedzones-' + routedZoneCount + '-' + (assignedNetwork?.total_assigned || 0)}
                data={{ type: 'FeatureCollection', features: destLayer.features.filter(f => routedDestSet.has(f.properties.code)) }}
                style={() => ({ fillColor: '#0D9488', fillOpacity: 0.5, color: '#0F6E56', weight: 2 })}
                onEachFeature={(feature, layer) => layer.bindTooltip(`${feature.properties.name} — assigned`, { sticky: true })} />
            )}
            {/* Highlight the destination zone of the route selected in the assignment list */}
            {destLayer && destLayer.features && selectedRouteDestination && (() => {
              const f = destLayer.features.find(x => x.properties.code === selectedRouteDestination);
              if (!f) return null;
              return (
                <GeoJSON key={'selzone-' + selectedRouteDestination} data={f} interactive={false}
                  style={() => ({ fillColor: '#D85A30', fillOpacity: 0.35, color: '#D85A30', weight: 3.5, dashArray: '6,3' })} />
              );
            })()}
            {destLayer && layerVisibility.destCentroids && Object.entries(destCentroids).map(([code, c]) => {
              const flow = odFlows?.flows?.find(f => f.destination === code);
              return (
                <Marker key={'cen-' + code} position={[c.lat, c.lng]} draggable={true}
                  icon={L.divIcon({ className: '', iconSize: [14, 14], iconAnchor: [7, 7], html: `<div style="width:14px;height:14px;border-radius:50%;background:${c.edited ? '#E85D00' : '#0D9488'};border:2px solid white;box-shadow:0 0 0 1.5px ${c.edited ? '#E85D00' : '#0D9488'}"></div>` })}
                  eventHandlers={{ dragend: (e) => { const ll = e.target.getLatLng(); setDestCentroids(prev => ({ ...prev, [code]: { lat: ll.lat, lng: ll.lng, edited: true } })); } }}>
                  <Popup>
                    <div style={{ fontFamily: 'Arial', fontSize: 12 }}>
                      <strong style={{ color: '#3D1A6E' }}>{flow?.name || code}</strong><br />
                      <span style={{ color: '#6B6880', fontSize: 11 }}>Routing centroid{c.edited ? ' · moved' : ' · population-weighted'}</span><br />
                      <span style={{ color: '#6B6880', fontSize: 11 }}>Drag to reposition onto the road network</span>
                      {c.edited && <><br /><span onClick={() => fetchDestCentroids(odFlows.flows)} style={{ color: '#7B4DBF', fontSize: 11, cursor: 'pointer', fontWeight: 700 }}>↺ Reset all to default</span></>}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
            {!destLayer && outputAreas && outputAreas.features && outputAreas.features.length > 0 && layerVisibility.outputAreas && (
              <GeoJSON key={JSON.stringify(outputAreas.features.map(f => f.properties.OA11CD))} data={outputAreas} style={styleOA} onEachFeature={onEachOA} />
            )}
            {assignedNetwork && assignedNetwork.edges ? (
              <>
                {showAggregated ? (
                  assignedNetwork.aggregated && assignedNetwork.aggregated.features ? (
                    <GeoJSON key={'agg-' + assignedNetwork.total_assigned + (labelMoveMode ? 'label' : '') + showAggregated} data={{
                      ...assignedNetwork.aggregated,
                      features: assignedNetwork.aggregated.features
                    }}
                      style={feature => labelMoveMode
                        ? { ...styleAggregatedEdge(feature), cursor: 'crosshair' }
                        : styleAggregatedEdge(feature)}
                      onEachFeature={(feature, layer) => {
                        if (labelMoveMode) {
                          layer.on('click', (e) => {
                            L.DomEvent.stopPropagation(e);
                            const name = feature.properties.name;
                            const trips = feature.properties.trips || 0;
                            if (!name || name === 'Unnamed road' || trips < 5) return;
                            const maxTrips = assignedNetwork?.aggregated?.features
                              ? Math.max(...assignedNetwork.aggregated.features
                                  .filter(f => f.properties.name === name)
                                  .map(f => f.properties.trips || 0), 0)
                              : trips;
                            setRoadLabelPositions(prev => ({
                              ...prev,
                              [`${name}||${maxTrips}`]: { lat: e.latlng.lat, lng: e.latlng.lng, name, trips: maxTrips }
                            }));
                          });
                          return;
                        }
                        onEachAggregatedEdge(feature, layer);
                      }} />
                  ) : null
                ) : (
                  <>
                    {layerVisibility.roadNetwork && <GeoJSON key={'base-' + assignedNetwork.edge_count} data={assignedNetwork.edges} style={() => ({ color: '#E0DCF0', weight: 1, opacity: 0.6 })} />}
                    {assignedNetwork.routes && assignedNetwork.routes.features && assignedNetwork.routes.features.length > 0 && (
                      <GeoJSON key={'routes-' + assignedNetwork.total_assigned + '-' + routeEditVersion + layerVisibility.arrivals + layerVisibility.departures + (selectedRouteDestination || '')} data={{
                        ...assignedNetwork.routes,
                        features: assignedNetwork.routes.features.filter(f => {
                          if (selectedRouteDestination && f.properties.destination !== selectedRouteDestination) return false;
                          return f.properties.direction === 'arrivals' ? layerVisibility.arrivals : layerVisibility.departures;
                        })
                      }}
                        style={feature => styleRoute(feature)}
                        onEachFeature={(feature, layer) => onEachRoute(feature, layer)} />
                    )}
                    {/* Manual route editing — draggable vertex + insert handles on the selected route */}
                    {editingRoute && (() => {
                      const feats = assignedNetwork.routes?.features || [];
                      const idx = findRouteIndex(feats, editingRoute.destination, editingRoute.direction);
                      if (idx < 0) return null;
                      const coords = feats[idx].geometry.coordinates;
                      const dest = editingRoute.destination, dir = editingRoute.direction;
                      return (
                        <>
                          {midpoints(coords).map(m => (
                            <Marker key={'mid-' + m.insertAt + '-' + m.lng} position={[m.lat, m.lng]} icon={midVertexIcon} draggable={true}
                              eventHandlers={{ dragend: e => { const p = e.target.getLatLng(); updateRouteFeature(dest, dir, { coords: insertVertex(coords, m.insertAt, p.lng, p.lat) }); } }} />
                          ))}
                          {coords.map((c, i) => (
                            <Marker key={'vtx-' + i} position={[c[1], c[0]]} icon={vertexIcon} draggable={true}
                              eventHandlers={{
                                dragend: e => { const p = e.target.getLatLng(); updateRouteFeature(dest, dir, { coords: moveVertex(coords, i, p.lng, p.lat) }); },
                                contextmenu: () => updateRouteFeature(dest, dir, { coords: removeVertex(coords, i) }),
                              }} />
                          ))}
                        </>
                      );
                    })()}
                  </>
                )}
              </>
            ) : roadNetwork && roadNetwork.edges && layerVisibility.roadNetwork ? (
              <GeoJSON key={'roads-' + roadNetwork.edge_count} data={roadNetwork.edges} style={styleRoad} onEachFeature={onEachRoad} />
            ) : null}
            {siteAccessNode && layerVisibility.siteAccess && (
              <CircleMarker center={[siteAccessNode.lat, siteAccessNode.lng]} radius={12}
                pathOptions={{ color: '#0D9488', fillColor: '#0D9488', fillOpacity: 1, weight: 3 }} />
            )}
            {pinLocation && <Marker position={pinLocation} icon={redPin} />}

            {/* Education schools — gravity-distributed education trips, sized by
                trip count. Teal = the education purpose colour. */}
            {!!educationSchools?.schools?.length && layerVisibility.educationSchools && (() => {
              const maxTrips = Math.max(1, ...educationSchools.schools.map(s => s.trips || 0));
              const phaseLabel = { primary: 'Primary', secondary: 'Secondary', all_through: 'All-through' };
              // Teal = primary, purple = secondary, amber = all-through.
              const phaseColour = { primary: '#0D9488', secondary: '#7B4DBF', all_through: '#C2740E' };
              return educationSchools.schools.filter(s => (s.trips || 0) > 0).map(s => {
                const r = 4 + 12 * Math.sqrt((s.trips || 0) / maxTrips);
                const c = phaseColour[s.phase] || '#0D9488';
                return (
                  <CircleMarker key={'sch-' + s.urn} center={[s.lat, s.lng]} radius={r}
                    pathOptions={{ color: '#ffffff', weight: 1.5, fillColor: c, fillOpacity: 0.85 }}>
                    <Tooltip direction="top" offset={[0, -r]}>
                      <span style={{ fontWeight: 800, color: c }}>{s.name}</span><br />
                      <span style={{ color: '#6B6880', fontWeight: 600 }}>
                        {phaseLabel[s.phase] || s.phase} · {s.trips} edu trips · {(s.share * 100).toFixed(1)}% · {s.distance_km} km
                      </span>
                    </Tooltip>
                  </CircleMarker>
                );
              });
            })()}

            {/* Nearest rail stations (Public transport tab) — labelled map layer */}
            {railStations && layerVisibility.railStations && railStations.map((s, i) => {
              const near = i === 0;
              const w = near ? 32 : 26, h = near ? 42 : 34;
              const ring = near ? '#F5C24B' : '#ffffff';
              const icon = L.divIcon({
                className: '',
                iconSize: [w, h],
                iconAnchor: [w / 2, h],
                html: `<svg width="${w}" height="${h}" viewBox="0 0 26 34" xmlns="http://www.w3.org/2000/svg">`
                  + `<path d="M13 0.9C6.1 0.9 0.8 6.2 0.8 13c0 8.7 12.2 20 12.2 20s12.2-11.3 12.2-20C25.2 6.2 19.9 0.9 13 0.9z" fill="#0D9488" stroke="${ring}" stroke-width="1.8"/>`
                  + `<circle cx="13" cy="13" r="7.6" fill="#ffffff"/>`
                  + `<rect x="8.6" y="6.3" width="8.8" height="9.6" rx="2.6" fill="#0D9488"/>`
                  + `<rect x="9.9" y="7.7" width="6.2" height="2.7" rx="1" fill="#ffffff"/>`
                  + `<circle cx="10.9" cy="13.1" r="1.05" fill="#ffffff"/><circle cx="15.1" cy="13.1" r="1.05" fill="#ffffff"/>`
                  + `<rect x="10.3" y="15.7" width="1.5" height="2.3" fill="#0D9488"/><rect x="14.2" y="15.7" width="1.5" height="2.3" fill="#0D9488"/>`
                  + `</svg>`,
              });
              return (
                <Marker key={'rail-' + s.atco} position={[s.lat, s.lng]} icon={icon} zIndexOffset={near ? 1000 : 0}>
                  <Tooltip permanent direction="right" offset={[6, -Math.round(h * 0.55)]}>
                    <span style={{ fontWeight: near ? 800 : 600, color: '#0F6E56' }}>{s.name}</span>
                    <span style={{ color: '#6B6880', fontWeight: 600 }}> · {s.distance_km} km</span>
                  </Tooltip>
                </Marker>
              );
            })}

            {/* Nearest bus stops (Public transport tab) — compact map layer, name on hover */}
            {busStops && layerVisibility.busStops && busStops.map((s, i) => {
              const near = i === 0;
              const d = near ? 22 : 18;
              const ring = near ? '#F5C24B' : '#ffffff';
              const icon = L.divIcon({
                className: '',
                iconSize: [d, d],
                iconAnchor: [d / 2, d / 2],
                html: `<svg width="${d}" height="${d}" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">`
                  + `<circle cx="10" cy="10" r="8.6" fill="#2563EB" stroke="${ring}" stroke-width="1.6"/>`
                  + `<rect x="6" y="5.3" width="8" height="7.4" rx="1.6" fill="#ffffff"/>`
                  + `<rect x="6.9" y="6.4" width="6.2" height="2.3" rx="0.6" fill="#2563EB"/>`
                  + `<circle cx="7.9" cy="13.2" r="1" fill="#ffffff"/><circle cx="12.1" cy="13.2" r="1" fill="#ffffff"/>`
                  + `</svg>`,
              });
              return (
                <Marker key={'bus-' + (s.stop_id || s.atco)} position={[s.lat, s.lng]} icon={icon} zIndexOffset={near ? 900 : 0}>
                  <Tooltip direction="top" offset={[0, -d / 2]}>
                    <span style={{ fontWeight: 600, color: '#1E3A8A' }}>🚌 {s.name}</span>
                    {typeof s.am_total === 'number'
                      ? <span style={{ color: '#6B6880', fontWeight: 600 }}> · {s.am_total}/{s.pm_total} buses/hr</span>
                      : <span style={{ color: '#6B6880', fontWeight: 600 }}> · {s.distance_km} km</span>}
                  </Tooltip>
                </Marker>
              );
            })}

            {/* Clicked bus route polyline (GTFS shape) */}
            {busRoute && busRoute.points && busRoute.points.length > 1 && (
              <Polyline positions={busRoute.points.map(p => [p[1], p[0]])} pathOptions={{ color: '#2563EB', weight: 4, opacity: 0.85, dashArray: (busRoute.synthetic && !busRoute.edited) ? '7 6' : null }} />
            )}
            {busRoute && busRouteEditing && busRoute.points && busRoute.points.length > 0 && (() => {
              const coords = busRoute.points; // [lng, lat]
              const update = nc => setBusRoute(prev => ({ ...prev, points: nc, edited: true }));
              return (
                <>
                  {midpoints(coords).map(m => (
                    <Marker key={'busmid-' + m.insertAt + '-' + m.lng} position={[m.lat, m.lng]} icon={midVertexIcon} draggable={true}
                      eventHandlers={{ dragend: e => { const p = e.target.getLatLng(); update(insertVertex(coords, m.insertAt, p.lng, p.lat)); } }} />
                  ))}
                  {coords.map((c, i) => (
                    <Marker key={'busvtx-' + i} position={[c[1], c[0]]} icon={vertexIcon} draggable={true}
                      eventHandlers={{
                        dragend: e => { const p = e.target.getLatLng(); update(moveVertex(coords, i, p.lng, p.lat)); },
                        contextmenu: () => update(removeVertex(coords, i)),
                      }} />
                  ))}
                </>
              );
            })()}

            {/* Active travel layers — shown when on active travel tab */}
            {mainTab === 'activetravel' && atResults && <>
              {atLayerVisibility.isochrone && Object.entries(atResults.isochroneResults).map(([mode, isoByMinutes]) => {
                const color = mode === 'walking' ? '#3D1A6E' : '#0D9488';
                const entries = Object.entries(isoByMinutes).sort((a, b) => b[0] - a[0]); // largest first
                const count = entries.length;
                return entries.map(([mins, iso], idx) => (
                  iso?.geometry && (
                    <GeoJSON key={`iso-${mode}-${mins}`} data={iso}
                      style={{
                        fillColor: color,
                        fillOpacity: count === 1 ? 0.1 : 0.07 + (idx / (count - 1)) * 0.13,
                        color,
                        weight: 1.5,
                        dashArray: '6,4',
                        opacity: 0.7,
                      }} />
                  )
                ));
              })}
              {atLayerVisibility.routes && Object.entries(atResults.routeResults).map(([mode, routeData]) => {
                const color = mode === 'walking' ? '#3D1A6E' : '#0D9488';
                if (atShowAggregated) {
                  const features = routeData?.aggregated?.features || [];
                  const maxCount = Math.max(...features.map(f => f.properties.count || 1), 1);
                  return features.length > 0 ? (
                    <GeoJSON key={`agg-${mode}`} data={routeData.aggregated}
                      style={f => ({ color, weight: 1 + ((f.properties.count || 1) / maxCount) * 6, opacity: 0.7 + ((f.properties.count || 1) / maxCount) * 0.3 })} />
                  ) : null;
                } else {
                  return routeData?.routes?.features?.length > 0 ? (
                    <GeoJSON key={`routes-${mode}`} data={routeData.routes}
                      style={{ color, weight: 2, opacity: 0.7 }}
                      onEachFeature={(feature, layer) => {
                        layer.bindTooltip(`<div style="font-family:Arial;font-size:12px;padding:4px 8px;"><strong>${feature.properties.name}</strong><br/>${mode === 'walking' ? '🚶' : '🚴'} ${feature.properties.duration_min} min · ${Math.round(feature.properties.distance_m)}m</div>`, { sticky: true });
                      }} />
                  ) : null;
                }
              })}
              {atLayerVisibility.destinations && (() => {
                const typeColors = { school: '#3D1A6E', hospital: '#DC2626', gp: '#EA580C', pharmacy: '#7C3AED', supermarket: '#0284C7', park: '#16A34A', leisure: '#0D9488', train_station: '#1D4ED8', bus_stop: '#92400E' };
                const allDests = Object.values(atResults.destinationResults).flat();
                const uniqueDests = [...new Map(allDests.map(d => [`${d.lat},${d.lng}`, d])).values()];
                return uniqueDests.map((dest, i) => (
                  <CircleMarker key={i} center={[dest.lat, dest.lng]} radius={8}
                    pathOptions={{ color: typeColors[dest.type] || '#666', fillColor: typeColors[dest.type] || '#666', fillOpacity: 0.9, weight: 2 }}>
                    <Popup>
                      <div style={{ fontFamily: 'Arial', fontSize: 12, minWidth: 150 }}>
                        <strong>{dest.name}</strong><br/>
                        <span style={{ color: '#6B6880', fontSize: 11 }}>{dest.description}</span>
                      </div>
                    </Popup>
                  </CircleMarker>
                ));
              })()}
            </>}

          </MapContainer>

          {/* Active travel legend on map */}
          {mainTab === 'activetravel' && atResults && (
            <div style={{ position: 'absolute', bottom: 16, right: 16, background: 'white', borderRadius: 8, padding: '10px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', fontSize: 11, zIndex: 1000 }}>
              <div style={{ fontWeight: 700, color: '#1C1C2E', marginBottom: 6 }}>Legend</div>
              {(atResults.mode === 'walking' || atResults.mode === 'both') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <div style={{ width: 20, height: 3, background: '#3D1A6E', borderRadius: 2 }} />
                  <span style={{ color: '#6B6880' }}>Walking isochrone</span>
                </div>
              )}
              {(atResults.mode === 'cycling' || atResults.mode === 'both') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                  <div style={{ width: 20, height: 3, background: '#0D9488', borderRadius: 2 }} />
                  <span style={{ color: '#6B6880' }}>Cycling isochrone</span>
                </div>
              )}
              <div style={{ fontSize: 10, color: '#C4BDD4', marginTop: 4 }}>© Crown copyright OS 2026</div>
            </div>
          )}
        </div>

        {/* ── Results dock ── */}
        <div style={{ flexShrink: 0, borderTop: '1.5px solid #EDE5F8', background: 'white' }}>
          <div onClick={() => setDockOpen(o => !o)} style={{ height: 34, display: 'flex', alignItems: 'center', gap: 10, padding: '0 14px', cursor: 'pointer', userSelect: 'none' }}>
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#3D1A6E' }}>Results</span>
            <span style={{ fontSize: 11, color: '#6B6880' }}>Trip totals</span>
            <div style={{ flex: 1 }} />
            <button onClick={(e) => { e.stopPropagation(); setView('results'); }}
              style={{ background: 'none', border: 'none', color: '#7B4DBF', fontSize: 11, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>Open full results ↗</button>
            <span style={{ color: '#7B4DBF', fontSize: 12, display: 'inline-block', transform: dockOpen ? 'none' : 'rotate(180deg)' }}>▾</span>
          </div>
          {dockOpen && (
            <div style={{ padding: '0 14px 12px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {(() => {
                const activeSc = scenarios.find(sc => sc.id === activeScenarioId) || scenarios[0];
                const periods = timePeriods.length > 0 ? timePeriods : [];
                if (!activeSc || periods.length === 0) {
                  return <div style={{ fontSize: 12, color: '#6B6880' }}>Set up scenarios and time periods to see trip totals here.</div>;
                }
                const metric = (k, v, sub) => (
                  <div key={k} style={{ background: '#F5F0FA', borderRadius: 9, padding: '9px 13px', minWidth: 120 }}>
                    <div style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#7B4DBF' }}>{k}</div>
                    <div style={{ fontSize: 19, fontWeight: 800, color: '#3D1A6E', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>{v}{sub && <span style={{ fontSize: 10, fontWeight: 700, color: '#6B6880' }}> {sub}</span>}</div>
                  </div>
                );
                return periods.map(tp => {
                  const t = periodTotals(activeSc, tp, scenarios, devSize);
                  return metric(tp.name, t.total, `(${t.arr} arr / ${t.dep} dep)`);
                });
              })()}
            </div>
          )}
        </div>

        </div>

      </div>

      {/* Project Details Modal */}
      {showProjectDetailsModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 10, width: 440, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#3D1A6E', marginBottom: 6, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Project Details</div>
            <div style={{ fontSize: 12, color: '#6B6880', marginBottom: 20 }}>Edit project information for reports and exports.</div>

            {[
              { label: 'Project Name', key: 'projectName', set: setProjectName, placeholder: 'e.g. Bracknell Housing Development' },
              { label: 'Project Number', key: 'projectNumber', set: setProjectNumber, placeholder: 'e.g. 2024-001' },
              { label: 'Client', key: 'projectClient', set: setProjectClient, placeholder: 'e.g. Acme Developments Ltd' },
              { label: 'Prepared By', key: 'projectPreparedBy', set: setProjectPreparedBy, placeholder: 'e.g. R. Lewis' },
              { label: 'Checked By', key: 'projectCheckedBy', set: setProjectCheckedBy, placeholder: 'e.g. J. Smith' },
            ].map(({ label, key, set, placeholder }) => (
              <div key={key} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 4 }}>{label}</label>
                <input
                  type="text"
                  value={key === 'projectName' ? projectName : key === 'projectNumber' ? projectNumber : key === 'projectClient' ? projectClient : key === 'projectPreparedBy' ? projectPreparedBy : projectCheckedBy}
                  onChange={e => set(e.target.value)}
                  placeholder={placeholder}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1.5px solid #EDE5F8', fontSize: 13, fontFamily: 'inherit', color: '#1C1C2E', outline: 'none', boxSizing: 'border-box', background: '#FAFAFA' }}
                />
              </div>
            ))}

            <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
              <button onClick={() => setShowProjectDetailsModal(false)}
                style={{ flex: 1, padding: '10px', background: 'white', color: '#6B6880', border: '1.5px solid #EDE5F8', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button onClick={() => { saveProject(); setShowProjectDetailsModal(false); }}
                style={{ flex: 2, padding: '10px', background: '#3D1A6E', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                💾 Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Scenario Comparison Modal */}
      {showCompareModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 10, width: Math.min(scenarios.length * 320 + 48, 900), maxWidth: '90vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 24px', background: '#3D1A6E', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'white', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Scenario Comparison</div>
              <button onClick={() => setShowCompareModal(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '4px 8px', borderRadius: 4 }}>✕</button>
            </div>
            <div style={{ overflowY: 'auto', padding: 24 }}>
              <div style={{ display: 'grid', gridTemplateColumns: scenarios.map(() => '1fr').join(' '), gap: 16 }}>
                {scenarios.map((sc, i) => {
                  const isS2 = i > 0; // any scenario after the first can match the first
                  const refSc = isS2 ? scenarios[0] : null;
                  const scDevSize = isS2 && sc.sameDevSize ? scenarios[0].devSize : sc.devSize;
                  return (
                    <div key={sc.id}>
                      <div style={{ background: '#3D1A6E', color: 'white', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontWeight: 700, fontSize: 13 }}>
                        {sc.name}
                      </div>

                      {/* Dev size */}
                      <div style={{ background: '#F5F0FA', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 6 }}>Development Size</div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#3D1A6E' }}>{scDevSize || devSize || '—'} <span style={{ fontSize: 12, fontWeight: 400, color: '#6B6880' }}>units</span></div>
                        {isS2 && sc.sameDevSize && <div style={{ fontSize: 10, color: '#0D9488', marginTop: 4 }}>✅ Same as {refSc.name}</div>}
                      </div>

                      {/* Time periods */}
                      <div style={{ background: '#F5F0FA', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 8 }}>Trip Rates</div>
                        {timePeriods.map(tp => {
                          const useRefRates = isS2 && sc.sameTripRates;
                          const rates = useRefRates
                            ? (refSc.tripRates?.[tp.id] || {})
                            : (sc.tripRates?.[tp.id] || {});
                          const sz = parseFloat(scDevSize || devSize) || 0;
                          const arr = parseFloat(rates.arrivals) || 0;
                          const dep = parseFloat(rates.departures) || 0;
                          const arrTrips = sz > 0 && arr > 0 ? Math.round(arr * sz) : null;
                          const depTrips = sz > 0 && dep > 0 ? Math.round(dep * sz) : null;
                          return (
                            <div key={tp.id} style={{ marginBottom: 10 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#3D1A6E', marginBottom: 4 }}>{tp.name} <span style={{ fontWeight: 400, color: '#C4BDD4' }}>({tp.startTime})</span></div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <div style={{ flex: 1, background: 'white', borderRadius: 6, padding: '6px 8px', border: '1px solid #EDE5F8' }}>
                                  <div style={{ fontSize: 9, color: '#6B6880', marginBottom: 2 }}>ARRIVALS</div>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: '#6B21A8' }}>{arr || '—'}<span style={{ fontSize: 9, fontWeight: 400, color: '#C4BDD4' }}>/unit</span></div>
                                  {arrTrips !== null && <div style={{ fontSize: 10, color: '#6B21A8', marginTop: 1 }}>{arrTrips} trips</div>}
                                </div>
                                <div style={{ flex: 1, background: 'white', borderRadius: 6, padding: '6px 8px', border: '1px solid #EDE5F8' }}>
                                  <div style={{ fontSize: 9, color: '#6B6880', marginBottom: 2 }}>DEPARTURES</div>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0D9488' }}>{dep || '—'}<span style={{ fontSize: 9, fontWeight: 400, color: '#C4BDD4' }}>/unit</span></div>
                                  {depTrips !== null && <div style={{ fontSize: 10, color: '#0D9488', marginTop: 1 }}>{depTrips} trips</div>}
                                </div>
                              </div>
                              {useRefRates && <div style={{ fontSize: 10, color: '#0D9488', marginTop: 4 }}>✅ Same as {refSc.name}</div>}
                            </div>
                          );
                        })}
                      </div>

                      {/* Distribution & mode share */}
                      {isS2 && (
                        <div style={{ background: '#F5F0FA', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 8 }}>Distribution & Mode Share</div>
                          <div style={{ fontSize: 12, color: sc.sameDistribution ? '#0D9488' : '#3D1A6E', fontWeight: 600, marginBottom: 4 }}>
                            {sc.sameDistribution ? `✅ Same distribution as ${refSc.name}` : '🔄 Different distribution'}
                          </div>
                          <div style={{ fontSize: 12, color: sc.sameModeShare ? '#0D9488' : '#3D1A6E', fontWeight: 600 }}>
                            {sc.sameModeShare ? `✅ Same mode share as ${refSc.name}` : '🔄 Different mode share'}
                          </div>
                        </div>
                      )}

                      {/* Assignment results if available */}
                      {assignedNetwork && activeScenarioId === sc.id && (
                        <div style={{ background: '#3D1A6E', borderRadius: 8, padding: '12px 14px' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#C4BDD4', marginBottom: 6 }}>Assignment Results</div>
                          <div style={{ fontSize: 20, fontWeight: 700, color: 'white' }}>{assignedNetwork.total_assigned} <span style={{ fontSize: 11, fontWeight: 400, color: '#C4BDD4' }}>vehicle trips</span></div>
                          <div style={{ fontSize: 11, color: '#C4BDD4', marginTop: 4 }}>{assignedNetwork.arr_assigned} arrivals · {assignedNetwork.dep_assigned} departures</div>
                        </div>
                      )}
                      {assignedNetwork && activeScenarioId !== sc.id && (
                        <div style={{ background: '#F5F0FA', borderRadius: 8, padding: '12px 14px', fontSize: 11, color: '#C4BDD4', fontStyle: 'italic' }}>
                          Switch to this scenario and run assignment to see results
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Options Modal (shared — see renderExportModal) */}
      {renderExportModal()}

      {/* Account Modal */}
      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}

      {showAccountModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 10, width: 460, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
            {/* Header */}
            <div style={{ background: '#3D1A6E', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'white', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Account</div>
              <button onClick={() => setShowAccountModal(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '4px 8px', borderRadius: 4 }}>✕</button>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', borderBottom: '1.5px solid #EDE5F8' }}>
              {[
                { key: 'details', label: 'My Account' },
                { key: 'password', label: 'Password' },
                { key: 'danger', label: 'Danger Zone' },
              ].map(tab => (
                <button key={tab.key} onClick={() => { setAccountTab(tab.key); setAccountMsg(''); }}
                  style={{ flex: 1, padding: '10px', background: accountTab === tab.key ? '#F5F0FA' : 'white', color: accountTab === tab.key ? '#3D1A6E' : '#6B6880', border: 'none', borderBottom: accountTab === tab.key ? '2.5px solid #3D1A6E' : '2.5px solid transparent', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>

              {/* My Account tab */}
              {accountTab === 'details' && (
                <div>
                  <div style={{ background: '#F5F0FA', borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 4 }}>Signed in as</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#3D1A6E' }}>{session?.user?.email}</div>
                  </div>

                  <div style={{ background: '#F5F0FA', borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 4 }}>Subscription</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#3D1A6E' }}>Free tier</div>
                    <div style={{ fontSize: 11, color: '#6B6880', marginTop: 3 }}>Paid plans coming soon</div>
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 6 }}>Change email address</div>
                    <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="New email address"
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1.5px solid #EDE5F8', fontSize: 13, fontFamily: 'inherit', color: '#1C1C2E', outline: 'none', boxSizing: 'border-box', marginBottom: 8 }} />
                    <button onClick={handleChangeEmail} disabled={accountLoading || !newEmail}
                      style={{ width: '100%', padding: '9px', background: !newEmail ? '#F5F0FA' : '#3D1A6E', color: !newEmail ? '#C4BDD4' : 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: !newEmail ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                      {accountLoading ? 'Updating...' : 'Update email'}
                    </button>
                  </div>

                  {accountMsg && (
                    <div style={{ fontSize: 12, padding: '8px 12px', borderRadius: 6, background: accountMsg.startsWith('✅') ? '#F0FDF9' : '#FEE2E2', color: accountMsg.startsWith('✅') ? '#0D9488' : '#991B1B', marginBottom: 12 }}>{accountMsg}</div>
                  )}

                  <div style={{ borderTop: '1px solid #EDE5F8', paddingTop: 16, marginTop: 4 }}>
                    <button onClick={() => { supabase.auth.signOut(); setShowAccountModal(false); }}
                      style={{ width: '100%', padding: '10px', background: 'white', color: '#6B6880', border: '1.5px solid #EDE5F8', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Sign out
                    </button>
                  </div>
                </div>
              )}

              {/* Password tab */}
              {accountTab === 'password' && (
                <div>
                  <div style={{ fontSize: 12, color: '#6B6880', marginBottom: 20 }}>Choose a strong password of at least 8 characters.</div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 4 }}>New password</label>
                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password"
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1.5px solid #EDE5F8', fontSize: 13, fontFamily: 'inherit', color: '#1C1C2E', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 4 }}>Confirm new password</label>
                    <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password"
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1.5px solid #EDE5F8', fontSize: 13, fontFamily: 'inherit', color: '#1C1C2E', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  {accountMsg && (
                    <div style={{ fontSize: 12, padding: '8px 12px', borderRadius: 6, background: accountMsg.startsWith('✅') ? '#F0FDF9' : '#FEE2E2', color: accountMsg.startsWith('✅') ? '#0D9488' : '#991B1B', marginBottom: 12 }}>{accountMsg}</div>
                  )}
                  <button onClick={handleChangePassword} disabled={accountLoading || !newPassword || !confirmPassword}
                    style={{ width: '100%', padding: '10px', background: (!newPassword || !confirmPassword) ? '#F5F0FA' : '#3D1A6E', color: (!newPassword || !confirmPassword) ? '#C4BDD4' : 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: (!newPassword || !confirmPassword) ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                    {accountLoading ? 'Updating...' : 'Update password'}
                  </button>
                </div>
              )}

              {/* Danger Zone tab */}
              {accountTab === 'danger' && (
                <div>
                  <div style={{ background: '#FEE2E2', border: '1.5px solid #FCA5A5', borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#991B1B', marginBottom: 6 }}>⚠️ Delete account</div>
                    <div style={{ fontSize: 12, color: '#7F1D1D', lineHeight: 1.6 }}>This will permanently delete your account and all your projects. This action cannot be undone.</div>
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 11, color: '#6B6880', marginBottom: 6 }}>Type <strong>DELETE</strong> to confirm:</label>
                    <input type="text" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)} placeholder="DELETE"
                      style={{ width: '100%', padding: '9px 12px', borderRadius: 6, border: '1.5px solid #FCA5A5', fontSize: 13, fontFamily: 'inherit', color: '#1C1C2E', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                  {accountMsg && (
                    <div style={{ fontSize: 12, padding: '8px 12px', borderRadius: 6, background: '#FEE2E2', color: '#991B1B', marginBottom: 12 }}>{accountMsg}</div>
                  )}
                  <button onClick={handleDeleteAccount} disabled={accountLoading || deleteConfirmText !== 'DELETE'}
                    style={{ width: '100%', padding: '10px', background: deleteConfirmText === 'DELETE' ? '#991B1B' : '#F5F0FA', color: deleteConfirmText === 'DELETE' ? 'white' : '#C4BDD4', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: deleteConfirmText !== 'DELETE' ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>
                    {accountLoading ? 'Deleting...' : 'Permanently delete my account'}
                  </button>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

      {/* Active Travel Modal — now unused, kept for safety */}
      {false && showActiveTravelModal && (
        <div style={{ position: 'fixed', top: 52, left: 0, width: '100vw', height: 'calc(100vh - 52px)', zIndex: 2000, display: 'flex', background: 'white' }}>

          {/* Left Panel */}
          <div style={{ width: 300, minWidth: 300, background: 'white', borderRight: '1.5px solid #EDE5F8', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ background: '#0D9488', padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'white', letterSpacing: '0.06em', textTransform: 'uppercase' }}>🚶 Active Travel</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>Accessibility Analysis</div>
              </div>
              <button onClick={() => setShowActiveTravelModal(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '4px 8px', borderRadius: 4 }}>✕</button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>

              {/* Origin info */}
              <div style={{ background: '#F0FDF9', border: '1px solid #0D9488', borderRadius: 6, padding: '8px 10px', marginBottom: 14, fontSize: 11, color: '#0D9488' }}>
                {siteAccessNode
                  ? `✅ Origin: Site access point (${siteAccessNode.lat.toFixed(4)}, ${siteAccessNode.lng.toFixed(4)})`
                  : `📍 Origin: Site pin — set a site access point for more accuracy`}
              </div>

              {/* Mode selector */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 6 }}>Travel Mode</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[{ key: 'walking', label: '🚶 Walk' }, { key: 'cycling', label: '🚴 Cycle' }, { key: 'both', label: '🚶🚴 Both' }].map(m => (
                    <button key={m.key} onClick={() => setAtMode(m.key)}
                      style={{ flex: 1, padding: '7px 4px', background: atMode === m.key ? '#0D9488' : 'white', color: atMode === m.key ? 'white' : '#0D9488', border: `1.5px solid ${atMode === m.key ? '#0D9488' : '#EDE5F8'}`, borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Time threshold */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 6 }}>Time Threshold</div>
                <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                  {[5, 10, 15, 20].map(m => {
                  const isOn = atSelectedMinutes.has(m);
                  return (
                    <button key={m} onClick={() => {
                      setAtCustomMinutes('');
                      setAtSelectedMinutes(prev => {
                        const next = new Set(prev);
                        if (next.has(m)) { if (next.size > 1) next.delete(m); }
                        else next.add(m);
                        return next;
                      });
                    }}
                      style={{ flex: 1, padding: '7px 4px', background: isOn && !atCustomMinutes ? '#0D9488' : 'white', color: isOn && !atCustomMinutes ? 'white' : '#0D9488', border: `1.5px solid ${isOn && !atCustomMinutes ? '#0D9488' : '#EDE5F8'}`, borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                      {m}
                    </button>
                  );
                })}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="number" min="1" max="60" value={atCustomMinutes}
                    onChange={e => setAtCustomMinutes(e.target.value)}
                    placeholder="Custom..."
                    style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: `1.5px solid ${atCustomMinutes ? '#0D9488' : '#EDE5F8'}`, fontSize: 12, fontFamily: 'inherit', outline: 'none' }} />
                  <span style={{ fontSize: 11, color: '#6B6880' }}>minutes</span>
                </div>
              </div>

              {/* Destination types */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 6 }}>Destination Types</div>
                {[
                  { key: 'school', label: '🏫 Schools', },
                  { key: 'hospital', label: '🏥 Hospitals' },
                  { key: 'gp', label: '⚕️ GP / Health centres' },
                  { key: 'pharmacy', label: '💊 Pharmacies' },
                  { key: 'supermarket', label: '🛒 Supermarkets' },
                  { key: 'park', label: '🌳 Parks' },
                  { key: 'leisure', label: '🏋️ Leisure centres' },
                  { key: 'train_station', label: '🚉 Train stations' },
                  { key: 'bus_stop', label: '🚌 Bus stations' },
                ].map(({ key, label }) => (
                  <div key={key} onClick={() => setAtDestTypes(prev => ({ ...prev, [key]: !prev[key] }))}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 8px', borderRadius: 6, cursor: 'pointer', marginBottom: 2, background: atDestTypes[key] ? '#F0FDF9' : 'transparent' }}
                    onMouseEnter={e => e.currentTarget.style.background = atDestTypes[key] ? '#F0FDF9' : '#F9F7FD'}
                    onMouseLeave={e => e.currentTarget.style.background = atDestTypes[key] ? '#F0FDF9' : 'transparent'}>
                    <div style={{ width: 16, height: 16, borderRadius: 3, border: `2px solid ${atDestTypes[key] ? '#0D9488' : '#C4BDD4'}`, background: atDestTypes[key] ? '#0D9488' : 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {atDestTypes[key] && <div style={{ color: 'white', fontSize: 10, lineHeight: 1, fontWeight: 700 }}>✓</div>}
                    </div>
                    <span style={{ fontSize: 12, color: '#1C1C2E' }}>{label}</span>
                  </div>
                ))}
              </div>

              {/* Layer toggles */}
              {atResults && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 6 }}>Layers</div>
                  {[
                    { key: 'isochrone', label: 'Isochrone' },
                    { key: 'destinations', label: 'Destinations' },
                    { key: 'routes', label: 'Routes' },
                  ].map(({ key, label }) => (
                    <div key={key} onClick={() => setAtLayerVisibility(prev => ({ ...prev, [key]: !prev[key] }))}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 8px', borderRadius: 6, cursor: 'pointer', marginBottom: 2 }}>
                      <span style={{ fontSize: 12, color: '#1C1C2E' }}>{label}</span>
                      <div style={{ width: 32, height: 18, borderRadius: 9, background: atLayerVisibility[key] ? '#0D9488' : '#C4BDD4', position: 'relative', transition: 'background 0.2s' }}>
                        <div style={{ position: 'absolute', top: 2, left: atLayerVisibility[key] ? 16 : 2, width: 14, height: 14, borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                      </div>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button onClick={() => setAtShowAggregated(false)}
                      style={{ flex: 1, padding: '5px', background: !atShowAggregated ? '#0D9488' : 'white', color: !atShowAggregated ? 'white' : '#0D9488', border: '1.5px solid #0D9488', borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Individual
                    </button>
                    <button onClick={() => setAtShowAggregated(true)}
                      style={{ flex: 1, padding: '5px', background: atShowAggregated ? '#0D9488' : 'white', color: atShowAggregated ? 'white' : '#0D9488', border: '1.5px solid #0D9488', borderRadius: 5, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Aggregated
                    </button>
                  </div>
                </div>
              )}

              {/* Error */}
              {atError && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '8px 10px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{atError}</div>}

              {/* Export button */}
              {atResults && !atLoading && (
                <button onClick={() => exportActiveTravelPDF({
                  projectName, projectNumber, projectClient, projectPreparedBy, projectCheckedBy,
                  atResults, mapboxToken: MAPBOX_TOKEN,
                })}
                  style={{ width: '100%', padding: '9px', background: 'white', color: '#0D9488', border: '1.5px solid #0D9488', borderRadius: 6, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8 }}>
                  📄 Export PDF Report
                </button>
              )}

              {/* Run button */}
              <button onClick={runActiveTravelAnalysis} disabled={atLoading}
                style={{ width: '100%', padding: '11px', background: atLoading ? '#EDE5F8' : '#0D9488', color: atLoading ? '#7B4DBF' : 'white', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: atLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', marginBottom: 12 }}>
                {atLoading ? 'Running analysis...' : '▶ Run Analysis'}
              </button>

              {/* Results summary */}
              {atResults && !atLoading && (() => {
                const allDests = Object.values(atResults.destinationResults).flat();
                const uniqueDests = [...new Map(allDests.map(d => [`${d.lat},${d.lng}`, d])).values()];
                const byType = {};
                uniqueDests.forEach(d => { byType[d.type] = (byType[d.type] || 0) + 1; });
                return (
                  <div style={{ background: '#F0FDF9', border: '1px solid #0D9488', borderRadius: 8, padding: '12px 14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#0D9488', marginBottom: 8 }}>
                      ✅ {uniqueDests.length} destinations found within {atResults.minutes} min {atResults.mode === 'both' ? 'walk/cycle' : atResults.mode}
                    </div>
                    {Object.entries(byType).map(([type, count]) => {
                      const labels = { school: '🏫 Schools', hospital: '🏥 Hospitals', gp: '⚕️ GP / Health', pharmacy: '💊 Pharmacies', supermarket: '🛒 Supermarkets', park: '🌳 Parks', leisure: '🏋️ Leisure', train_station: '🚉 Stations', bus_stop: '🚌 Bus stops' };
                      return (
                        <div key={type} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6B6880', marginBottom: 3 }}>
                          <span>{labels[type] || type}</span>
                          <span style={{ fontWeight: 700, color: '#0D9488' }}>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Results table */}
              {atResults && !atLoading && (() => {
                const modes = atResults.mode === 'both' ? ['walking', 'cycling'] : [atResults.mode];
                const allDests = atResults.destinationResults[modes[0]] || [];
                if (allDests.length === 0) return null;

                return (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B6880', marginBottom: 8 }}>Destination Details</div>
                    {allDests.map((dest, i) => {
                      const walkRoute = atResults.routeResults['walking']?.routes?.features?.find(f =>
                        Math.abs(f.properties.dest_lat - dest.lat) < 0.0005 &&
                        Math.abs(f.properties.dest_lng - dest.lng) < 0.0005
                      );
                      const cycleRoute = atResults.routeResults['cycling']?.routes?.features?.find(f =>
                        Math.abs(f.properties.dest_lat - dest.lat) < 0.0005 &&
                        Math.abs(f.properties.dest_lng - dest.lng) < 0.0005
                      );
                      const typeLabels = { school: '🏫', hospital: '🏥', gp: '⚕️', pharmacy: '💊', supermarket: '🛒', park: '🌳', leisure: '🏋️', train_station: '🚉', bus_stop: '🚌' };
                      return (
                        <div key={i} style={{ padding: '8px 10px', background: '#F9F7FD', borderRadius: 6, marginBottom: 6, borderLeft: '3px solid #0D9488' }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#1C1C2E', marginBottom: 4 }}>
                            {typeLabels[dest.type] || '📍'} {dest.name}
                          </div>
                          <div style={{ fontSize: 10, color: '#6B6880', marginBottom: 4 }}>{dest.description}</div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            {walkRoute && (
                              <div style={{ fontSize: 10, color: '#3D1A6E', background: '#F5F0FA', padding: '2px 6px', borderRadius: 4 }}>
                                🚶 {walkRoute.properties.duration_min} min · {Math.round(walkRoute.properties.distance_m)}m
                              </div>
                            )}
                            {cycleRoute && (
                              <div style={{ fontSize: 10, color: '#0D9488', background: '#F0FDF9', padding: '2px 6px', borderRadius: 4 }}>
                                🚴 {cycleRoute.properties.duration_min} min · {Math.round(cycleRoute.properties.distance_m)}m
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

            </div>
          </div>

          {/* Map */}
          <div style={{ flex: 1, position: 'relative' }}>
            <MapContainer center={pinLocation || [51.5, -1.5]} zoom={14}
              style={{ width: '100%', height: '100%' }}>
              <TileLayer url={"https://api.mapbox.com/styles/v1/mapbox/light-v11/tiles/{z}/{x}/{y}?access_token=" + MAPBOX_TOKEN} attribution="Mapbox" tileSize={512} zoomOffset={-1} />

              {/* Isochrones */}
              {atResults && atLayerVisibility.isochrone && Object.entries(atResults.isochroneResults).map(([mode, iso]) => (
                iso?.geometry && (
                  <GeoJSON key={`iso-${mode}`} data={iso}
                    style={{ fillColor: mode === 'walking' ? '#3D1A6E' : '#0D9488', fillOpacity: 0.1, color: mode === 'walking' ? '#3D1A6E' : '#0D9488', weight: 2, dashArray: '6,4' }} />
                )
              ))}

              {/* Routes */}
              {atResults && atLayerVisibility.routes && Object.entries(atResults.routeResults).map(([mode, routeData]) => {
                const color = mode === 'walking' ? '#3D1A6E' : '#0D9488';
                if (atShowAggregated) {
                  const features = routeData?.aggregated?.features || [];
                  const maxCount = Math.max(...features.map(f => f.properties.count || 1), 1);
                  return features.length > 0 ? (
                    <GeoJSON key={`agg-${mode}`} data={routeData.aggregated}
                      style={f => ({ color, weight: 1 + ((f.properties.count || 1) / maxCount) * 6, opacity: 0.7 + ((f.properties.count || 1) / maxCount) * 0.3 })} />
                  ) : null;
                } else {
                  return routeData?.routes?.features?.length > 0 ? (
                    <GeoJSON key={`routes-${mode}`} data={routeData.routes}
                      style={{ color, weight: 2, opacity: 0.7 }}
                      onEachFeature={(feature, layer) => {
                        layer.bindTooltip(
                          `<div style="font-family:Arial;font-size:12px;padding:4px 8px;">
                            <strong>${feature.properties.name}</strong><br/>
                            ${mode === 'walking' ? '🚶' : '🚴'} ${feature.properties.duration_min} min · ${Math.round(feature.properties.distance_m)}m
                          </div>`, { sticky: true }
                        );
                      }} />
                  ) : null;
                }
              })}

              {/* Destination markers */}
              {atResults && atLayerVisibility.destinations && (() => {
                const typeColors = { school: '#3D1A6E', hospital: '#DC2626', gp: '#EA580C', pharmacy: '#7C3AED', supermarket: '#0284C7', park: '#16A34A', leisure: '#0D9488', train_station: '#1D4ED8', bus_stop: '#92400E' };
                const allDests = Object.values(atResults.destinationResults).flat();
                const uniqueDests = [...new Map(allDests.map(d => [`${d.lat},${d.lng}`, d])).values()];
                return uniqueDests.map((dest, i) => (
                  <CircleMarker key={i} center={[dest.lat, dest.lng]} radius={8}
                    pathOptions={{ color: typeColors[dest.type] || '#666', fillColor: typeColors[dest.type] || '#666', fillOpacity: 0.9, weight: 2 }}>
                    <Popup>
                      <div style={{ fontFamily: 'Arial', fontSize: 12, minWidth: 150 }}>
                        <strong>{dest.name}</strong><br/>
                        <span style={{ color: '#6B6880', fontSize: 11 }}>{dest.description}</span>
                      </div>
                    </Popup>
                  </CircleMarker>
                ));
              })()}

              {/* Site pin */}
              {pinLocation && <Marker position={pinLocation} icon={redPin} />}
              {siteAccessNode && (
                <CircleMarker center={[siteAccessNode.lat, siteAccessNode.lng]} radius={10}
                  pathOptions={{ color: '#0D9488', fillColor: '#0D9488', fillOpacity: 1, weight: 3 }} />
              )}
            </MapContainer>

            {/* Legend */}
            {atResults && (
              <div style={{ position: 'absolute', bottom: 16, right: 16, background: 'white', borderRadius: 8, padding: '10px 12px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', fontSize: 11, zIndex: 1000 }}>
                <div style={{ fontWeight: 700, color: '#1C1C2E', marginBottom: 6 }}>Legend</div>
                {(atResults.mode === 'walking' || atResults.mode === 'both') && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <div style={{ width: 20, height: 3, background: '#3D1A6E', borderRadius: 2 }} />
                    <span style={{ color: '#6B6880' }}>Walking isochrone</span>
                  </div>
                )}
                {(atResults.mode === 'cycling' || atResults.mode === 'both') && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <div style={{ width: 20, height: 3, background: '#0D9488', borderRadius: 2 }} />
                    <span style={{ color: '#6B6880' }}>Cycling isochrone</span>
                  </div>
                )}
                <div style={{ fontSize: 10, color: '#C4BDD4', marginTop: 4 }}>
                  © Crown copyright OS 2026
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Schematic Editor Modal */}
      {showEditor && (
        <div style={{ position: 'fixed', top: 52, left: 0, width: '100vw', height: 'calc(100vh - 52px)', background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 10, width: '90vw', height: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid #EDE5F8', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#3D1A6E' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'white', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Schematic Editor</div>
                <div style={{ fontSize: 11, color: '#C4BDD4', marginTop: 2 }}>Draw junctions and links — state is preserved when you close</div>
              </div>
              <button onClick={() => setShowEditor(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '4px 8px', borderRadius: 4 }}>✕</button>
            </div>
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <SchematicEditor
                assignedNetwork={assignedNetwork}
                onClose={() => setShowEditor(false)}
                nodes={schematicNodes}
                setNodes={setSchematicNodes}
                links={schematicLinks}
                setLinks={setSchematicLinks}
                matrixExportRef={junctionMatrixExportRef}
                onMatricesComputed={setJunctionMatrices}
                siteBox={schematicSiteBox}
                setSiteBox={setSchematicSiteBox}
                amTotal={amTotal}
                pmTotal={pmTotal}
                modeShare={modeShare}
                onEnterJunctionMapMode={(nodeId, numArms, callback) => {
                  setPendingJunctionNodeId(nodeId);
                  schematicJunctionMapCallback.current = callback;
                  setPendingGates([]);
                  setGateDrawStart(null);
                  setGateDraftPoint(null);
                  setPendingGateConfirm(null);
                  setJunctionNumArms(numArms || 3);
                  setShowEditor(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default App;